// Smart cart management utility for voucher operations
class ExchangeCartManager {
  // Store original cart state when exchange process starts
  static originalCartState = null;
  static currentCartState = null; // Track current cart state to avoid multiple fetches
  static adjustmentProductVariantId = null;

  // Initialize cart state tracking when exchange page loads
  static async initializeCartState() {
    try {
      // Get current cart data
      let cartData = await window.apiService.get('/cart.js');
      if (typeof cartData === 'string') {
        cartData = JSON.parse(cartData);
      }

      // Get adjustment product variant ID from theme settings
      const exchangeProduct = window.account?.exchangeProduct;
      if (exchangeProduct?.variantId) {
        ExchangeCartManager.adjustmentProductVariantId = exchangeProduct.variantId.toString();
      }

      // Save original cart state (first time only)
      if (!ExchangeCartManager.originalCartState) {
        ExchangeCartManager.originalCartState = {
          items: cartData.items || [],
          item_count: cartData.item_count || 0,
          total_price: cartData.total_price || 0,
          token: cartData.token || cartData.id
        };
      }

      // Always update current cart state
      ExchangeCartManager.currentCartState = {
        items: cartData.items || [],
        item_count: cartData.item_count || 0,
        total_price: cartData.total_price || 0,
        token: cartData.token || cartData.id
      };



      return ExchangeCartManager.currentCartState;
    } catch (error) {

      // Fallback to empty state
      const emptyState = {
        items: [],
        item_count: 0,
        total_price: 0,
        token: null
      };

      if (!ExchangeCartManager.originalCartState) {
        ExchangeCartManager.originalCartState = emptyState;
      }
      ExchangeCartManager.currentCartState = emptyState;

      return ExchangeCartManager.currentCartState;
    }
  }

  // Update current cart state after cart operations
  static async updateCartState() {
    try {
      let cartData = await window.apiService.get('/cart.js');
      if (typeof cartData === 'string') {
        cartData = JSON.parse(cartData);
      }

      ExchangeCartManager.currentCartState = {
        items: cartData.items || [],
        item_count: cartData.item_count || 0,
        total_price: cartData.total_price || 0,
        token: cartData.token || cartData.id
      };

      // Update global cart object
      window.cart = cartData;

      // Update header cart icon
      SharedCartUtils.updateHeaderCartIcon(cartData.item_count || 0);



      return ExchangeCartManager.currentCartState;
    } catch (error) {

      return ExchangeCartManager.currentCartState; // Return last known state
    }
  }

  // Get current cart state without fetching (use cached state)
  static getCurrentCartState() {
    return ExchangeCartManager.currentCartState;
  }

  // Check if cart contains adjustment product
  static hasAdjustmentProductInCart(cartData = null) {
    // Use provided cartData or current cached state
    const cart = cartData || ExchangeCartManager.currentCartState;

    if (!ExchangeCartManager.adjustmentProductVariantId || !cart?.items) {
      return false;
    }

    return cart.items.some(item =>
      item.variant_id.toString() === ExchangeCartManager.adjustmentProductVariantId
    );
  }

  // Get adjustment product item from cart
  static getAdjustmentProductFromCart(cartData = null) {
    // Use provided cartData or current cached state
    const cart = cartData || ExchangeCartManager.currentCartState;

    if (!ExchangeCartManager.adjustmentProductVariantId || !cart?.items) {
      return null;
    }

    return cart.items.find(item =>
      item.variant_id.toString() === ExchangeCartManager.adjustmentProductVariantId
    );
  }

  // Smart cart preparation based on current state
  static async prepareCartForExchange(priceDifference, voucherGid = null, productVariantGid = null) {
    try {


      // If no price difference, no cart modification needed
      if (priceDifference <= 0) {

        return ExchangeCartManager.currentCartState;
      }

      // Get exchange product configuration
      const exchangeProduct = window.account?.exchangeProduct;
      if (!exchangeProduct?.variantId || !exchangeProduct?.price) {
        throw new Error('Exchange product not properly configured');
      }

      const adjustmentVariantId = exchangeProduct.variantId;
      const adjustmentProductPrice = exchangeProduct.price / 100; // Convert from cents
      const requiredQuantity = Math.ceil(priceDifference / adjustmentProductPrice);



      // Use current cached cart state instead of fetching
      const currentCartData = ExchangeCartManager.currentCartState;

      // Scenario 1: Cart is empty - proceed with adding adjustment product
      if (!currentCartData.items || currentCartData.items.length === 0) {

        await window.apiService.post('/cart/add.js', {
          id: adjustmentVariantId,
          quantity: requiredQuantity,
          properties: {
            '_extend_type': 'voucher_exchange',
            '_gift_card_gid': voucherGid,
            '_product_variant_gid': productVariantGid
          }
        });

        // Fetch the full cart data after adding the item
        const cartResponse = await window.apiService.get('/cart.js');
        const updatedCartData = typeof cartResponse === 'string' ? JSON.parse(cartResponse) : cartResponse;
        ExchangeCartManager.currentCartState = {
          items: updatedCartData.items || [],
          item_count: updatedCartData.item_count || 0,
          total_price: updatedCartData.total_price || 0,
          token: updatedCartData.token || updatedCartData.id
        };


        return ExchangeCartManager.currentCartState;
      }
      // Scenario 2: Cart has adjustment product - check if quantity is correct
      else if (ExchangeCartManager.hasAdjustmentProductInCart()) {
        const adjustmentItem = ExchangeCartManager.getAdjustmentProductFromCart();


        if (adjustmentItem.quantity === requiredQuantity) {
          // Scenario 4: Quantity is already correct, do nothing

          return ExchangeCartManager.currentCartState; // No API call needed
        } else {
          // Scenario 3: Update quantity of existing adjustment product

          const cartResponse = await window.apiService.post('/cart/change.js', {
            id: adjustmentItem.key,
            quantity: requiredQuantity
          });

          // Use the cart response directly instead of fetching again
          const updatedCartData = typeof cartResponse === 'string' ? JSON.parse(cartResponse) : cartResponse;
          ExchangeCartManager.currentCartState = {
            items: updatedCartData.items || [],
            item_count: updatedCartData.item_count || 0,
            total_price: updatedCartData.total_price || 0,
            token: updatedCartData.token || updatedCartData.id
          };


          return ExchangeCartManager.currentCartState;
        }
      }
      // Scenario 2 (part 2): Cart has other products but no adjustment product
      else {

        // Clear cart first, then add adjustment product
        await window.apiService.post('/cart/clear.js');
        await window.apiService.post('/cart/add.js', {
          id: adjustmentVariantId,
          quantity: requiredQuantity,
          properties: {
            '_extend_type': 'voucher_exchange',
            '_gift_card_gid': voucherGid,
            '_product_variant_gid': productVariantGid
          }
        });

        // Fetch the full cart data after adding the item
        const cartResponse = await window.apiService.get('/cart.js');
        const updatedCartData = typeof cartResponse === 'string' ? JSON.parse(cartResponse) : cartResponse;
        ExchangeCartManager.currentCartState = {
          items: updatedCartData.items || [],
          item_count: updatedCartData.item_count || 0,
          total_price: updatedCartData.total_price || 0,
          token: updatedCartData.token || updatedCartData.id
        };


        return ExchangeCartManager.currentCartState;
      }

    } catch (error) {
      console.error('Error in smart cart preparation:', error);
      throw error;
    }
  }

  // Reset cart state tracking
  static resetCartState() {
    ExchangeCartManager.originalCartState = null;
    ExchangeCartManager.currentCartState = null;

  }
}

// Shared utility functions to reduce redundancy
class SharedCartUtils {
  // Consolidated gift card data extraction logic
  static extractGiftCardData(giftCard) {
    const lastChars = giftCard.lastCharacters || (giftCard.giftCardId ? giftCard.giftCardId.slice(-4) : '????');
    const giftCardId = giftCard.giftCardId || (giftCard.id ? giftCard.id.replace('gid://shopify/AppliedGiftCard/', '') : '');

    let amount = 0;
    if (giftCard.amountUsed && typeof giftCard.amountUsed.amount !== 'undefined') {
      amount = parseInt(giftCard.amountUsed.amount, 10);
    } else if (typeof giftCard.applied !== 'undefined') {
      amount = parseInt(giftCard.applied, 10);
    }

    return { lastChars, giftCardId, amount };
  }

  // Update header cart icon visual state and count badges
  static updateHeaderCartIcon(itemCount) {
    if (typeof window.updateHeaderCartIcon === 'function') {
      window.updateHeaderCartIcon(itemCount);
      return;
    }

    const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, .header-cart-count');
    cartCountElements.forEach(element => {
      if (element.classList.contains('header-cart-btn')) return;
      element.textContent = itemCount;
      element.style.display = itemCount > 0 ? '' : 'none';
    });

    if (window.theme) window.theme.cartCount = itemCount;
  }

  // Consolidated cart clearing with proper cleanup
  static hasKnownEmptyCart() {
    if (window.__cartState?.isLoaded === true) {
      return window.__cartState.isEmpty === true;
    }

    const knownCartState =
      ExchangeCartManager.getCurrentCartState?.()
      || window.cart
      || null;

    if (!knownCartState) {
      return false;
    }

    const itemCount = Number(knownCartState.item_count || 0);
    const itemLength = Array.isArray(knownCartState.items) ? knownCartState.items.length : 0;

    return itemCount === 0 && itemLength === 0;
  }

  static async clearCartAndCookies(options = {}) {
    const { force = false } = options;

    try {
      if (!force && SharedCartUtils.hasKnownEmptyCart()) {
        SharedCartUtils.updateHeaderCartIcon(0);
        return;
      }

      // Clear Shopify cart
      await window.apiService.post('/cart/clear.js');

      // Remove cart-related cookies
      const cookiesToClear = ['cart', 'cart_token', 'cart_ts', 'cart_ver'];
      cookiesToClear.forEach(cookie => {
        document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      });

      // Update window.cart object
      window.cart = {
        token: null,
        note: '',
        attributes: {},
        total_price: 0,
        total_weight: 0,
        item_count: 0,
        items: [],
        requires_shipping: false,
        currency: window.cart?.currency || 'USD'
      };
      window.__cartState = {
        isLoaded: true,
        itemCount: 0,
        isEmpty: true,
        ...(typeof window.buildCartStateMeta === 'function'
          ? window.buildCartStateMeta()
          : { updated_at: new Date().toISOString(), updated_at_ms: Date.now() })
      };

      // Update ExchangeCartManager state if it's being used
      if (ExchangeCartManager && ExchangeCartManager.currentCartState) {
        ExchangeCartManager.currentCartState = {
          items: [],
          item_count: 0,
          total_price: 0,
          token: null
        };
      }

      // Trigger cart update events
      if (typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart: window.cart }
        }));
      }

      // Update cart UI if available
      if (typeof CartManager !== 'undefined' && window.cartManager) {
        await window.cartManager.updateCartUI();
      }

      // Update header cart icon state
      SharedCartUtils.updateHeaderCartIcon(window.cart.item_count || 0);
    } catch (error) {
      console.error('Error clearing cart and cookies:', error);
      throw error;
    }
  }

  // Consolidated popup reset utility with state preservation
  static resetVoucherPopup(popup, popupType, defaultTitle = 'Voucher') {


    try {
      // First, try to restore from saved state if available
      if (VoucherActionHandler && typeof VoucherActionHandler.restorePopupState === 'function') {
        const restored = VoucherActionHandler.restorePopupState(popup);
        if (restored) {

          return;
        }
      }

      // Fallback: Manual reset if no saved state is available


      // Clean up managers first
      if (popup.exchangeCartDiscountManager) {
        popup.exchangeCartDiscountManager.clearDiscountForm();
        popup.exchangeCartDiscountManager = null;
      }
      if (popup.exchangeQliroManager) {
        popup.exchangeQliroManager = null;
      }

      // Mark popup as inactive
      popup.dataset.popupActive = 'false';

      // Reset popup title
      const popupTitle = popup.querySelector(`.${popupType}-voucher-popup__title`);
      if (popupTitle) {
        popupTitle.textContent = defaultTitle;
      }

      // Reset popup body content based on type
      const popupBody = popup.querySelector(`.${popupType}-voucher-popup__body`);
      if (popupBody) {
        if (popupType === 'extend') {
          popupBody.innerHTML = `
            <div class="extend-voucher-info">
              <p>Extend your voucher for an additional period.</p>
            </div>
          `;
        } else if (popupType === 'exchange') {
          // Reset Qliro containers
          const qliroContainer = popup.querySelector('.exchange-qliro-container');
          if (qliroContainer) {
            qliroContainer.innerHTML = '<p class="cart-qliro__loading">Checkout loading...</p>';
            qliroContainer.style.display = 'none';
          }

          // Clear dataset attributes
          Object.keys(popup.dataset).forEach(key => {
            if (key.startsWith('cart') || key.startsWith('qliro') || key.startsWith('product') || key.startsWith('voucher')) {
              delete popup.dataset[key];
            }
          });
        }
      }

      // Show popup footer
      const popupFooter = popup.querySelector('.popup-footer');
      if (popupFooter) {
        popupFooter.style.display = '';
      }


    } catch (error) {
      console.error(`Error resetting ${popupType} voucher popup:`, error);
    }
  }
}

// Unified voucher utility class with both static and instance methods
class VoucherUtils {
  constructor(customerEmail = null, customerId = null) {
    this.customerEmail = customerEmail || window.account?.customerEmail || window.customerEmail;
    this.customerId = customerId || window.account?.customerId || window.customerId;
    this.element = null; // Can be set by components that need DOM access
  }

  // API and status methods
  createApiPayload(voucherCode, action) {
    const payload = {
      customerEmail: this.customerEmail,
      customerGid: `gid://shopify/Customer/${this.customerId}`,
      action: action
    };

    // Determine method based on the voucher code format
    if (voucherCode.startsWith('gid://shopify/GiftCard/')) {
      payload.method = 'GID';
      payload.giftCardGid = voucherCode;
    } else {
      payload.method = 'CODE';
      payload.giftCardCode = voucherCode;
    }

    return payload;
  }

  async checkVoucherStatus(voucherCode, apiEndpoint = `${window.proxyPath}/redeem`) {
    try {
      const payload = this.createApiPayload(voucherCode, 'GET_STATUS');

      const result = await window.apiService.post(apiEndpoint, payload);


      if (!result || typeof result !== 'object') {
        throw new Error('Invalid API response format');
      }

      if (!result.success) {
        throw new Error(result.message || 'API request failed');
      }

      if (!result.status || typeof result.status !== 'object') {
        throw new Error('Missing or invalid status object in response');
      }


      return result.status;
    } catch (error) {
      console.error('Error in checkVoucherStatus:', error);
      throw error;
    }
  }

  async connectVoucher(voucherCode, apiEndpoint = `${window.proxyPath}/redeem`) {
    try {
      const payload = this.createApiPayload(voucherCode, 'CONNECT');

      const result = await window.apiService.post(apiEndpoint, payload);


      if (!result || typeof result !== 'object') {
        throw new Error('Invalid API response format');
      }

      if (!result.success) {
        throw new Error(result.message || 'Failed to connect voucher');
      }


      return result;
    } catch (error) {
      console.error('Error in connectVoucher:', error);
      throw error;
    }
  }

  async redeemVoucher(voucherCode, apiEndpoint = `${window.proxyPath}/redeem`) {
    try {
      const payload = this.createApiPayload(voucherCode, 'REDEEM');

      const result = await window.apiService.post(apiEndpoint, payload);


      if (!result || typeof result !== 'object') {
        throw new Error('Invalid API response format');
      }

      if (!result.success) {
        throw new Error(result.message || 'Failed to redeem voucher');
      }


      return result;
    } catch (error) {
      console.error('Error in redeemVoucher:', error);
      throw error;
    }
  }

  async giftVoucher(voucherId, recipientData, apiEndpoint = `${window.proxyPath}/regift`) {
    try {
      const payload = {
        giftCardId: voucherId,
        email: recipientData.email,
        name: `${recipientData.firstName} ${recipientData.lastName}`.trim()
      };

      // Add optional fields if provided
      if (recipientData.message) {
        payload.message = recipientData.message;

      } else {

      }

      if (recipientData.deliveryDateTime) {
        payload.deliveryDateTime = recipientData.deliveryDateTime;

      } else {

      }



      const response = await window.apiService.post(apiEndpoint, payload);
      return response;
    } catch (error) {
      console.error('Error gifting voucher:', error);
      throw error;
    }
  }

  // Helper methods
  handleAlreadyRedeemedVoucher(isCurrentCustomer, element) {
    const messageKey = isCurrentCustomer
      ? 'voucher_already_redeemed'
      : 'voucher_already_redeemed_by_another_customer';

    const fallbackMessage = isCurrentCustomer
      ? 'Voucher already redeemed'
      : 'Voucher already redeemed by another customer';

    ErrorUtils.showInlineError(element, messageKey, fallbackMessage, 'redeem', false);
  }

  handleGiftboxChoice(voucherCode, status, isGid = false) {



    // For giftbox vouchers, is_redeemed: true is expected - it means the initial redemption happened
    // and now the user needs to choose their giftbox product
    if (status.is_redeemed && !status.is_giftbox) {
      console.error('Cannot start giftbox choice - regular voucher already redeemed');
      const isCurrentCustomer = status.customer_email === voucherUtils.customerEmail;
      const messageKey = isCurrentCustomer
        ? 'voucher_already_redeemed'
        : 'voucher_already_redeemed_by_another_customer';
      const fallbackMessage = isCurrentCustomer
        ? 'Voucher already redeemed'
        : 'Voucher already redeemed by another customer';
      ErrorUtils.error('customer.account.choice', messageKey, fallbackMessage);
      return;
    }

    // For giftbox vouchers, is_redeemed: true is the expected state for choice phase
    if (status.is_giftbox && status.is_redeemed) {

    }

    // Store only GID and essential data in localStorage
    const giftboxChoiceData = {
      voucherId: status.giftCardId || voucherCode,
      voucherName: status.productTitle || 'Gift Box',
      originalValue: status.current_balance || status.balance || status.initial_balance,
      currency: status.currency || 'SEK',
      formatted_balance: status.formatted_balance,
      customerEmail: status.customer_email || '',
      giftboxCollectionHandle: status.giftbox_collection || '',
      timestamp: Date.now(),
      chooseProcessInitiated: true,
      processType: 'choose_process'
    };



    try {
      localStorage.setItem('giftboxChoice', JSON.stringify(giftboxChoiceData));


      // Redirect to giftbox collection page
      const collectionUrl = status.giftbox_collection
        ? `/collections/${status.giftbox_collection}`
        : '/collections/giftbox'; // fallback collection


      window.location.href = collectionUrl;

    } catch (error) {
      console.error('Failed to store giftbox choice data in localStorage:', error);
      ErrorUtils.error('customer.account.redeem', 'redeem_error', 'Failed to start choice process');
    }
  }

  getVoucherCodeFromForm(selector = '[data-action="input-voucher-code"]', fallbackSelector = '#voucherCode') {
    let input = this.element?.querySelector(selector);
    if (!input && fallbackSelector) {
      input = this.element?.querySelector(fallbackSelector);
    }

    if (input) {
      return input.value.trim();
    }

    console.warn(`Voucher input not found with selectors: ${selector}, ${fallbackSelector}`);
    return null;
  }

  // Static utility methods (keep these static as they don't need instance data)
  static isExpired(voucher) {
    // If backend provides is_expired directly, use that
    if (voucher && typeof voucher.is_expired === 'boolean') {
      return voucher.is_expired;
    }

    // Fallback to date calculation
    if (!voucher || !voucher.expires_at) {
      return false;
    }

    // Use UTC for consistent comparison across timezones
    const today = new Date();
    const todayUTC = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());

    const expiryDate = new Date(voucher.expires_at);
    const expiryUTC = Date.UTC(expiryDate.getUTCFullYear(), expiryDate.getUTCMonth(), expiryDate.getUTCDate());

    return expiryUTC < todayUTC;
  }

  static isExpiringSoon(voucher, daysThreshold = 30) {
    if (!voucher || !voucher.expires_at || this.isExpired(voucher)) {
      return false;
    }

    // Use UTC for consistent comparison across timezones
    const today = new Date();
    const todayUTC = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());

    const expiryDate = new Date(voucher.expires_at);
    const expiryUTC = Date.UTC(expiryDate.getUTCFullYear(), expiryDate.getUTCMonth(), expiryDate.getUTCDate());

    const timeDiff = expiryUTC - todayUTC;
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    return daysDiff <= daysThreshold && daysDiff > 0;
  }

  static formatDate(dateString) {
    if (!dateString) {
      return '';
    }

    try {
      // Handle various date formats
      let year, month, day;

      // If it's already a Date object, use it directly
      if (dateString instanceof Date) {
        const date = dateString;
        // Check if date is valid
        if (isNaN(date.getTime())) {
          console.warn('Invalid date:', dateString);
          return String(dateString);
        }
        // Use local date methods to get the correct date in user's timezone
        year = date.getFullYear();
        month = String(date.getMonth() + 1).padStart(2, '0');
        day = String(date.getDate()).padStart(2, '0');
      } else {
        const dateStr = String(dateString);

        // Check if it's a date-only string in YYYY-MM-DD format (no time component)
        // This avoids timezone issues by directly extracting the date parts
        const dateOnlyMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (dateOnlyMatch) {
          year = dateOnlyMatch[1];
          month = dateOnlyMatch[2];
          day = dateOnlyMatch[3];
        } else {
          // For ISO format strings with time (T and/or Z), parse and use local date
          let date;
          if (dateStr.includes('T') || dateStr.includes('Z')) {
            date = new Date(dateStr);
          } else {
            // For other formats, replace dashes with slashes for consistent parsing
            const normalizedDateString = dateStr.replace(/-/g, '/');
            date = new Date(normalizedDateString);
          }

          // Check if date is valid
          if (isNaN(date.getTime())) {
            console.warn('Invalid date:', dateString);
            return dateString; // Return original string if invalid
          }

          // Use local date methods to get the correct date in user's timezone
          year = date.getFullYear();
          month = String(date.getMonth() + 1).padStart(2, '0');
          day = String(date.getDate()).padStart(2, '0');
        }
      }

      return `${year}/${month}/${day}`;
    } catch (error) {
      console.warn('Error formatting date:', error, dateString);
      return dateString; // Return original string on error
    }
  }

  /**
   * Format variant title for display — numeric-only titles become "1 person" / "2 persons".
   * @param {string|number|null|undefined} title
   * @returns {string}
   */
  static formatVariantTitle(title) {
    const raw = title == null || title === '' ? '' : String(title).trim();
    if (!raw) return 'Standard';

    if (/^\d+$/.test(raw)) {
      const count = parseInt(raw, 10);
      if (count === 1) {
        return TranslationUtils.get('customer.account.vouchers.variant_person_one', '1 person');
      }
      return TranslationUtils.format(
        'customer.account.vouchers.variant_person_other',
        '{{ count }} persons',
        { count }
      );
    }

    return raw;
  }

  /**
   * Get human-readable booking status text with translation support
   * @param {string} bookingStatus - The booking status (e.g., 'COMPLETED_CARRIED', 'CANCELED')
   * @returns {string} Translated status text
   */
  static getBookingStatusText(bookingStatus) {
    // Always downcase the status first for consistent comparison (keep underscores)
    const statusKey = bookingStatus ? bookingStatus.toString().toLowerCase() : 'booked';

    // Special handling for completed_carried status (with underscore)
    if (statusKey === 'completed_carried') {
      return TranslationUtils.get('customer.account.vouchers.booking_status.completed_carried', 'Completed experience');
    }

    // Special handling for completed_canceled status (with underscore)
    if (statusKey === 'completed_canceled') {
      return TranslationUtils.get('customer.account.vouchers.booking_status.completed_canceled', 'Completed (Canceled)');
    }

    // Special handling for cancellation statuses
    if (statusKey === 'canceled_request_customer') {
      return TranslationUtils.get(
        'customer.account.vouchers.booking_status.canceled_request_customer',
        'Customer canceled booking request'
      );
    }
    if (statusKey === 'canceled_customer') {
      return TranslationUtils.get('customer.account.vouchers.booking_status.canceled_customer', 'Customer canceled booking');
    }
    if (statusKey === 'canceled_request_vendor') {
      return TranslationUtils.get(
        'customer.account.vouchers.booking_status.canceled_request_vendor',
        'Vendor canceled booking request'
      );
    }
    if (
      statusKey === 'canceled'
      || statusKey === 'cancelled'
      || statusKey === 'canceled_vendor'
    ) {
      return TranslationUtils.get('customer.account.vouchers.booking_status.canceled_vendor', 'Vendor canceled booking');
    }

    // Default translation lookup for other statuses
    const translatedText = TranslationUtils.get(
      `customer.account.vouchers.booking.${statusKey}`,
      bookingStatus || 'Booked'
    );

    return translatedText;
  }

  /**
   * Get CSS class for booking status
   * @param {string} bookingStatus - The booking status (e.g., 'COMPLETED_CARRIED', 'CANCELED')
   * @returns {string} CSS class name (e.g., 'voucher-status--completed-carried')
   */
  static getBookingStatusClass(bookingStatus) {
    // Normalize: lowercase and replace underscores with hyphens for CSS class
    const statusKey = bookingStatus ? bookingStatus.toString().toLowerCase().replace(/_/g, '-') : 'booked';
    return `voucher-status--${statusKey}`;
  }
}

// Popup management utility to consolidate popup operations
class PopupUtils {
  static openGiftPopup(voucherIdentifier) {
    this.openPopup('#account-gift-popup', voucherIdentifier, (popup) => {
      // Save original popup state before any modifications
      if (VoucherActionHandler && typeof VoucherActionHandler.savePopupState === 'function') {
        VoucherActionHandler.savePopupState(popup);
      }

      // Reset the form when opening the popup to ensure clean state
      if (VoucherActionHandler && typeof VoucherActionHandler.resetGiftPopupForm === 'function') {
        VoucherActionHandler.resetGiftPopupForm(popup);
      }
    });
  }

  // Helper method to configure common popup data attributes
  static _configurePopupData(popup, voucherIdentifier, isGid, status) {
    // Save original popup state before any modifications
    if (VoucherActionHandler && typeof VoucherActionHandler.savePopupState === 'function') {
      VoucherActionHandler.savePopupState(popup);
    }

    // Set voucher input if available
    const voucherInput = popup.querySelector('[data-action="input-voucher-code"]');
    if (voucherInput) {
      voucherInput.value = voucherIdentifier;
    }

    // Set method and information attributes
    if (isGid) {
      popup.dataset.redeemMethod = 'GID';
      popup.dataset.information = 'GID';
    } else {
      popup.dataset.redeemMethod = 'CODE';
      popup.dataset.information = 'CODE';
    }

    // Store voucher status in popup for later use
    if (status) {
      popup.dataset.voucherStatus = JSON.stringify(status);
      popup.dataset.voucherIdentifier = voucherIdentifier;
      // Also store needs_connection flag directly on popup for easy access
      popup.dataset.needsConnection = status.needs_connection === true ? 'true' : 'false';
    } else {
      // Default to needs connection if no status provided
      popup.dataset.needsConnection = 'true';
    }
  }

  static openRedeemPopup(voucherIdentifier, isGid = false, status = null, element = document) {
    // Check if we're currently inside a grouping popup using the proper Popup class method
    const voucherGroupingPopup = document.querySelector('[id$="voucher-grouping-popup"]');
    const isChild = voucherGroupingPopup && voucherGroupingPopup.getAttribute('aria-expanded') === 'true';



    // Use regex to find popup that ends with 'redeem-confirm-popup'
    const popupSelector = '[id$="redeem-confirm-popup"]';
    this.openPopup(popupSelector, voucherIdentifier, (popup) => {
      // Save original popup state before any modifications
      if (VoucherActionHandler && typeof VoucherActionHandler.savePopupState === 'function') {
        VoucherActionHandler.savePopupState(popup);
      }

      // Set voucher input if available
      const voucherInput = popup.querySelector('[data-action="input-voucher-code"]');
      if (voucherInput) {
        voucherInput.value = voucherIdentifier;
      }

      // Set method and information attributes
      if (isGid) {
        popup.dataset.redeemMethod = 'GID';
        popup.dataset.information = 'GID';
      } else {
        popup.dataset.redeemMethod = 'CODE';
        popup.dataset.information = 'CODE';
      }

      // Store voucher status in popup for later use
      if (status) {
        popup.dataset.voucherStatus = JSON.stringify(status);
        // Also store needs_connection flag directly on popup for easy access
        popup.dataset.needsConnection = status.needs_connection === true ? 'true' : 'false';
      } else {
        // Default to needs connection if no status provided
        popup.dataset.needsConnection = 'true';
      }

      // Find the redeem button and configure based on needs_connection
      const redeemButton = popup.querySelector('[data-action="voucher-action"][data-method="redeem"]');
      if (redeemButton) {
        // Determine if this voucher needs connection based on status
        const needsConnection = status && status.needs_connection === true;

        if (needsConnection) {

          redeemButton.dataset.needsConnection = 'true';

          // Update button text to "Claim Gift Card"
          const buttonText = redeemButton.querySelector('.ButtonText');
          if (buttonText) {
            buttonText.textContent = TranslationUtils.get('customer.account.redeem.claim_button', 'Claim Gift Card');
          }
        } else {

          redeemButton.dataset.needsConnection = 'false';

          // Keep button text as "Redeem voucher"
          const buttonText = redeemButton.querySelector('.ButtonText');
          if (buttonText) {
            buttonText.textContent = TranslationUtils.get('customer.account.redeem.redeem_button', 'Redeem voucher');
          }
        }
      }
    }, element, isChild);
  }

  static openConnectPopup(voucherIdentifier, isGid = false, status = null, element = document) {
    // Check if we're currently inside a grouping popup using the proper Popup class method
    const voucherGroupingPopup = document.querySelector('[id$="voucher-grouping-popup"]');
    const isChild = voucherGroupingPopup && voucherGroupingPopup.getAttribute('aria-expanded') === 'true';



    // Use regex to find popup that ends with 'connect-confirm-popup'
    const popupSelector = '[id$="connect-confirm-popup"]';
    this.openPopup(popupSelector, voucherIdentifier, (popup) => {
      // Save original popup state before any modifications
      if (VoucherActionHandler && typeof VoucherActionHandler.savePopupState === 'function') {
        VoucherActionHandler.savePopupState(popup);
      }

      // Set voucher input if available
      const voucherInput = popup.querySelector('[data-action="input-voucher-code"]');
      if (voucherInput) {
        voucherInput.value = voucherIdentifier;
      }

      // Set method and information attributes
      if (isGid) {
        popup.dataset.redeemMethod = 'GID';
        popup.dataset.information = 'GID';
      } else {
        popup.dataset.redeemMethod = 'CODE';
        popup.dataset.information = 'CODE';
      }

      // Store voucher status in popup for later use
      if (status) {
        popup.dataset.voucherStatus = JSON.stringify(status);
        // Mark as needs connection (this is a connect popup)
        popup.dataset.needsConnection = 'true';
      } else {
        // Default to needs connection for connect popup
        popup.dataset.needsConnection = 'true';
      }

      // Configure the connect button
      const connectButton = popup.querySelector('[data-action="voucher-action"][data-method="redeem"]');
      if (connectButton) {

        connectButton.dataset.needsConnection = 'true';
      }
    }, element, isChild);
  }

  static openChoosePopup(voucherIdentifier, isGid = false, status = null, element = document) {
    // Check if we're currently inside a grouping popup using the proper Popup class method
    const voucherGroupingPopup = document.querySelector('[id$="voucher-grouping-popup"]');
    const isChild = voucherGroupingPopup && voucherGroupingPopup.getAttribute('aria-expanded') === 'true';


    // Find the actual Popup element inside the block (Shopify wraps blocks in a div)
    const popupSelector = '[id$="choose-confirm-popup"] .Popup, [id$="choose-confirm-popup"].Popup';
    this.openPopup(popupSelector, voucherIdentifier, (popup) => {
      // Configure common popup data using helper
      this._configurePopupData(popup, voucherIdentifier, isGid, status);

      // Find and update the action button for choose popup
      const actionButton = popup.querySelector('[data-action="voucher-action"][data-method="redeem"]');
      if (actionButton) {
        const needsConnection = status && status.needs_connection === true;

        // Only change to init-choice if the voucher is already redeemed
        // If not redeemed, keep it as 'redeem' action (for connect/redeem flow)
        if (status && status.is_redeemed === true) {
          // Change the button action to init-choice for redeemed giftboxes
          actionButton.dataset.method = 'init-choice';
          actionButton.dataset.needsConnection = 'false';

          // Update button text to "Continue"
          const buttonText = actionButton.textContent;
          actionButton.textContent = 'Continue';


        } else {
          // Keep as redeem action for non-redeemed vouchers


          // Set the needs connection flag on the button
          actionButton.dataset.needsConnection = needsConnection ? 'true' : 'false';

          // For unredeemed giftbox vouchers, update button text to "Choose Product"
          if (status && status.is_giftbox && !status.is_redeemed) {
            const buttonTextEl = actionButton.querySelector('.ButtonText');
            if (buttonTextEl) {
              buttonTextEl.textContent = TranslationUtils.get('customer.account.vouchers.actions.choose_product', 'Choose Product');
            }
          }
        }
      } else {
        console.warn('Could not find action button in choose popup to configure');
      }
    }, element, isChild);
  }

  static openGiftboxChoosePopup(voucherIdentifier, isGid = false, status = null, element = document) {
    // Check if we're currently inside a grouping popup
    const voucherGroupingPopup = document.querySelector('[id$="voucher-grouping-popup"]');
    const isChild = voucherGroupingPopup && voucherGroupingPopup.getAttribute('aria-expanded') === 'true';


    // Find the actual Popup element inside the block (Shopify wraps blocks in a div)
    const popupSelector = '[id$="giftbox-choose-popup"] .Popup, [id$="giftbox-choose-popup"].Popup';
    this.openPopup(popupSelector, voucherIdentifier, (popup) => {
      // Configure common popup data
      this._configurePopupData(popup, voucherIdentifier, isGid, status);

      // Find and update the action button
      const actionButton = popup.querySelector('[data-action="voucher-action"][data-method="redeem"]');
      if (actionButton) {
        const needsConnection = status && status.needs_connection === true;

        // Set the needs connection flag on the button
        actionButton.dataset.needsConnection = needsConnection ? 'true' : 'false';


      } else {
        console.warn('Could not find action button in giftbox choose popup to configure');
      }
    }, element, isChild);
  }

  static openExtendPopup(voucherIdentifier, voucherData, element = document) {
    this.openPopup('#extend-voucher-popup', voucherIdentifier, async (popup) => {
      try {
        // Save original popup state before any modifications
        if (VoucherActionHandler && typeof VoucherActionHandler.savePopupState === 'function') {
          VoucherActionHandler.savePopupState(popup);
        }

        // Store context data in localStorage for return after Qliro checkout
        // Store context data but don't mark as initiated yet
        // This will only be marked as initiated when user clicks "Go to Checkout"
        const extendContextData = {
          voucherId: voucherIdentifier,
          voucherName: voucherData?.productTitle || 'Voucher',
          timestamp: Date.now(),
          extendInitiated: false, // Only set to true when user proceeds to checkout
          // Store current URL context to return to
          contextUrl: window.location.pathname + window.location.search
        };

        try {
          localStorage.setItem('extendGiftCard', JSON.stringify(extendContextData));

        } catch (error) {
          console.error('Failed to store extend context data in localStorage:', error);
        }

        // Set voucher data for the extend popup immediately
        if (voucherData) {
          popup.dataset.voucherName = voucherData.productTitle || 'Voucher';
          popup.dataset.voucherCode = voucherData.last_characters || '';
          popup.dataset.voucherAmount = voucherData.initial_value || voucherData.original_amount || '';

          // Update popup content with voucher information
          const voucherNameElement = popup.querySelector('[data-extend-voucher-name]');
          const voucherCodeElement = popup.querySelector('[data-extend-voucher-code]');

          if (voucherNameElement) {
            voucherNameElement.textContent = voucherData.productTitle || 'Voucher';
          }

          if (voucherCodeElement) {
            // Extract the last part of the GID (the actual ID number)
            const displayCode = voucherData.shopify_gift_card_id || voucherIdentifier;
            const lastPart = displayCode.includes('/') ? displayCode.split('/').pop() : displayCode;
            voucherCodeElement.textContent = `(${lastPart})`;
          }
        }

        // Mark popup as active for background task checking
        popup.dataset.popupActive = 'true';

        // Popup is ready for user interaction - no loading state needed


        // Background task: Prepare cart and Qliro for extend operation
        PopupUtils.prepareExtendCartAndQliro(popup, voucherIdentifier)
          .then(async (cartData) => {
            // Check if popup is still active before proceeding
            if (popup.dataset.popupActive !== 'true') {

              return;
            }


            popup.dataset.cartPrepared = 'true';

            // Pre-initialize Qliro checkout in background (only if popup still active)
            if (popup.dataset.popupActive === 'true' && cartData) {

              await PopupUtils.initializeExtendQliroCheckout(popup, cartData);


              if (popup.dataset.qliroReady === 'true') {

              } else {
                console.warn('⚠️ Extend popup partially prepared: Cart ready, Qliro failed - will use fallback');
              }
            }
          })
          .catch((error) => {
            // Only show error if popup is still active
            if (popup.dataset.popupActive === 'true') {
              console.error('Error preparing extend cart:', error);
              popup.dataset.cartPrepared = 'false';
              popup.dataset.qliroReady = 'false';

              // Show error notification
              if (toastNotifications) {
                toastNotifications.error('Failed to prepare extend cart. Please try again.');
              }
            }
          });

      } catch (error) {
        console.error('Error setting up extend popup:', error);

        // Show error notification
        if (toastNotifications) {
          toastNotifications.error(`Error loading extend popup: ${error.message}`);
        }
      }
    }, element);
  }

  static async openExchangePopup(productId, productTitle, productPrice, variantInfo = null, element = document) {
    this.openPopup('#exchange-voucher-popup', productId, async (popup) => {
      try {
        // Save original popup state before any modifications
        if (VoucherActionHandler && typeof VoucherActionHandler.savePopupState === 'function') {
          VoucherActionHandler.savePopupState(popup);
        }

        // Cart state should already be initialized on page load
        // If not initialized, do it now as fallback
        if (!ExchangeCartManager.currentCartState) {
          console.warn('Cart state not initialized, initializing now as fallback...');
          await ExchangeCartManager.initializeCartState();
        }

        // Get exchange data from localStorage
        const exchangeData = JSON.parse(localStorage.getItem('exchangeGiftCard') || '{}');
        const voucherValue = parseFloat(exchangeData.originalValue) || 0;
        const productPriceValue = parseInt(productPrice) / 100; // Convert from cents
        const difference = productPriceValue - voucherValue;

        // Set product and exchange data in popup immediately
        popup.dataset.productId = productId;
        popup.dataset.productTitle = productTitle;
        popup.dataset.productPrice = productPrice;
        popup.dataset.voucherValue = voucherValue;
        popup.dataset.priceDifference = difference;
        popup.dataset.productVariantId = variantInfo?.id || productId; // Use variant ID from variantInfo or fallback to productId

        // Update popup content with product and exchange information immediately
        const productNameElements = popup.querySelectorAll('[data-exchange-product-name]');
        const productImageElement = popup.querySelector('[data-exchange-product-image]');
        const productOldNameElement = popup.querySelector('[data-exchange-old-product-name]');
        const productOldCodeElement = popup.querySelector('[data-exchange-voucher-code]');
        const priceDifferenceContainer = popup.querySelector('[data-exchange-price-difference-container]');
        const priceDifferenceElement = popup.querySelector('[data-exchange-price-difference]');
        const productOptionsContainer = popup.querySelector('[data-exchange-options]');

        if (productNameElements) {
          // Use variant info title if available, otherwise fall back to productTitle
          const displayTitle = variantInfo?.title || productTitle;
          productNameElements.forEach((element) => {
            element.textContent = displayTitle;
          });
          productOldNameElement.textContent = exchangeData.voucherName || 'Voucher';
          productOldCodeElement.textContent = `(#${exchangeData.voucherId.replace('gid://shopify/GiftCard/', '')})`;
        }

        // Set product image if variant info is available
        if (productImageElement && variantInfo?.image) {
          productImageElement.src = variantInfo.image;
          productImageElement.alt = variantInfo.title || productTitle;
          productImageElement.style.display = 'inline-block';
        }

        if (productOptionsContainer && variantInfo?.options) {
          const optionsHtml = variantInfo.options
            .filter(option => option.value !== 'Default Title')
            .map(option => `
              <div class="exchange__product-option">
                <span class="u-p4 grey-primary-text">${option.name}: ${option.value}</span>
              </div>
            `).join('');
          productOptionsContainer.innerHTML = optionsHtml;
        }


        // Update pricing summary section
        const currentPriceElement = popup.querySelector('[data-exchange-current-price]');
        const newPriceElement = popup.querySelector('[data-exchange-new-price]');
        const subtotalElement = popup.querySelector('[data-exchange-subtotal]');
        const giftcardRowElement = popup.querySelector('[data-exchange-giftcard-row]');
        const giftcardDiscountElement = popup.querySelector('[data-exchange-giftcard-discount]');

        // Set current voucher price
        if (currentPriceElement) {
          currentPriceElement.textContent = Shopify.formatMoney(voucherValue * 100, window.theme.moneyFormat);
        }

        // Set new product price
        if (newPriceElement) {
          newPriceElement.textContent = Shopify.formatMoney(productPriceValue * 100, window.theme.moneyFormat);
        }

        // Get gift card container elements (legacy location, kept for compatibility)
        const giftcardContainer = popup.querySelector('[data-exchange-giftcard-container]');
        const giftcardElement = popup.querySelector('[data-exchange-giftcard-amount]');

        // Calculate subtotal: new price - current price - gift card discount
        // Initially gift card discount is 0 (will be updated when discount codes are applied)
        let giftCardDiscount = 0;

        // Handle downgrade scenario (difference < 0)
        if (difference < 0) {
          const downgradeAmount = Math.abs(difference);

          // If downgrade is more than 50 SEK, show gift card
          if (downgradeAmount > 50) {
            // Hide price difference
            if (priceDifferenceContainer) {
              priceDifferenceContainer.style.display = 'none';
            }

            // Show gift card in pricing summary (this is what customer receives back)
            if (giftcardRowElement && giftcardDiscountElement) {
              const formattedGiftcard = Shopify.formatMoney(downgradeAmount * 100, window.theme.moneyFormat);
              giftcardDiscountElement.textContent = `+${formattedGiftcard}`;
              giftcardRowElement.style.display = 'flex';
            }

            // Legacy: Show gift card (kept for compatibility)
            if (giftcardElement && giftcardContainer) {
              const formattedGiftcard = Shopify.formatMoney(downgradeAmount * 100, window.theme.moneyFormat);
              const giftcardText = window.theme?.strings?.product?.giftcard_amount || 'Gift Card + {amount}';
              const displayText = giftcardText.replace('{amount}', formattedGiftcard);

              giftcardElement.textContent = displayText;
              giftcardContainer.style.display = 'flex';

            }

            // Subtotal: new price - current price - gift card = negative + gift card = 0
            // They get a gift card back, so subtotal to pay is 0
            if (subtotalElement) {
              subtotalElement.textContent = Shopify.formatMoney(0, window.theme.moneyFormat);
            }
          } else {
            // Downgrade is 50 SEK or less, show as 0 difference
            if (priceDifferenceContainer && priceDifferenceElement) {
              priceDifferenceElement.textContent = Shopify.formatMoney(0, window.theme.moneyFormat);
              priceDifferenceContainer.style.display = 'flex';
            }

            // Hide gift card in pricing summary
            if (giftcardRowElement) {
              giftcardRowElement.style.display = 'none';
            }

            // Legacy: Hide gift card
            if (giftcardContainer) {
              giftcardContainer.style.display = 'none';
            }

            // Subtotal is 0 (minor downgrade, no refund)
            if (subtotalElement) {
              subtotalElement.textContent = Shopify.formatMoney(0, window.theme.moneyFormat);
            }
          }
        } else if (difference > 0) {
          // Handle upgrade scenario - show price difference
          if (priceDifferenceContainer && priceDifferenceElement) {
            priceDifferenceElement.textContent = Shopify.formatMoney(difference * 100, window.theme.moneyFormat);
            priceDifferenceContainer.style.display = 'flex';
          }

          // Hide gift card in pricing summary initially (will show when discount applied)
          if (giftcardRowElement) {
            giftcardRowElement.style.display = 'none';
          }

          // Legacy: Hide gift card
          if (giftcardContainer) {
            giftcardContainer.style.display = 'none';
          }

          // Initial subtotal: new price - current price - 0 (no discount yet) = difference
          if (subtotalElement) {
            const calculatedSubtotal = difference - giftCardDiscount;
            subtotalElement.textContent = Shopify.formatMoney(calculatedSubtotal * 100, window.theme.moneyFormat);
          }
        } else {
          // No difference (equal exchange)
          if (priceDifferenceContainer) {
            priceDifferenceContainer.style.display = 'none';
          }

          // Hide gift card in pricing summary
          if (giftcardRowElement) {
            giftcardRowElement.style.display = 'none';
          }

          // Legacy: Hide gift card
          if (giftcardContainer) {
            giftcardContainer.style.display = 'none';
          }

          // Subtotal: new price - current price - 0 = 0 (equal exchange)
          if (subtotalElement) {
            subtotalElement.textContent = Shopify.formatMoney(0, window.theme.moneyFormat);
          }
        }

        // Show discount container immediately if there's a price difference
        const cartDiscountContainer = popup.querySelector('.exchange-voucher-popup__cart-discount[data-exchange-cart-discount]');
        if (cartDiscountContainer) {
          if (difference > 0) {
            cartDiscountContainer.style.display = 'block';
            cartDiscountContainer.classList.remove('hidden');

          } else {
            cartDiscountContainer.style.display = 'none';
            cartDiscountContainer.classList.add('hidden');

          }
        }


        // Mark popup as active for background task checking
        popup.dataset.popupActive = 'true';

        // Popup is ready for user interaction - no loading state needed


        // Background task: Smart cart preparation for exchange
        const variantId = variantInfo?.id || productId; // Use variant ID from variantInfo or fallback to productId
        const productVariantGid = variantId ? `gid://shopify/ProductVariant/${variantId}` : null;
        ExchangeCartManager.prepareCartForExchange(difference, exchangeData.voucherId, productVariantGid)
          .then(async (cartData) => {
            // Check if popup is still active before proceeding
            if (popup.dataset.popupActive !== 'true') {

              return;
            }


            popup.dataset.cartPrepared = 'true';

            // Initialize discount manager with prepared cart data (only if there's a price difference)
            if (cartData && popup.dataset.popupActive === 'true') {

              await PopupUtils.initializeExchangeDiscountManager(popup, cartData);


              await PopupUtils.updateExchangePopupTotals(popup, cartData, voucherValue);

              // Pre-initialize Qliro checkout in background (only if popup still active and price difference > 0)
              if (popup.dataset.popupActive === 'true' && difference > 0) {

                await PopupUtils.initializeExchangeQliroCheckout(popup, cartData);


                if (popup.dataset.qliroReady === 'true') {

                } else {
                  console.warn('⚠️ Exchange popup partially prepared: Cart ready, Qliro failed - will use fallback');
                }
              } else if (difference <= 0) {

              }
            }
          })
          .catch((error) => {
            // Only show error if popup is still active
            if (popup.dataset.popupActive === 'true') {
              console.error('Error preparing exchange cart:', error);
              popup.dataset.cartPrepared = 'false';
              popup.dataset.qliroReady = 'false';

              // Show error notification
              if (toastNotifications) {
                toastNotifications.error('Failed to prepare exchange cart. Please try again.');
              }
            }
          });

      } catch (error) {
        console.error('Error setting up exchange popup:', error);

        // Show error notification instead of inline error
        if (toastNotifications) {
          toastNotifications.error(`Error loading exchange: ${error.message}`);
        } else {
          // Use toast notification for exchange loading errors
          if (toastNotifications) {
            toastNotifications.error(`Error loading exchange: ${error.message}`);
          }
        }
      }
    }, element);
  }

  // Consolidated cart preparation utility to follow DRY principles
  static async prepareVoucherCart(operationType, config = {}) {
    try {


      // Step 1: Clear the cart (common for all voucher operations)
      await SharedCartUtils.clearCartAndCookies();

      // Step 2: Get product configuration from theme settings
      const productConfigKey = operationType === 'exchange' ? 'exchangeProduct' : 'extensionProduct';
      const product = window.account?.[productConfigKey];

      if (!product) {
        throw new Error(`${operationType} product not configured in theme settings. Please contact support.`);
      }

      const variantId = product.variantId;
      if (!variantId) {
        throw new Error(`${operationType} variant ID not found in theme settings`);
      }

      // Step 3: Determine quantity and properties based on operation type
      let quantity = 1;
      let properties = null;

      if (operationType === 'exchange') {
        // For exchange: calculate quantity based on price difference
        const priceDifference = config.priceDifference || 0;

        if (priceDifference <= 0) {

          // Just get current cart data for discount functionality
          try {
            const currentCartData = await window.apiService.get('/cart.js');
            return typeof currentCartData === 'string' ? JSON.parse(currentCartData) : currentCartData;
          } catch (error) {
            console.warn('Failed to get current cart data for direct exchange:', error);
            return null;
          }
        }

        const productPrice = product.price; // Price in cents
        if (!productPrice) {
          throw new Error(`Exchange product configuration incomplete: variantId=${variantId}, price=${productPrice}`);
        }

        // Calculate quantity needed based on price difference
        const productPriceValue = productPrice / 100; // Convert from cents
        quantity = Math.ceil(priceDifference / productPriceValue);

        // Set exchange properties for Qliro
        properties = {
          '_extend_type': 'voucher_exchange',
          '_gift_card_gid': config.voucherGid || config.voucherId,
          '_product_variant_gid': config.productVariantGid
        };


      } else if (operationType === 'extension') {
        // For extension: fixed quantity with gift card properties
        quantity = 1;
        properties = {
          _gift_card_gid: config.voucherIdentifier,
          _extend_type: 'voucher_extension'
        };
      }

      // Step 4: Add product to cart


      const cartData = await PopupUtils.addProductToCart(variantId, quantity, properties);

      // Step 5: Validate cart state
      if (!cartData || !cartData.item_count || cartData.item_count === 0) {
        throw new Error(`Failed to prepare ${operationType} cart - cart is empty after addition`);
      }

      // Step 6: Update global state (common post-processing)
      window.cart = cartData;
      SharedCartUtils.updateHeaderCartIcon(cartData.item_count || 0);



      // Step 7: Show success notification for exchange operations
      if (operationType === 'exchange' && toastNotifications) {
        toastNotifications.success('Exchange cart prepared successfully');
      }

      return cartData;

    } catch (error) {
      console.error(`Error preparing ${operationType} cart:`, error);
      throw error;
    }
  }

  static async prepareExtendCartAndQliro(popup, voucherIdentifier) {
    return this.prepareVoucherCart('extension', { voucherIdentifier });
  }

  static openPopup(selector, voucherIdentifier, setupCallback = null, element = document, isChild = false) {


    if (window.globalPopup && typeof window.globalPopup.openPopup === 'function') {
      const popup = element.querySelector(selector);


      if (popup) {
        popup.dataset.voucherIdentifier = voucherIdentifier;


        if (setupCallback) {

          setupCallback(popup);
        }


        window.globalPopup.openPopup(popup, isChild);

      } else {
        console.error('Popup element not found with selector:', selector);
      }
    } else {
      console.error('window.globalPopup or openPopup method not available');
    }
  }



  static async addProductToCart(variantId, quantity = 1, properties = null) {
    try {
      // Prepare the payload
      const payload = {
        id: variantId,
        quantity: quantity
      };

      // Add properties if provided
      if (properties && typeof properties === 'object') {
        payload.properties = properties;
      }

      // Add product to cart using apiService - this returns only the added item
      const addResponse = await window.apiService.post('/cart/add.js', payload);



      // Fetch the full cart data after adding the item
      const cartResponse = await window.apiService.get('/cart.js');

      // Parse the cart response if it comes back as a string
      let cartData = cartResponse;
      if (typeof cartResponse === 'string') {
        cartData = JSON.parse(cartResponse);
      }


      return cartData;
    } catch (error) {
      console.error('Error adding product to cart:', error);
      throw error;
    }
  }

  static async updateExchangePricingSummaryWithDiscounts(popup, responseData) {
    // Extract applied gift cards from response
    let appliedGiftCards = [];

    if (responseData?.data?.cart?.appliedGiftCards) {
      appliedGiftCards = responseData.data.cart.appliedGiftCards || [];
    } else if (responseData?.appliedGiftCards) {
      appliedGiftCards = responseData.appliedGiftCards || [];
    } else if (responseData?.discounts?.giftCards) {
      appliedGiftCards = responseData.discounts.giftCards || [];
    }

    if (!Array.isArray(appliedGiftCards)) {
      appliedGiftCards = [];
    }

    // Fetch cart data to get discount codes
    let cartData = null;
    let discountCodes = [];
    try {
      const cartResponse = await fetch('/cart.js');
      cartData = await cartResponse.json();
      if (typeof window.syncKnownCartStateFromCart === 'function') {
        window.syncKnownCartStateFromCart(cartData);
      } else if (typeof window.updateKnownCartState === 'function') {
        window.updateKnownCartState(cartData);
      } else {
        const itemCount = Number(cartData?.item_count || 0);
        const itemLength = Array.isArray(cartData?.items) ? cartData.items.length : 0;
        const cartStateMeta = typeof window.buildCartStateMeta === 'function'
          ? window.buildCartStateMeta()
          : { updated_at: new Date().toISOString(), updated_at_ms: Date.now() };

        window.cart = cartData;
        window.__cartState = {
          isLoaded: true,
          itemCount,
          isEmpty: itemCount === 0 && itemLength === 0,
          ...cartStateMeta
        };
      }

      // Extract discount codes from cart_level_discount_applications
      if (cartData?.cart_level_discount_applications) {
        discountCodes = cartData.cart_level_discount_applications;
      }
    } catch (error) {
      console.warn('Failed to fetch cart data for discount codes:', error);
    }

    const summaryContainer = popup.querySelector('#exchange-applied-codes-summary');
    if (!summaryContainer) return;

    // Clear existing content
    summaryContainer.innerHTML = '';

    // Check if we have any discounts to display
    const hasDiscounts = discountCodes.length > 0 || appliedGiftCards.length > 0;

    // Hide container if no discounts
    if (!hasDiscounts) {
      summaryContainer.style.display = 'none';

      // Still need to update subtotal without discounts
      const currentPrice = parseFloat(popup.dataset.voucherValue) || 0;
      const newPrice = parseInt(popup.dataset.productPrice) / 100;
      const priceDifference = newPrice - currentPrice;

      // Check if we're returning a gift card (downgrade scenario)
      const giftcardRowElement = popup.querySelector('[data-giftcard-row]');
      const giftcardCreditElement = popup.querySelector('[data-exchange-giftcard-credit]');
      let giftCardCredit = 0;

      if (priceDifference < 0) {
        const downgradeAmount = Math.abs(priceDifference);
        // If downgrade is more than 50 SEK, a gift card will be returned
        if (downgradeAmount > 50) {
          giftCardCredit = downgradeAmount;
          // Show the gift card in the pricing summary
          if (giftcardRowElement && giftcardCreditElement) {
            const formattedGiftcard = Shopify.formatMoney(downgradeAmount * 100, window.theme.moneyFormat);
            giftcardCreditElement.textContent = `+${formattedGiftcard}`;
            giftcardRowElement.style.display = 'flex';
          }
        } else {
          // Hide gift card row if downgrade is too small
          if (giftcardRowElement) {
            giftcardRowElement.style.display = 'none';
          }
        }
      } else {
        // Hide gift card row for upgrade scenarios
        if (giftcardRowElement) {
          giftcardRowElement.style.display = 'none';
        }
      }

      // Subtotal: new price - current price + gift card credit
      // Example: 649 - 3085 + 2436 = 0 (net amount to pay)
      const calculatedSubtotal = priceDifference + giftCardCredit;

      const subtotalElement = popup.querySelector('[data-exchange-subtotal]');
      if (subtotalElement) {
        subtotalElement.textContent = Shopify.formatMoney(Math.max(0, calculatedSubtotal) * 100, window.theme.moneyFormat);
      }
      return;
    }

    // Show container when there are discounts
    summaryContainer.style.display = 'flex';

    // Calculate total discounts
    let totalGiftCardDiscount = 0;
    let totalDiscountCodeAmount = 0;

    // Generate HTML for discount codes first
    discountCodes.forEach((discount) => {
      const amount = discount.total_allocated_amount || 0;
      totalDiscountCodeAmount += amount;

      const discountDisplay = amount > 0 ? `-${Shopify.formatMoney(amount, window.theme.moneyFormat)}` : '';

      summaryContainer.innerHTML += `
        <div class="exchange__pricing-row exchange__pricing-row--discount">
          <span class="u-p3 exchange__giftcard-label">
            <span>${discount.title}</span>
            <button class="cart-discount__remove-link u-s4" data-action="remove-discount" data-code-to-remove="${discount.title}" title="Remove ${discount.title}">Remove code</button>
          </span>
          <span class="u-p3 exchange__discount-amount">${discountDisplay}</span>
        </div>
      `;
    });

    // Generate HTML for each applied gift card
    appliedGiftCards.forEach((gc) => {
      // Use shared utility for gift card data extraction
      const { lastChars, giftCardId, amount } = SharedCartUtils.extractGiftCardData(gc);

      totalGiftCardDiscount += amount;

      const giftCardDisplay = amount > 0 ? `-${Shopify.formatMoney(amount, window.theme.moneyFormat)}` : '';

      summaryContainer.innerHTML += `
        <div class="exchange__pricing-row exchange__pricing-row--giftcard">
          <span class="u-p3 exchange__giftcard-label">
            <span>Gift Card (...${lastChars})</span>
            <button class="cart-discount__remove-link u-s4" data-action="remove-discount" data-giftcard-id-to-remove="${giftCardId}" title="Remove gift card ending in ${lastChars}">Remove code</button>
          </span>
          <span class="u-p3 exchange__discount-amount">${giftCardDisplay}</span>
        </div>
      `;
    });

    // Update subtotal calculation
    const currentPrice = parseFloat(popup.dataset.voucherValue) || 0;
    const newPrice = parseInt(popup.dataset.productPrice) / 100;
    const giftCardDiscount = totalGiftCardDiscount / 100; // Convert from cents
    const discountCodeAmount = totalDiscountCodeAmount / 100; // Convert from cents
    const totalDiscount = giftCardDiscount + discountCodeAmount;
    const priceDifference = newPrice - currentPrice;

    // Check if we're returning a gift card (downgrade scenario)
    const giftcardRowElement = popup.querySelector('[data-giftcard-row]');
    const giftcardCreditElement = popup.querySelector('[data-exchange-giftcard-credit]');
    let giftCardCredit = 0;

    if (priceDifference < 0) {
      const downgradeAmount = Math.abs(priceDifference);
      // If downgrade is more than 50 SEK, a gift card will be returned
      if (downgradeAmount > 50) {
        giftCardCredit = downgradeAmount;
        // Show the gift card in the pricing summary
        if (giftcardRowElement && giftcardCreditElement) {
          const formattedGiftcard = Shopify.formatMoney(downgradeAmount * 100, window.theme.moneyFormat);
          giftcardCreditElement.textContent = `+${formattedGiftcard}`;
          giftcardRowElement.style.display = 'flex';
        }
      } else {
        // Hide gift card row if downgrade is too small
        if (giftcardRowElement) {
          giftcardRowElement.style.display = 'none';
        }
      }
    } else {
      // Hide gift card row for upgrade scenarios
      if (giftcardRowElement) {
        giftcardRowElement.style.display = 'none';
      }
    }

    // Subtotal: new price - current price + gift card credit - applied discounts
    // Example: 649 - 3085 + 2436 - 0 = 0 (net amount to pay)
    const calculatedSubtotal = priceDifference + giftCardCredit - totalDiscount;

    const subtotalElement = popup.querySelector('[data-exchange-subtotal]');
    if (subtotalElement) {
      subtotalElement.textContent = Shopify.formatMoney(Math.max(0, calculatedSubtotal) * 100, window.theme.moneyFormat);

    }
  }

  static async updateExchangePopupTotals(popup, cartData, voucherValue) {

    if (cartData) {

      // Use cart total_price which includes any applied discounts
      const cartTotal = cartData.total_price / 100; // Convert from cents to dollars
      popup.dataset.cartTotal = cartTotal.toFixed(2);

      // Get current and new prices
      const currentPrice = voucherValue; // Already in dollars
      const newPrice = parseInt(popup.dataset.productPrice) / 100; // Convert from cents to dollars
      const originalDifference = parseFloat(popup.dataset.priceDifference) || 0;
      const priceDifference = newPrice - currentPrice;

      // Calculate gift card discount: how much discount was applied from original difference
      // originalDifference = what customer should pay without discounts
      // cartTotal = what customer pays after discounts
      // giftCardDiscount = discount amount applied
      let giftCardDiscount = 0;
      let giftCardCredit = 0;

      if (originalDifference > 0) {
        // Upgrade scenario: Calculate discount applied
        giftCardDiscount = Math.max(0, originalDifference - cartTotal);
      } else if (priceDifference < 0) {
        // Downgrade scenario: Gift card will be returned
        const downgradeAmount = Math.abs(priceDifference);
        if (downgradeAmount > 50) {
          giftCardCredit = downgradeAmount;
        }
      }

      // Show/hide gift card row based on scenario
      const giftcardRowElement = popup.querySelector('[data-giftcard-row]');
      const giftcardCreditElement = popup.querySelector('[data-exchange-giftcard-credit]');

      if (giftCardCredit > 0) {
        // Show the gift card in the pricing summary
        if (giftcardRowElement && giftcardCreditElement) {
          const formattedGiftcard = Shopify.formatMoney(giftCardCredit * 100, window.theme.moneyFormat);
          giftcardCreditElement.textContent = `+${formattedGiftcard}`;
          giftcardRowElement.style.display = 'flex';
        }
      } else {
        // Hide gift card row for upgrade scenarios or small downgrades
        if (giftcardRowElement) {
          giftcardRowElement.style.display = 'none';
        }
      }

      // Calculate subtotal: new price - current price + gift card credit - gift card discount
      // Upgrade: newPrice - currentPrice - giftCardDiscount
      // Downgrade: newPrice - currentPrice + giftCardCredit = negative + positive = 0
      const subtotalElement = popup.querySelector('[data-exchange-subtotal]');
      if (subtotalElement) {
        const calculatedSubtotal = newPrice - currentPrice + giftCardCredit - giftCardDiscount;
        subtotalElement.textContent = Shopify.formatMoney(Math.max(0, calculatedSubtotal) * 100, window.theme.moneyFormat);

      }
    }
  }

  static async updateExchangeDiscountSummary(popup, cartData) {
    try {

      if (!cartData || Number(cartData.item_count || 0) <= 0) {
        return;
      }

      // Get applied discounts from the discount manager
      if (!popup.exchangeCartDiscountManager || !popup.exchangeCartDiscountManager.cartItems.currentCartToken) {
        console.warn('Exchange discount manager or cart token not available for summary update');
        return;
      }

      const response = await fetch(`${window.proxyPath}/checkout-discounts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          action: 'GET_STATUS',
          cart_token: popup.exchangeCartDiscountManager.cartItems.currentCartToken
        })
      });

      const discountData = await response.json();

      // Check success flag instead of HTTP status (API always returns 200)
      if (discountData.success === false) {
        console.warn('Failed to fetch discount status for summary:', discountData.message, discountData.errorCode);
        return;
      }

      // Use the shared method to update from data
      await PopupUtils.updateExchangeDiscountSummaryFromData(popup, cartData, discountData);

    } catch (error) {
      console.warn('Error updating exchange discount summary:', error);
    }
  }

  static async updateExchangeDiscountSummaryFromData(popup, cartData, discountData) {
    try {





      // Extract applied discounts and gift cards
      let appliedDiscounts = [];
      let appliedGiftCards = [];

      // Get discount codes from server-side (these are typically percentage or fixed amount discounts)


      if (cartData.discount_applications && cartData.discount_applications.length > 0) {
        cartData.discount_applications.forEach(discount => {
          appliedDiscounts.push({
            type: 'discount_code',
            title: discount.title,
            code: discount.title, // For discount codes, title is usually the code
            value: discount.value,
            value_type: discount.value_type, // 'percentage' or 'fixed_amount'
            target_type: discount.target_type
          });
        });
      }

      // Also check cart_level_discount_applications (Shopify's standard format)
      if (cartData.cart_level_discount_applications && cartData.cart_level_discount_applications.length > 0) {
        cartData.cart_level_discount_applications.forEach(discount => {
          appliedDiscounts.push({
            type: 'cart_level_discount',
            title: discount.title,
            code: discount.title,
            value: discount.value,
            value_type: discount.value_type,
            target_type: discount.target_type,
            total_allocated_amount: discount.total_allocated_amount
          });
        });
      }

      // Get gift cards from our API response - use same logic as cart page
      // 1. GET_STATUS (new shape)
      if (discountData?.data?.cart?.appliedGiftCards) {
        appliedGiftCards = discountData.data.cart.appliedGiftCards || [];
      }
      // 2. ADD/REMOVE (new shape)
      else if (discountData?.appliedGiftCards) {
        appliedGiftCards = discountData.appliedGiftCards || [];
      }
      // 3. Fallback: discounts.giftCards (legacy/mixed)
      else if (discountData?.discounts?.giftCards) {
        appliedGiftCards = discountData.discounts.giftCards || [];
      }

      // If null, treat as empty array
      if (!Array.isArray(appliedGiftCards)) {
        appliedGiftCards = [];
      }



      // Update the discount summary display
      const summaryContainer = popup.querySelector('#exchange-applied-discounts-summary');
      const discountsList = popup.querySelector('#exchange-discounts-list');

      if (summaryContainer && discountsList) {
        let discountsHTML = '';

        // Add discount codes
        appliedDiscounts.forEach(discount => {
          let discountAmount = '';

          if (discount.total_allocated_amount) {
            // For cart_level_discount_applications, use total_allocated_amount
            discountAmount = `-${Shopify.formatMoney(discount.total_allocated_amount, window.theme.moneyFormat)}`;
          } else if (discount.value_type === 'percentage') {
            discountAmount = `${discount.value}% off`;
          } else if (discount.value_type === 'fixed_amount') {
            discountAmount = `-${Shopify.formatMoney(discount.value, window.theme.moneyFormat)}`;
          } else {
            discountAmount = `-${Shopify.formatMoney(discount.value, window.theme.moneyFormat)}`;
          }

          const discountCode = discount.code || discount.title;

          discountsHTML += `
            <div class="exchange-discount-item u-s4">
              <span class="discount-code">${discountCode}</span>
              <span class="discount-amount">${discountAmount}</span>
              <button type="button" 
                      class="exchange-discount__remove-btn u-s4" 
                      data-action="remove-discount" 
                      data-code-to-remove="${discountCode}"
                      title="Remove ${discountCode}">
                Remove
              </button>
            </div>
          `;
        });

        // Add gift cards using consolidated utility
        appliedGiftCards.forEach(giftCard => {
          const { lastChars, giftCardId, amount } = SharedCartUtils.extractGiftCardData(giftCard);
          const giftCardDisplay = amount > 0 ? `-${Shopify.formatMoney(amount, window.theme.moneyFormat)}` : '';
          const giftCardCode = `Gift Card (...${lastChars})`;

          discountsHTML += `
            <div class="exchange-discount-item u-s4">
              <span class="discount-code">${giftCardCode}</span>
              <span class="discount-amount">${giftCardDisplay}</span>
              <button type="button" 
                      class="exchange-discount__remove-btn u-s4" 
                      data-action="remove-gift-card" 
                      data-gift-card-id="${giftCardId}"
                      title="Remove ${giftCardCode}">
                Remove
              </button>
            </div>
          `;
        });

        if (discountsHTML) {
          discountsList.innerHTML = discountsHTML;
          summaryContainer.style.display = 'block';

        } else {
          summaryContainer.style.display = 'none';

        }
      } else {
        console.warn('Exchange discount summary containers not found');
      }

    } catch (error) {
      console.warn('Error updating exchange discount summary from data:', error);
    }
  }

  static async initializeExchangeDiscountManager(popup, cartData) {
    if (typeof CartDiscountManager !== 'undefined') {
      const cartDiscountContainer = popup.querySelector('[data-exchange-cart-discount]');
      const priceDifference = parseFloat(popup.dataset.priceDifference) || 0;

      if (cartDiscountContainer && cartData) {


        // Only initialize discount manager if user needs to pay additional amount
        if (priceDifference > 0) {
          // Ensure discount container is visible
          if (cartDiscountContainer.style.display === 'none') {
            cartDiscountContainer.style.display = 'block';
            cartDiscountContainer.classList.remove('hidden');
          }

          // Use cart token if available, otherwise use a fallback approach
          const cartToken = cartData.token || cartData.id || 'exchange-cart';


          // Create a mock cartItems object that CartDiscountManager expects
          const mockCartItems = {
            element: cartDiscountContainer,
            currentCartToken: cartToken,
            currentCartItemCount: Number(cartData.item_count || 0),
            refreshAfterUpdate: async (needsHTMLRefresh = false) => {
              // Custom refresh logic for exchange popup
              try {
                const updatedCartResponse = await fetch('/cart.js', {
                  headers: { 'Accept': 'application/json' }
                });

                if (updatedCartResponse.ok) {
                  const updatedCartData = await updatedCartResponse.json();
                  if (typeof window.syncKnownCartStateFromCart === 'function') {
                    window.syncKnownCartStateFromCart(updatedCartData);
                  } else if (typeof window.updateKnownCartState === 'function') {
                    window.updateKnownCartState(updatedCartData);
                  } else {
                    const itemCount = Number(updatedCartData?.item_count || 0);
                    const itemLength = Array.isArray(updatedCartData?.items) ? updatedCartData.items.length : 0;
                    const cartStateMeta = typeof window.buildCartStateMeta === 'function'
                      ? window.buildCartStateMeta()
                      : { updated_at: new Date().toISOString(), updated_at_ms: Date.now() };

                    window.cart = updatedCartData;
                    window.__cartState = {
                      isLoaded: true,
                      itemCount,
                      isEmpty: itemCount === 0 && itemLength === 0,
                      ...cartStateMeta
                    };
                  }
                  const updatedItemCount = Number(updatedCartData.item_count || 0);
                  mockCartItems.currentCartItemCount = updatedItemCount;

                  // Update header cart icon state
                  SharedCartUtils.updateHeaderCartIcon(updatedCartData.item_count || 0);

                  // Update popup totals with new cart data
                  const voucherValue = parseFloat(popup.dataset.voucherValue) || 0;
                  await PopupUtils.updateExchangePopupTotals(popup, updatedCartData, voucherValue);

                  // Make a single API call to get discount status
                  if (popup.exchangeCartDiscountManager && popup.exchangeCartDiscountManager.cartItems.currentCartToken && updatedItemCount > 0) {
                    try {
                      const response = await fetch(`${window.proxyPath}/checkout-discounts`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Accept': 'application/json'
                        },
                        body: JSON.stringify({
                          action: 'GET_STATUS',
                          cart_token: popup.exchangeCartDiscountManager.cartItems.currentCartToken
                        })
                      });

                      if (response.ok) {
                        const discountData = await response.json();

                        // Update both displays from the single API response
                        popup.exchangeCartDiscountManager.updateAppliedCodes(discountData);
                        await PopupUtils.updateExchangeDiscountSummaryFromData(popup, updatedCartData, discountData);
                      }
                    } catch (error) {
                      console.error('Error fetching discount status in refreshAfterUpdate:', error);
                    }
                  }

                  // Invalidate pre-prepared Qliro since cart has changed

                  popup.dataset.qliroReady = 'false';
                  popup.dataset.cartPrepared = 'false'; // Also mark cart as not prepared

                  // Clear the pre-prepared Qliro manager reference
                  if (popup.exchangeQliroManager) {
                    popup.exchangeQliroManager = null;
                  }

                  // Update Qliro checkout if it's visible in the exchange popup
                  await PopupUtils.updateExchangeQliroAfterDiscount(popup);

                  // DON'T mark as ready - force exchange to use fallback path

                }
              } catch (error) {
                console.error('Error refreshing exchange popup after update:', error);
                // Mark Qliro as not ready on error
                popup.dataset.qliroReady = 'false';
              }
            }
          };

          // Create CartDiscountManager instance with mock cartItems and exchange popup selectors
          const exchangeCartDiscountManager = new CartDiscountManager(mockCartItems, {
            codeInputSelector: '#exchange-discount-code',
            statusDivSelector: '#exchange-discount-status',
            appliedCodesSelector: '#exchange-applied-codes',
            applyBtnSelector: '[data-action="apply-discount"]'
          });

          // Override updateAppliedCodes to also update pricing summary
          const originalUpdateAppliedCodes = exchangeCartDiscountManager.updateAppliedCodes.bind(exchangeCartDiscountManager);
          exchangeCartDiscountManager.updateAppliedCodes = function (responseData) {
            // Call original method
            originalUpdateAppliedCodes(responseData);

            // Also update exchange pricing summary
            PopupUtils.updateExchangePricingSummaryWithDiscounts(popup, responseData);
          };

          // Bind events manually to both the cart discount container and the summary container
          const delegateElement = new Delegate(cartDiscountContainer);
          exchangeCartDiscountManager.bindEvents(delegateElement);

          // Also bind events to the exchange discount summary for remove buttons
          const summaryContainer = popup.querySelector('#exchange-applied-codes-summary');
          if (summaryContainer) {
            const summaryDelegateElement = new Delegate(summaryContainer);
            exchangeCartDiscountManager.bindEvents(summaryDelegateElement);
          }

          // Store reference for cleanup
          popup.exchangeCartDiscountManager = exchangeCartDiscountManager;

          // Load any existing discounts
          try {
            // Update cart token in case it changed during cart preparation
            mockCartItems.currentCartToken = cartData.token || cartData.id || 'exchange-cart';
            mockCartItems.currentCartItemCount = Number(cartData.item_count || 0);
            await exchangeCartDiscountManager.loadExistingDiscounts();


            // Note: updateExchangeDiscountSummary will be called by loadExistingDiscounts
            // so we don't need to call it separately here
          } catch (error) {
            console.warn('Error loading existing discounts for exchange popup:', error);
            // Don't fail the entire popup setup for discount loading errors
          }
        } else {

        }
      } else {
        console.warn('CartDiscountManager not available, cart discount container not found, or cart data missing for exchange popup');
        // Continue without discount functionality rather than failing
      }
    } else {
      console.warn('CartDiscountManager class not available - cart discount functionality will be disabled for exchange popup');
    }
  }

  static async updateExchangeQliroAfterDiscount(popup) {
    try {
      // Check if Qliro is currently visible in the popup
      const qliroContainer = popup.querySelector('.exchange-qliro-container');
      const qliroCheckoutContainer = qliroContainer?.querySelector('#qliro-checkout-container');

      if (!qliroContainer || !qliroCheckoutContainer) {

        return;
      }

      // Check if Qliro container is visible (display is not 'none')
      const isVisible = qliroContainer.style.display !== 'none' &&
        window.getComputedStyle(qliroContainer).display !== 'none';

      if (!isVisible) {

        return;
      }



      // IMMEDIATELY clear existing Qliro content and show loading state
      qliroCheckoutContainer.innerHTML = '<p class="cart-qliro__loading">Updating checkout...</p>';

      // Force a repaint to ensure loading state is visible immediately
      void qliroCheckoutContainer.offsetHeight;

      // Fetch fresh cart data to verify current state

      let freshCartData;
      try {
        const cartResponse = await fetch('/cart.js');
        freshCartData = await cartResponse.json();
        if (typeof window.syncKnownCartStateFromCart === 'function') {
          window.syncKnownCartStateFromCart(freshCartData);
        } else if (typeof window.updateKnownCartState === 'function') {
          window.updateKnownCartState(freshCartData);
        } else {
          const itemCount = Number(freshCartData?.item_count || 0);
          const itemLength = Array.isArray(freshCartData?.items) ? freshCartData.items.length : 0;
          const cartStateMeta = typeof window.buildCartStateMeta === 'function'
            ? window.buildCartStateMeta()
            : { updated_at: new Date().toISOString(), updated_at_ms: Date.now() };

          window.cart = freshCartData;
          window.__cartState = {
            isLoaded: true,
            itemCount,
            isEmpty: itemCount === 0 && itemLength === 0,
            ...cartStateMeta
          };
        }

      } catch (error) {
        console.error('🔄 Failed to fetch fresh cart data:', error);
      }

      // Small delay to ensure cart is fully processed on Qliro's side
      await new Promise(resolve => setTimeout(resolve, 500));

      // Verify popup is still active before proceeding
      if (popup.dataset.popupActive !== 'true') {

        return;
      }

      // Re-initialize Qliro with updated cart
      if (typeof CartQliroManager !== 'undefined') {

        // Create new Qliro manager instance with exchange context
        const qliroManager = new CartQliroManager(qliroContainer, {
          container: qliroCheckoutContainer,
          context: 'exchange'
        });

        // Force reinitialize with fresh cart data

        await qliroManager.initializeQliroCheckout(true);

      } else {
        console.warn('❌ CartQliroManager not available for Qliro update');
        qliroCheckoutContainer.innerHTML = '<p class="cart-qliro__loading" style="color: red;">Failed to update checkout. Please refresh the page.</p>';
      }
    } catch (error) {
      console.error('❌ Error updating Qliro after discount change:', error);

      // Show error in Qliro container
      const qliroCheckoutContainer = popup.querySelector('#qliro-checkout-container');
      if (qliroCheckoutContainer) {
        qliroCheckoutContainer.innerHTML = '<p class="cart-qliro__loading" style="color: red;">Failed to update checkout. Please try again or refresh the page.</p>';
      }

      // Show error message to user
      if (typeof toastNotifications !== 'undefined') {
        toastNotifications.warning('Checkout update failed. Please try again.');
      }
    }
  }

  static async initializeExchangeQliroCheckout(popup, cartData) {
    try {
      // Check if popup is still active before proceeding
      if (popup.dataset.popupActive !== 'true') {

        return;
      }



      // Create a hidden Qliro container in the popup for pre-initialization
      const existingQliroContainer = popup.querySelector('.exchange-qliro-container');
      let qliroContainer;

      if (existingQliroContainer) {
        qliroContainer = existingQliroContainer;

      } else {
        qliroContainer = document.createElement('div');
        qliroContainer.className = 'exchange-qliro-container';
        qliroContainer.style.display = 'none'; // Hidden initially

        // Create proper Qliro checkout container structure
        const qliroCheckoutDiv = document.createElement('div');
        qliroCheckoutDiv.id = 'qliro-checkout-container';
        qliroCheckoutDiv.className = 'cart-qliro';
        qliroCheckoutDiv.innerHTML = '<p class="cart-qliro__loading">Preparing checkout...</p>';
        qliroContainer.appendChild(qliroCheckoutDiv);

        const popupBody = popup.querySelector('.exchange-voucher-popup__body');
        if (popupBody) {
          popupBody.appendChild(qliroContainer);

        } else {
          console.error('Exchange popup body not found for Qliro container');
          return;
        }
      }

      // Double-check popup is still active before continuing with Qliro
      if (popup.dataset.popupActive !== 'true') {

        return;
      }

      // Initialize Qliro checkout using CartQliroManager
      if (typeof CartQliroManager !== 'undefined') {
        const qliroCheckoutContainer = qliroContainer.querySelector('#qliro-checkout-container');
        if (!qliroCheckoutContainer) {
          console.error('Qliro checkout container not found');
          return;
        }


        const qliroManager = new CartQliroManager(qliroContainer, {
          context: 'exchange'
        });

        // Set the cart token for Qliro initialization
        qliroManager.currentCartToken = cartData.token || cartData.id;



        // Initialize Qliro checkout with better error handling
        try {
          await qliroManager.initializeQliroCheckout();

        } catch (qliroError) {
          console.warn('Error during Qliro initialization:', qliroError);
          // Continue without failing - we can fall back to manual initialization
          popup.dataset.qliroReady = 'false';
          return;
        }

        // Final check before storing the manager reference
        if (popup.dataset.popupActive === 'true') {
          // Store reference for later use
          popup.exchangeQliroManager = qliroManager;
          popup.dataset.qliroReady = 'true';


        } else {

          qliroManager = null;
          popup.dataset.qliroReady = 'false';
        }
      } else {
        console.warn('CartQliroManager not available for exchange popup');
        popup.dataset.qliroReady = 'false';
      }

    } catch (error) {
      console.warn('Error pre-initializing Qliro checkout for exchange:', error);
      if (popup.dataset.popupActive === 'true') {
        popup.dataset.qliroReady = 'false';
      }
    }
  }

  static async initializeExtendQliroCheckout(popup, cartData) {
    try {
      // Check if popup is still active before proceeding
      if (popup.dataset.popupActive !== 'true') {

        return;
      }



      // Create a hidden Qliro container in the popup for pre-initialization
      const existingQliroContainer = popup.querySelector('.extend-qliro-container');
      let qliroContainer;

      if (existingQliroContainer) {
        qliroContainer = existingQliroContainer;

      } else {
        qliroContainer = document.createElement('div');
        qliroContainer.className = 'extend-qliro-container';
        qliroContainer.style.display = 'none'; // Hidden initially

        // Create proper Qliro checkout container structure
        const qliroCheckoutDiv = document.createElement('div');
        qliroCheckoutDiv.id = 'qliro-checkout-container';
        qliroCheckoutDiv.className = 'cart-qliro';
        qliroCheckoutDiv.innerHTML = '<p class="cart-qliro__loading">Preparing checkout...</p>';
        qliroContainer.appendChild(qliroCheckoutDiv);

        const popupBody = popup.querySelector('.extend-voucher-popup__body');
        if (popupBody) {
          popupBody.appendChild(qliroContainer);

        } else {
          console.error('Extend popup body not found for Qliro container');
          return;
        }
      }

      // Double-check popup is still active before continuing with Qliro
      if (popup.dataset.popupActive !== 'true') {

        return;
      }

      // Initialize Qliro checkout using CartQliroManager
      if (typeof CartQliroManager !== 'undefined') {
        const qliroCheckoutContainer = qliroContainer.querySelector('#qliro-checkout-container');
        if (!qliroCheckoutContainer) {
          console.error('Qliro checkout container not found in extend popup');
          return;
        }


        const qliroManager = new CartQliroManager(qliroContainer);

        // Set the cart token for Qliro initialization
        qliroManager.currentCartToken = cartData.token || cartData.id;


        // Initialize Qliro checkout with better error handling
        try {
          await qliroManager.initializeQliroCheckout();

        } catch (qliroError) {
          console.warn('Error during extend Qliro initialization:', qliroError);
          // Continue without failing - we can fall back to manual initialization
          popup.dataset.qliroReady = 'false';
          return;
        }

        // Final check before storing the manager reference
        if (popup.dataset.popupActive === 'true') {
          // Store reference for later use
          popup.extendQliroManager = qliroManager;
          popup.dataset.qliroReady = 'true';


        } else {

          qliroManager = null;
          popup.dataset.qliroReady = 'false';
        }
      } else {
        console.warn('CartQliroManager not available for extend popup');
        popup.dataset.qliroReady = 'false';
      }

    } catch (error) {
      console.warn('Error pre-initializing Qliro checkout for extend:', error);
      if (popup.dataset.popupActive === 'true') {
        popup.dataset.qliroReady = 'false';
      }
    }
  }
}

// Initialize global voucher utilities instance
const voucherUtils = new VoucherUtils();

class VoucherActionHandler extends HTMLElement {
  // Static Map to store original popup states for restoration (shared across all instances)
  static originalPopupStates = new Map();

  constructor() {
    super();

    this.element = this; // Set element reference to this custom element
    this.apiEndpointRedeem = `${window.proxyPath}/redeem`;
    this.apiEndpointRegift = `${window.proxyPath}/regift`;

    // Set element reference in global voucher utils for DOM operations
    voucherUtils.element = this.element;


  }

  connectedCallback() {


    // Only set up if not already done
    if (!this._initialized) {
      // Set up event delegation on this element for all voucher actions
      this.setupEventDelegation();

      // Set up global popup close handler if not already set
      this.setupGlobalPopupCloseHandler();

      this._initialized = true;
    }
  }

  disconnectedCallback() {


    // Clean up event delegation
    if (this.delegateElement) {
      this.delegateElement.off();
      this.delegateElement = null;
    }

    this._initialized = false;
  }

  // Set up event delegation on this element for efficiency
  setupEventDelegation() {


    // Use this custom element for event delegation
    this.delegateElement = new Delegate(this);
    this.delegateElement.on('click', '[data-action="voucher-action"]', this.handleVoucherAction.bind(this));

    // Also handle voucher grouping actions
    this.delegateElement.on('click', '[data-action="combine-vouchers"]', this.handleCombineVouchers.bind(this));
    this.delegateElement.on('click', '[data-action="continue-without-grouping"]', this.handleContinueWithoutGrouping.bind(this));

    // Handle bulk cancel booking action from unified popup
    this.delegateElement.on('click', '[data-action="cancel-all-bookings"]', this.handleCancelAllBookings.bind(this));

    // Handle bulk complete booking action from unified popup
    this.delegateElement.on('click', '[data-action="complete-all-bookings"]', this.handleCompleteAllBookings.bind(this));

    // Handle update booking datetime action from edit popup
    this.delegateElement.on('click', '[data-action="update-booking-datetime"]', this.handleUpdateBookingDateTime.bind(this));

  }

  setupGlobalPopupCloseHandler() {
    // Only set up the handler if globalPopup exists and doesn't already have our handler
    if (window.globalPopup && !window.globalPopup._voucherCloseHandlerSet) {


      // Store the original onPopupClose if it exists
      const originalOnPopupClose = window.globalPopup.onPopupClose;

      window.globalPopup.onPopupClose = (popup) => {

        // Call original handler first if it exists
        if (originalOnPopupClose && typeof originalOnPopupClose === 'function') {
          try {
            originalOnPopupClose(popup);
          } catch (error) {
            console.warn('Error in original popup close handler:', error);
          }
        }

        // Handle voucher-specific popups
        VoucherActionHandler.handleVoucherPopupClose(popup);
      };

      // Mark that we've set up our handler
      window.globalPopup._voucherCloseHandlerSet = true;
    }
  }

  static handleVoucherPopupClose(popup) {
    if (!popup || !popup.id) return;



    // Handle exchange voucher popup - clear cart when closed without proceeding
    if (popup.id === 'exchange-voucher-popup') {


      // Mark popup as inactive to stop any background processes
      popup.dataset.popupActive = 'false';

      // Reset the popup to original state
      if (typeof VoucherActionHandler.restorePopupState === 'function') {
        VoucherActionHandler.restorePopupState(popup);
      } else {
        console.warn('VoucherActionHandler.restorePopupState not available for exchange popup');
        VoucherActionHandler.resetExchangeVoucherPopup(popup);
      }

      // Clear cart when closing without proceeding
      SharedCartUtils.clearCartAndCookies();
    }
    // Handle extend voucher popup - still clear cart for extend operations
    else if (popup.id === 'extend-voucher-popup') {


      // Mark popup as inactive to stop any background processes
      popup.dataset.popupActive = 'false';

      if (typeof VoucherActionHandler.restorePopupState === 'function') {
        VoucherActionHandler.restorePopupState(popup);
      } else {
        console.warn('VoucherActionHandler.restorePopupState not available for extend popup');
        VoucherActionHandler.resetExtendVoucherPopup(popup);
      }

      // Clear localStorage to prevent false success toast on page refresh
      try {
        localStorage.removeItem('extendGiftCard');

      } catch (error) {
        console.warn('Failed to clear extend localStorage:', error);
      }

      // Clear cart for extend operations
      SharedCartUtils.clearCartAndCookies();
    }
    // Handle voucher grouping popup - reset input field
    else if (popup.id.includes('voucher-grouping-popup')) {


      // Reset the input field in the grouping form
      const inputForm = popup.querySelector('voucher-action-handler.collapsible-form');
      if (inputForm) {
        const voucherInput = inputForm.querySelector('[name="voucher_code"]');
        if (voucherInput) {
          voucherInput.value = '';
        }

        // Clear any validation messages
        const inputValidation = inputForm.querySelector('.input-validation');
        if (inputValidation) {
          inputValidation.style.display = 'none';
          inputValidation.innerHTML = '';
        }

        // Remove error class from field
        const field = voucherInput?.closest('.field');
        if (field) {
          field.classList.remove('field--error');
        }
      }
    }
    // Handle other voucher popups - clear cart for redeem operations only  
    else {
      const voucherPopupIds = ['redeem-confirm-popup', 'connect-confirm-popup', 'account-gift-popup', 'cancel-booking-popup', 'cancel-booking-popup-unified', 'complete-booking-popup'];
      if (voucherPopupIds.includes(popup.id)) {

        // Reset gift popup form if it's the gift popup (no cart clearing for gift)
        if (popup.id === 'account-gift-popup') {

          VoucherActionHandler.resetGiftPopupForm(popup);
        }
        // Clear cart for both redeem and connect popups
        else if (popup.id === 'redeem-confirm-popup' || popup.id === 'connect-confirm-popup') {

          SharedCartUtils.clearCartAndCookies();
        }
        // Handle cancel booking popup close (both regular and unified)
        else if (popup.id === 'cancel-booking-popup' || popup.id === 'cancel-booking-popup-unified') {

          VoucherActionHandler.resetCancelBookingPopup(popup);
        }
        // Handle complete booking popup close
        else if (popup.id === 'complete-booking-popup') {

          VoucherActionHandler.resetCompleteBookingPopup(popup);
        }
      }
    }
  }

  handleVoucherPopupClose(popup) {
    VoucherActionHandler.handleVoucherPopupClose(popup);
  }

  static resetExtendVoucherPopup(popup) {
    SharedCartUtils.resetVoucherPopup(popup, 'extend', 'Extend Voucher');
  }

  static resetCancelBookingPopup(popup) {


    // Clear all dataset attributes used for single and group cancellations
    delete popup.dataset.voucherId;
    delete popup.dataset.voucherData;
    delete popup.dataset.voucherIds;
    delete popup.dataset.isGroupCancellation;
    delete popup.dataset.groupId;
    delete popup.dataset.bookingTokens;
    delete popup.dataset.selectedBookingIds; // For unified popup

    // Clear any textarea content (cancel reason)
    const textarea = popup.querySelector('textarea[name="cancel_reason_message"]');
    if (textarea) {
      textarea.value = '';
    }

    // Reset button state if it was disabled
    // Handle both regular popup (data-method="cancel-booking") and unified popup (data-action="cancel-all-bookings")
    const cancelButton = popup.querySelector('[data-method="cancel-booking"]') ||
      popup.querySelector('[data-action="voucher-action"][data-method="cancel-booking"]') ||
      popup.querySelector('[data-action="cancel-all-bookings"]');
    if (cancelButton) {
      cancelButton.disabled = false;
      // Restore original button text if needed
      const originalText = TranslationUtils.get('customer.account.vouchers.cancel_booking.confirm_cancel', 'Cancel Booking');

      // Try different text locations
      const buttonText = cancelButton.querySelector('.ButtonText');
      if (buttonText) {
        buttonText.textContent = originalText;
      } else {
        // If no .ButtonText element, set directly on button
        cancelButton.textContent = originalText;
      }
    }


  }

  static resetCompleteBookingPopup(popup) {


    // Clear dataset attributes used for tracking
    delete popup.dataset.mode;
    delete popup.dataset.selectedBookingIds;

    // Reset the form
    const form = popup.querySelector('[data-complete-booking-form]');
    if (form) {
      // Reset radio buttons - set "yes" as checked
      const yesRadio = form.querySelector('input[name="experience_completed"][value="yes"]');
      const noRadio = form.querySelector('input[name="experience_completed"][value="no"]');
      if (yesRadio) yesRadio.checked = true;
      if (noRadio) noRadio.checked = false;

      // Hide and clear the reasoning section
      const reasoningSection = form.querySelector('[data-reasoning-section]');
      if (reasoningSection) {
        reasoningSection.style.display = 'none';
      }

      // Clear textarea
      const textarea = form.querySelector('#reasoning_message') || form.querySelector('textarea[name="reasoning_message"]');
      if (textarea) {
        textarea.value = '';
      }

      // Reset reason category radio buttons
      const reasonCategoryRadios = form.querySelectorAll('input[name="reason_category"]');
      reasonCategoryRadios.forEach(radio => {
        radio.checked = false;
      });
    }

    // Reset button state if it was disabled
    const confirmButton = popup.querySelector('.complete-booking-popup__confirm-btn');
    if (confirmButton) {
      confirmButton.disabled = false;

      // Restore original button text
      const originalText = TranslationUtils.get('vendor.bookings.complete_popup.confirm', 'Confirm booking');
      const buttonText = confirmButton.querySelector('.ButtonText');
      if (buttonText) {
        buttonText.textContent = originalText;
      } else {
        confirmButton.textContent = originalText;
      }
    }

    // Hide loader if visible
    const loader = popup.querySelector('.itemLoader, [data-loader]');
    if (loader) {
      loader.classList.remove('is-visible');
      loader.style.display = 'none';
    }


  }

  static resetExchangeVoucherPopup(popup) {
    SharedCartUtils.resetVoucherPopup(popup, 'exchange', 'Exchange Voucher');
  }

  /**
   * Determines the cancellation action type for a voucher
   * Based on cancel_implementation_fe.md documentation
   * @param {Object} voucher - The voucher object
   * @returns {string|null} - "cancel_request", "cancel_confirmed", or null if cannot cancel
   */
  getCancelActionType(voucher) {
    // Check if already canceled or completed first
    const completedStatuses = ['canceled', 'completed_canceled', 'completed_carried'];
    if (completedStatuses.includes(voucher.booking_status)) {
      return null; // Cannot cancel
    }

    // Determine based on booking status
    // IN_PROGRESS status = unconfirmed booking request (can always be canceled)
    if (voucher.booking_status === 'IN_PROGRESS') {
      return 'cancel_request'; // Unconfirmed booking request
    }

    // booked status = confirmed booking (needs date eligibility check)
    if (voucher.booking_status === 'booked') {
      return 'cancel_confirmed'; // Confirmed booking
    }

    // For vouchers without explicit booking_status, check if they have booking info
    // and is_booked flag or booking_gid_ref
    if (!voucher.booking_status) {
      if (voucher.is_booked || voucher.booking_gid_ref) {
        // Has booking info but no status - treat as unconfirmed request
        return 'cancel_request';
      }
    }

    // No booking to cancel
    return null;
  }

  /**
   * Gets all voucher IDs from a booking (including group bookings)
   * @param {Object} voucher - Voucher object that may be part of a group
   * @returns {Array<string>} - Array of voucher GIDs
   */
  getAllVoucherIdsForCancellation(voucher) {
    // If it's a single voucher (most common case)
    if (voucher.shopify_gift_card_id) {
      return [voucher.shopify_gift_card_id];
    }

    // Fallback
    return [];
  }

  // Open cancel booking popup
  openCancelBookingPopup(voucher) {


    const popup = document.querySelector('#cancel-booking-popup');
    if (!popup) {
      console.error('Cancel booking popup not found');
      ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', 'Cancel booking popup not available. Please refresh the page and try again.');
      return;
    }

    // Store voucher data in popup dataset
    popup.dataset.voucherId = voucher.shopify_gift_card_id;
    popup.dataset.voucherData = JSON.stringify(voucher);

    // Open the popup
    if (window.globalPopup && typeof window.globalPopup.openPopup === 'function') {
      window.globalPopup.openPopup(popup);

    } else {
      console.error('Global popup manager not available');
      ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', 'Unable to open cancel booking popup. Please refresh the page and try again.');
    }
  }

  // Handle initial cancel booking action - open popup with voucher data
  async handleInitCancelBookingAction(event) {


    const button = event.target.closest('button');
    const voucherId = button?.dataset.voucherId;

    if (!voucherId) {
      console.error('No voucher ID found for cancel booking');
      ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', 'Voucher ID not found. Please try again.');
      return;
    }

    try {
      // Find the AccountVouchers instance to access voucher data and helper methods
      const accountVouchersElement = document.querySelector('account-vouchers');
      if (!accountVouchersElement) {
        console.error('AccountVouchers element not found');
        ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', 'Unable to access voucher data. Please refresh the page and try again.');
        return;
      }

      // Find the voucher data
      const voucher = accountVouchersElement.findVoucherById(voucherId);
      if (!voucher) {
        console.error('Voucher not found for cancellation:', voucherId);
        ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', 'Voucher not found. Please refresh the page and try again.');
        return;
      }

      // Check if cancellation is still allowed
      const cancellationInfo = accountVouchersElement.getCancellationInfo(voucher);
      if (!cancellationInfo.canCancel) {
        let message;
        if (cancellationInfo.reason === 'too_late') {
          message = cancellationInfo.message;
        } else {
          message = TranslationUtils.get(
            'customer.account.vouchers.notifications.cancellation_not_allowed',
            'Cancellation is no longer allowed for this booking. Contact support if you need assistance.'
          );
        }

        ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', message);
        return;
      }

      // Open cancel booking popup
      this.openCancelBookingPopup(voucher);

    } catch (error) {
      console.error('Error in handleInitCancelBookingAction:', error);
      ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', 'An error occurred while opening cancel booking popup.');
    }
  }

  // Open complete booking popup with voucher data
  openCompleteBookingPopup(voucher) {


    const popup = document.querySelector('#complete-booking-popup');
    if (!popup) {
      console.error('Complete booking popup not found');
      ErrorUtils.error('customer.account.complete_booking', 'complete_booking_error', 'Complete booking popup not available. Please refresh the page and try again.');
      return;
    }

    // Find the AccountVouchers instance to use the unified popup configuration
    const accountVouchersElement = document.querySelector('account-vouchers');
    if (accountVouchersElement && accountVouchersElement.vendorActions) {
      // Use the unified approach - configure popup for single mode
      accountVouchersElement.vendorActions.configurePopupForSingleMode(popup, voucher);

      // Populate voucher information in the popup
      this.populateVoucherInfo(popup, voucher);

      // Open the popup using the global popup manager
      if (window.globalPopup && typeof window.globalPopup.openPopup === 'function') {
        window.globalPopup.openPopup(popup);
      } else {
        console.error('Global popup manager not available');
        ErrorUtils.error('customer.account.complete_booking', 'complete_booking_error', 'Unable to open popup. Please refresh the page and try again.');
      }
      return;
    }

    // Fallback: if AccountVouchers not available, use array format directly
    popup.dataset.mode = 'single';
    popup.dataset.selectedBookingIds = JSON.stringify([voucher.shopify_gift_card_id]);

    // Populate voucher information in the popup
    this.populateVoucherInfo(popup, voucher);

    // Open the popup
    if (window.globalPopup && typeof window.globalPopup.openPopup === 'function') {
      window.globalPopup.openPopup(popup);

    } else {
      console.error('Global popup manager not available');
      ErrorUtils.error('customer.account.complete_booking', 'complete_booking_error', 'Unable to open complete booking popup. Please refresh the page and try again.');
    }
  }

  // Populate voucher info for complete booking popup
  populateVoucherInfo(popup, voucher) {
    // Update voucher display ID (remove the GID prefix for display)
    const displayIdElement = popup.querySelector('[data-voucher-display-id]');
    if (displayIdElement) {
      // Extract the numeric ID from the GID
      const gidMatch = voucher.shopify_gift_card_id.match(/\/(\d+)$/);
      const displayId = gidMatch ? gidMatch[1] : voucher.shopify_gift_card_id;
      displayIdElement.textContent = displayId;
    }

    // Update voucher name in subtitle if available
    const voucherNameElement = popup.querySelector('.complete-booking-popup__voucher-name');
    if (voucherNameElement && voucher.experience_name) {
      voucherNameElement.textContent = `Confirm booking for: ${voucher.experience_name}`;
    }


  }

  // Handle initial complete booking action - open popup with voucher data
  async handleInitCompleteBookingAction(event) {


    const button = event.target.closest('button');
    const voucherId = button?.dataset.voucherId;

    if (!voucherId) {
      console.error('No voucher ID found for complete booking');
      ErrorUtils.error('customer.account.complete_booking', 'complete_booking_error', 'Voucher ID not found. Please try again.');
      return;
    }

    try {
      // Find the AccountVouchers instance to access voucher data and helper methods
      const accountVouchersElement = document.querySelector('account-vouchers');
      if (!accountVouchersElement) {
        console.error('AccountVouchers element not found');
        ErrorUtils.error('customer.account.complete_booking', 'complete_booking_error', 'Unable to access voucher data.');
        return;
      }

      // Find the voucher data
      const voucher = accountVouchersElement.findVoucherById(voucherId);
      if (!voucher) {
        console.error('Voucher not found in AccountVouchers data:', voucherId);
        ErrorUtils.error('customer.account.complete_booking', 'complete_booking_error', 'Voucher data not found.');
        return;
      }



      // Open complete booking popup
      this.openCompleteBookingPopup(voucher);

    } catch (error) {
      console.error('Error in handleInitCompleteBookingAction:', error);
      ErrorUtils.error('customer.account.complete_booking', 'complete_booking_error', 'An error occurred while opening complete booking popup.');
    }
  }

  // Handle initial pure cancel booking action - open unified cancel popup with voucher data
  async handleInitPureCancelBookingAction(event) {


    const button = event.target.closest('button');
    const voucherId = button?.dataset.voucherId;

    if (!voucherId) {
      console.error('No voucher ID found for pure cancel booking');
      ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', 'Voucher ID not found. Please try again.');
      return;
    }

    try {
      // Find the AccountVouchers instance to access voucher data and helper methods
      const accountVouchersElement = document.querySelector('account-vouchers');
      if (!accountVouchersElement) {
        console.error('AccountVouchers element not found');
        ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', 'Unable to access voucher data.');
        return;
      }

      // Find the voucher data
      const voucher = accountVouchersElement.findVoucherById(voucherId);
      if (!voucher) {
        console.error('Voucher not found in AccountVouchers data:', voucherId);
        ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', 'Voucher data not found.');
        return;
      }



      // Check if pure cancel is available (vendor-specific check)
      if (accountVouchersElement.vendorActions) {
        const cancelCheck = accountVouchersElement.vendorActions.checkPureCancelAvailability(voucher);
        if (!cancelCheck.canCancel) {
          const message = cancelCheck.reason || TranslationUtils.get(
            'vendor.bookings.notifications.cancellation_not_allowed',
            'Cancellation is not allowed for this booking.'
          );
          ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', message);
          return;
        }
      }

      // Open unified cancel booking popup
      this.openPureCancelBookingPopup(voucher);

    } catch (error) {
      console.error('Error in handleInitPureCancelBookingAction:', error);
      ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', 'An error occurred while opening cancel booking popup.');
    }
  }

  // Open pure cancel booking popup (unified cancel popup for vendor)
  openPureCancelBookingPopup(voucher) {


    const popup = document.querySelector('#cancel-booking-popup-unified');
    if (!popup) {
      console.error('Cancel booking popup (unified) not found');
      ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', 'Cancel booking popup not available. Please refresh the page and try again.');
      return;
    }

    // Find the AccountVouchers instance to use the vendor actions helper
    const accountVouchersElement = document.querySelector('account-vouchers');
    if (accountVouchersElement && accountVouchersElement.vendorActions) {
      // Use the vendor actions helper to configure popup for single mode
      accountVouchersElement.vendorActions.configureUnifiedCancelPopupForSingleMode(popup, voucher);

      // Open the popup using the global popup manager
      if (window.globalPopup && typeof window.globalPopup.openPopup === 'function') {
        window.globalPopup.openPopup(popup);

      } else {
        console.error('Global popup manager not available');
        ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', 'Unable to open popup. Please refresh the page and try again.');
      }
      return;
    }

    // Fallback: if AccountVouchers not available, configure directly
    popup.dataset.mode = 'single';
    popup.dataset.selectedBookingIds = JSON.stringify([voucher.shopify_gift_card_id]);

    // Store bookingToken if available
    if (voucher.bookingToken) {
      popup.dataset.bookingTokens = JSON.stringify([voucher.bookingToken]);
    }

    // Open the popup
    if (window.globalPopup && typeof window.globalPopup.openPopup === 'function') {
      window.globalPopup.openPopup(popup);

    } else {
      console.error('Global popup manager not available');
      ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', 'Unable to open cancel booking popup. Please refresh the page and try again.');
    }
  }

  async handleVoucherAction(event) {
    // Check if this is a cancel-all-bookings action first (unified popup handler)
    // This should be handled by handleCancelAllBookings, not this method
    const button = event.target.closest('button');
    if (button && (button.dataset.action === 'cancel-all-bookings' || button.closest('[data-action="cancel-all-bookings"]'))) {

      return; // Let the cancel-all-bookings handler process it
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation(); // Prevent other listeners on the same element





    const method = event.target.dataset.method;



    if (!method) {
      console.error('No method specified for voucher action');
      return;
    }



    switch (method) {
      case 'redeem':
        await this.processRedeemAction();
        break;
      case 'init-connect':
        await this.handleInitConnectAction(event);
        break;
      case 'init-redeem':
        await this.handleInitRedeemAction(event);
        break;
      case 'init-choice':
        await this.handleInitChoiceAction(event);
        break;
      case 'gift':
        await this.processGiftAction();
        break;
      case 'init-gift':
        this.handleInitGiftAction(event);
        break;
      case 'init-extend':

        this.handleInitExtendAction(event);
        break;
      case 'extend':

        await this.processExtendAction();
        break;
      case 'init-exchange-info':
        this.handleInitExchangeInfoAction(event);
        break;
      case 'init-exchange':
        // Now handled by ExchangeCollectionGrid for better separation

        break;
      case 'exchange':
        await this.processExchangeAction();
        break;
      case 'cancel-booking':
        // Only process if this is NOT from the unified popup (which uses cancel-all-bookings)
        const popup = document.querySelector('#cancel-booking-popup-unified');
        if (popup && popup.contains(event.target)) {

          return;
        }
        await this.processCancelBookingAction();
        break;
      case 'init-cancel-booking':
        await this.handleInitCancelBookingAction(event);
        break;
      case 'init-complete-booking':
        await this.handleInitCompleteBookingAction(event);
        break;
      case 'init-pure-cancel-booking':
        await this.handleInitPureCancelBookingAction(event);
        break;
      case 'create-purchase-invoice':
        await this.processCreatePurchaseInvoiceAction(event);
        break;
      default:
        console.error(`Unknown voucher action method: ${method}`);
    }
  }



  // Handle CONNECT flow - claiming/connecting a voucher to customer account (from redeem form with code input)
  async handleInitConnectAction(event) {

    const parentElement = event.target.closest('voucher-action-handler');

    // Get voucher code from input
    const voucherInput = parentElement ? parentElement.querySelector('[name="voucher_code"]') : null;
    const voucherCode = voucherInput ? voucherInput.value.trim() : null;
    const isGid = false; // Connect flow always uses code input




    if (!voucherCode) {
      ErrorUtils.error('customer.account.redeem', 'voucher_code_required', 'Voucher code is required');
      ErrorUtils.showInlineError(parentElement, 'customer.account.redeem', 'voucher_code_required', 'Voucher code is required', false);
      return;
    }

    // Backend validation
    try {
      const status = await voucherUtils.checkVoucherStatus(voucherCode);


      if (!status.found) {
        ErrorUtils.showInlineError(parentElement, 'voucher_not_found', 'Voucher not found', 'redeem', false);
        return;
      }

      // Check if gift card is expired
      if (status.is_expired === true) {
        ErrorUtils.showInlineError(parentElement, 'gift_card_expired', 'This gift card has expired', 'redeem', false);
        return;
      }

      // Check for old gift card without product information
      if (status.errorCode === 'GIFTCARD_EXISTS_BUT_NO_PRODUCT' || (status.isOldGiftCard && !status.canConnect)) {
        const message = TranslationUtils.get(
          'customer.account.redeem.gift_card_no_product_info_html',
          'This gift card exists but does not have product information and cannot be automatically converted. <a href="/pages/convert-gift-card" style="color: var(--primary_text); text-decoration: underline;">Please contact support for assistance</a>'
        );
        ErrorUtils.showInlineError(parentElement, 'gift_card_no_product_info', message, 'redeem', false);
        return;
      }

      // Check if we're in a grouping popup context and validate product match
      const voucherGroupingPopup = document.querySelector('[id$="voucher-grouping-popup"]');
      const isGroupingContext = voucherGroupingPopup && voucherGroupingPopup.getAttribute('aria-expanded') === 'true';

      if (isGroupingContext) {


        // Get grouping data from localStorage
        const groupingData = localStorage.getItem('voucherGroupingData');
        if (groupingData) {
          try {
            const parsedData = JSON.parse(groupingData);
            const primaryProductVariantId = parsedData.productVariantId;

            // Check if the new voucher is for a different product
            if (status.product_gid && primaryProductVariantId && status.product_gid !== primaryProductVariantId) {
              console.error('❌ Voucher is for a different product:', {
                voucherProduct: status.product_gid,
                primaryProduct: primaryProductVariantId
              });
              ErrorUtils.showInlineError(
                parentElement,
                'voucher_different_product_grouping',
                'This voucher is for a different product and cannot be added to this grouping',
                'redeem',
                false
              );
              return;
            }


          } catch (error) {
            console.error('Error parsing grouping data for product validation:', error);
          }
        }
      }

      // Check if already connected
      if (status.is_connected) {


        let isCurrentCustomer = false;
        if (typeof status.customer_email === 'boolean') {
          isCurrentCustomer = status.customer_email === true;
        } else if (typeof status.customer_email === 'string') {
          isCurrentCustomer = status.customer_email === voucherUtils.customerEmail;
        }

        if (!isCurrentCustomer) {
          ErrorUtils.showInlineError(
            parentElement,
            'voucher_already_connected_by_another',
            'This voucher has already been claimed by another customer.',
            'redeem',
            false
          );
          return;
        }

        // Connected by current customer
        if (isGroupingContext) {
          // In grouping context: directly redeem and add to group


          try {
            const redeemResult = await voucherUtils.redeemVoucher(voucherCode);

            if (!redeemResult || !redeemResult.success) {
              console.error('Failed to redeem already-connected voucher:', redeemResult);

              // Handle specific error cases with better messages
              if (redeemResult && redeemResult.code === 'VALIDATION_FAILED') {
                if (redeemResult.message && redeemResult.message.includes('already been redeemed')) {
                  ErrorUtils.showInlineError(parentElement, 'voucher_already_in_group', 'This voucher is already in the grouping', 'redeem', false);
                  return;
                }
              }

              // Generic error fallback
              const errorMessage = redeemResult?.message || 'Failed to redeem voucher';
              ErrorUtils.showInlineError(parentElement, 'redeem_error', errorMessage, 'redeem', false);
              return;
            }



            // Show success toast
            ErrorUtils.success('customer.account.vouchers.grouping', 'voucher_added_success', 'Voucher added to group!');

            // Get the grouping popup and add the card
            const voucherGroupingPopup = document.querySelector('[id$="voucher-grouping-popup"]');
            if (voucherGroupingPopup) {
              const accountVouchersElement = document.querySelector('account-vouchers');
              if (accountVouchersElement && accountVouchersElement.addNewVoucherToGroupingPopup) {
                accountVouchersElement.addNewVoucherToGroupingPopup(voucherCode, voucherGroupingPopup);
              }

              if (accountVouchersElement && accountVouchersElement.updateCombineButtonState) {
                accountVouchersElement.updateCombineButtonState();
              }
            }
            return;
          } catch (error) {
            console.error('Error redeeming already-connected voucher:', error);
            ErrorUtils.showInlineError(parentElement, 'redeem_error', 'Failed to redeem voucher', 'redeem', false);
            return;
          }
        }

        // Normal flow: show info message
        ErrorUtils.showInlineError(
          parentElement,
          'voucher_already_connected_by_you',
          'You have already claimed this voucher. Please go to your vouchers to use it.',
          'redeem',
          false
        );
        return;
      }

      // Check if already redeemed (shouldn't happen for non-connected, but check anyway)
      if (status.is_redeemed === true && !status.is_giftbox) {
        const isCurrentCustomer = status.customer_email === voucherUtils.customerEmail;
        voucherUtils.handleAlreadyRedeemedVoucher(isCurrentCustomer, parentElement);
        return;
      }

      // Voucher is not connected - proceed with connect/claim flow


      // Check if we're in grouping context - if yes, directly redeem without confirmation popup
      if (isGroupingContext) {


        try {
          // Redeem the voucher (this handles connection internally if needed)
          const redeemResult = await voucherUtils.redeemVoucher(voucherCode);

          if (!redeemResult || !redeemResult.success) {
            console.error('Failed to redeem voucher:', redeemResult);

            // Handle specific error cases with better messages
            if (redeemResult && redeemResult.code === 'VALIDATION_FAILED') {
              if (redeemResult.message && redeemResult.message.includes('already been redeemed')) {
                ErrorUtils.showInlineError(parentElement, 'voucher_already_in_group', 'This voucher is already in the grouping', 'redeem', false);
                return;
              }
            }

            // Generic error fallback
            const errorMessage = redeemResult?.message || 'Failed to redeem voucher';
            ErrorUtils.showInlineError(parentElement, 'redeem_error', errorMessage, 'redeem', false);
            return;
          }



          // Show success toast
          ErrorUtils.success('customer.account.vouchers.grouping', 'voucher_added_success', 'Voucher added to group!');

          // Get the grouping popup
          const voucherGroupingPopup = document.querySelector('[id$="voucher-grouping-popup"]');
          if (voucherGroupingPopup) {
            // Find the AccountVouchers instance to call addNewVoucherToGroupingPopup
            const accountVouchersElement = document.querySelector('account-vouchers');
            if (accountVouchersElement && accountVouchersElement.addNewVoucherToGroupingPopup) {
              accountVouchersElement.addNewVoucherToGroupingPopup(voucherCode, voucherGroupingPopup);
            }

            // Update the combine button state
            if (accountVouchersElement && accountVouchersElement.updateCombineButtonState) {
              accountVouchersElement.updateCombineButtonState();
            }
          }
        } catch (error) {
          console.error('Error in direct redeem flow:', error);
          ErrorUtils.showInlineError(parentElement, 'redeem_error', 'Failed to add voucher to grouping', 'redeem', false);
        }
      } else {
        // Normal flow: Open connect popup for confirmation

        status.needs_connection = true;
        PopupUtils.openConnectPopup(voucherCode, isGid, status);
      }
    } catch (err) {
      console.error('Error in connect flow:', err);
      ErrorUtils.error('customer.account.redeem', 'redeem_error', 'Failed to check voucher');
    }
  }

  // Handle REDEEM flow - using an already-connected voucher (from account page buttons with data-voucher-id)
  async handleInitRedeemAction(event) {


    // This flow is ONLY for GID-based redemption (vouchers already in customer's account)
    const voucherCode = event.target.dataset.voucherId;
    const isGid = true;



    if (!voucherCode) {
      ErrorUtils.error('customer.account.redeem', 'voucher_code_required', 'Voucher ID is required');
      return;
    }

    // Backend validation
    try {
      const status = await voucherUtils.checkVoucherStatus(voucherCode);


      if (!status.found) {
        ErrorUtils.error('customer.account.redeem', 'voucher_not_found', 'Voucher not found');
        return;
      }

      // Check if gift card is expired
      if (status.is_expired === true) {
        ErrorUtils.error('customer.account.redeem', 'gift_card_expired', 'This gift card has expired');
        return;
      }

      // Note: We don't check is_connected here because this flow is only accessible 
      // for vouchers already in the customer's account. The voucher MUST be connected 
      // to appear in the account page, so we only verify ownership.

      // Verify voucher belongs to current customer
      let isCurrentCustomer = false;
      if (typeof status.customer_email === 'boolean') {
        isCurrentCustomer = status.customer_email === true;
      } else if (typeof status.customer_email === 'string') {
        isCurrentCustomer = status.customer_email === voucherUtils.customerEmail;
      }

      if (!isCurrentCustomer) {
        ErrorUtils.error('customer.account.redeem', 'voucher_not_yours', 'This voucher does not belong to you');
        return;
      }

      // Check if already redeemed
      if (status.is_redeemed === true) {
        // For giftbox vouchers, is_redeemed: true is expected - they should proceed to choice phase
        if (status.is_giftbox) {

          PopupUtils.openChoosePopup(voucherCode, isGid, status);
          return;
        }

        // For regular vouchers, is_redeemed: true means they can't be redeemed again
        voucherUtils.handleAlreadyRedeemedVoucher(true, null);
        return;
      }

      // Voucher is ready for redemption

      status.needs_connection = false; // Already connected (that's how it got in the account)

      // Open appropriate popup based on voucher type
      if (status.is_giftbox) {

        PopupUtils.openGiftboxChoosePopup(voucherCode, isGid, status);
      } else {

        PopupUtils.openRedeemPopup(voucherCode, isGid, status);
      }
    } catch (err) {
      console.error('Error in redeem flow:', err);
      ErrorUtils.error('customer.account.redeem', 'redeem_error', 'Failed to check voucher');
    }
  }

  async handleInitChoiceAction(event) {
    // Find the currently open popup
    const popups = document.querySelectorAll('[id$="redeem-confirm-popup"] .Popup, [id$="redeem-confirm-popup"].Popup, [id$="connect-confirm-popup"] .Popup, [id$="connect-confirm-popup"].Popup, [id$="choose-confirm-popup"] .Popup, [id$="choose-confirm-popup"].Popup, [id$="giftbox-choose-popup"] .Popup, [id$="giftbox-choose-popup"].Popup');

    let popup = null;
    for (const p of popups) {
      if (p.getAttribute('aria-expanded') === 'true') {
        popup = p;
        break;
      }
    }

    if (!popup) {
      console.error('No open popup found for choice action');
      ErrorUtils.error('customer.account.choice', 'choice_error', 'Failed to start choice process');
      return;
    }



    if (!popup.dataset.voucherStatus) {
      console.error('No voucher status found in popup data:', popup.dataset);
      ErrorUtils.error('customer.account.choice', 'choice_error', 'Failed to start choice process');
      return;
    }

    try {
      const status = JSON.parse(popup.dataset.voucherStatus);
      const voucherCode = popup.dataset.voucherIdentifier;
      const isGid = popup.dataset.redeemMethod === 'GID';

      if (!voucherCode) {
        ErrorUtils.error('customer.account.choice', 'voucher_code_required', 'Voucher code is required');
        return;
      }


      voucherUtils.handleGiftboxChoice(voucherCode, status, isGid);

    } catch (error) {
      console.error('Error parsing voucher status:', error);
      ErrorUtils.error('customer.account.choice', 'choice_error', 'Failed to start choice process');
    }
  }

  handleInitGiftAction(event) {
    // For card-based actions, get voucher identifier directly from button's data-voucher-id
    const voucherIdentifier = event.target.dataset.voucherId;
    if (!voucherIdentifier) {
      console.error('No voucher identifier found on button');
      return;
    }

    // For GID-based actions, skip client-side input validation and directly open gift popup
    PopupUtils.openGiftPopup(voucherIdentifier);
  }

  handleInitExtendAction(event) {
    // Prevent event bubbling that might cause popup to close
    event.preventDefault();
    event.stopPropagation();

    // For card-based actions, get voucher identifier directly from button's data-voucher-id
    const voucherIdentifier = event.target.dataset.voucherId;
    if (!voucherIdentifier) {
      console.error('No voucher identifier found on extend button');
      return;
    }



    // Find the voucher data from the account vouchers component
    const accountVouchersElement = document.querySelector('account-vouchers');
    if (accountVouchersElement && accountVouchersElement.state) {
      // Search top-level vouchers first
      let voucherData = accountVouchersElement.state.giftCards.find(card => card.shopify_gift_card_id === voucherIdentifier);

      // If not found at top level, search inside groups (vouchers in groups are nested)
      if (!voucherData) {
        for (const item of accountVouchersElement.state.giftCards) {
          if (item.type === 'giftcard_group' && item.vouchers) {
            const found = item.vouchers.find(v => v.shopify_gift_card_id === voucherIdentifier);
            if (found) {
              voucherData = found;
              break;
            }
          }
        }
      }

      if (voucherData) {
        PopupUtils.openExtendPopup(voucherIdentifier, voucherData);
      } else {
        console.error('Voucher data not found for identifier:', voucherIdentifier);
        // Fallback: open popup with just the identifier
        PopupUtils.openExtendPopup(voucherIdentifier, { shopify_gift_card_id: voucherIdentifier });
      }
    } else {
      console.error('Account vouchers component not found or no state available');
      // Fallback: open popup with just the identifier
      PopupUtils.openExtendPopup(voucherIdentifier, { shopify_gift_card_id: voucherIdentifier });
    }
  }


  async processRedeemAction() {
    // Get the popup element to read the voucher identifier from data attributes
    // Look for .Popup elements that are currently open (aria-expanded="true")
    const popups = document.querySelectorAll('[id$="redeem-confirm-popup"] .Popup, [id$="redeem-confirm-popup"].Popup, [id$="connect-confirm-popup"] .Popup, [id$="connect-confirm-popup"].Popup, [id$="choose-confirm-popup"] .Popup, [id$="choose-confirm-popup"].Popup, [id$="giftbox-choose-popup"] .Popup, [id$="giftbox-choose-popup"].Popup');

    let popup = null;
    for (const p of popups) {
      if (p.getAttribute('aria-expanded') === 'true') {
        popup = p;
        break;
      }
    }

    if (!popup) {
      console.error('No open popup found');
      return;
    }



    // Read voucher identifier from popup data attribute
    const voucherIdentifier = popup.dataset.voucherIdentifier;
    if (!voucherIdentifier) {
      console.error('No voucher identifier found in popup data attributes:', popup.dataset);
      return;
    }

    // Check if voucher needs connection or is ready for redemption
    const needsConnection = popup.dataset.needsConnection === 'true';


    try {
      // Parse voucher status if available
      let voucherStatus = null;
      if (popup.dataset.voucherStatus) {
        try {
          voucherStatus = JSON.parse(popup.dataset.voucherStatus);
        } catch (e) {
          console.error('Error parsing voucher status:', e);
        }
      }

      // Route to appropriate action based on needs_connection flag
      if (needsConnection) {
        // CONNECT FLOW: Claim the voucher to customer's account


        // Double-check voucher is not already connected
        if (voucherStatus && voucherStatus.is_connected) {
          console.error('Voucher is already connected - this should not happen in connect flow');
          ErrorUtils.error('customer.account.redeem', 'voucher_already_connected', 'This voucher is already claimed');
          return;
        }

        // Call CONNECT action to claim voucher
        await this.processConnectVoucher(popup, voucherIdentifier);
      } else {
        // REDEEM FLOW: Actually use/redeem the voucher

        await this.processRedeemVoucher(popup, voucherIdentifier, voucherStatus);
      }
    } catch (error) {
      console.error('Error during voucher connection:', error);
      ErrorUtils.error('customer.account.redeem', 'connect_error', 'Failed to claim gift card');
    }
  }

  async processConnectVoucher(popup, voucherIdentifier) {


    try {
      const connectResult = await voucherUtils.connectVoucher(voucherIdentifier);

      if (!connectResult || !connectResult.success) {
        console.error('Failed to connect voucher:', connectResult);
        ErrorUtils.error('customer.account.redeem', 'connect_error', 'Failed to claim gift card');
        return;
      }



      // Check if we're in a voucher grouping context
      const isVoucherGroupingContext = this.isVoucherGroupingPopupOpen();

      if (isVoucherGroupingContext) {


        // Close the connect popup
        if (window.globalPopup && typeof window.globalPopup.closePopup === 'function') {
          window.globalPopup.closePopup(popup);
        }

        // Automatically redeem the voucher after connecting (for grouping flow)
        try {
          const redeemResult = await voucherUtils.redeemVoucher(voucherIdentifier);

          if (!redeemResult || !redeemResult.success) {
            console.error('Failed to redeem voucher after connect:', redeemResult);
            ErrorUtils.error('customer.account.redeem', 'redeem_after_connect_error', 'Failed to redeem voucher after connecting');
            return;
          }



          // Show success toast
          ErrorUtils.success('customer.account.vouchers.grouping', 'voucher_added_success', 'Voucher added to group!');

          // Dispatch update event to add the voucher to the grouping popup
          this.dispatchVoucherGroupingUpdate(voucherIdentifier);
        } catch (error) {
          console.error('Error redeeming voucher after connect:', error);
          ErrorUtils.error('customer.account.redeem', 'redeem_after_connect_error', 'Failed to redeem voucher after connecting');
        }
      } else {


        // Show success toast
        ErrorUtils.success('customer.account.redeem', 'connect_success', 'Gift card claimed successfully!');

        // Close the popup after successful connection
        if (window.globalPopup && typeof window.globalPopup.closePopup === 'function') {
          window.globalPopup.closePopup(popup);
        }

        this.notifyCacheInvalidation('connect');

        // Navigate to non-redeemed filter to show the newly connected voucher
        setTimeout(() => {

          const url = new URL(window.location);
          url.searchParams.set('vouchers', 'non-redeemed');
          window.location.href = url.toString();
        }, 1000);
      }
    } catch (error) {
      console.error('Error connecting voucher:', error);
      ErrorUtils.error('customer.account.redeem', 'connect_error', 'Failed to claim gift card');
    }
  }

  async processRedeemVoucher(popup, voucherIdentifier, voucherStatus) {


    try {
      // Call the actual redemption API
      const redeemResult = await voucherUtils.redeemVoucher(voucherIdentifier);

      if (!redeemResult || !redeemResult.success) {
        console.error('Failed to redeem voucher:', redeemResult);
        ErrorUtils.error('customer.account.redeem', 'redeem_error', 'Failed to redeem voucher');
        return;
      }



      // Close the popup after successful redemption
      if (window.globalPopup && typeof window.globalPopup.closePopup === 'function') {
        window.globalPopup.closePopup(popup);
      }

      // Check if this is a giftbox voucher - if so, start the choice process
      if (voucherStatus && voucherStatus.is_giftbox) {


        // Success toast specific to giftbox
        ErrorUtils.success('customer.account.redeem', 'giftbox_redeem_success', 'Gift box redeemed! Now choose your product.');

        // Start the giftbox choice process
        const updatedStatus = {
          ...voucherStatus,
          is_redeemed: true // Update status to reflect redemption
        };
        voucherUtils.handleGiftboxChoice(voucherIdentifier, updatedStatus, popup.dataset.redeemMethod === 'GID');

        return; // Don't reload page, the choice handler will redirect
      }

      // For regular vouchers - show toast and refresh list
      ErrorUtils.success('customer.account.redeem', 'redeem_success', 'Voucher redeemed successfully!');

      // Dispatch single event to refresh list and check if we need to switch tabs
      setTimeout(() => {

        const event = new CustomEvent('voucher-action-completed', {
          detail: { action: 'redeem', checkEmptyAndSwitch: true }
        });
        document.dispatchEvent(event);
      }, 500);
    } catch (error) {
      console.error('Error during voucher redemption:', error);
      ErrorUtils.error('customer.account.redeem', 'redeem_error', 'Failed to redeem voucher');
    }
  }

  // Legacy redeem method kept for other flows that need actual redemption
  async processRedeemActionLegacy() {
    const popup = document.querySelector('[id$="redeem-confirm-popup"], [id$="connect-confirm-popup"], [id$="choose-confirm-popup"]');
    if (!popup) {
      console.error('Redeem confirm popup not found');
      return;
    }

    const voucherIdentifier = popup.dataset.voucherIdentifier;
    if (!voucherIdentifier) {
      console.error('No voucher identifier found in popup data attributes');
      return;
    }

    try {
      const payload = voucherUtils.createApiPayload(voucherIdentifier, 'REDEEM');
      const result = await window.apiService.post(this.apiEndpointRedeem, payload);

      // Check if redemption was successful
      if (result && result.success) {


        // Close the popup
        if (window.globalPopup && typeof window.globalPopup.closePopup === 'function') {
          window.globalPopup.closePopup(popup);
        }

        // Check if we're in a voucher grouping context using proper Popup class methods
        const isVoucherGroupingContext = this.isVoucherGroupingPopupOpen();



        if (isVoucherGroupingContext) {

          // Dispatch custom event for voucher grouping update
          this.dispatchVoucherGroupingUpdate(voucherIdentifier);
        } else {

          // Normal redeem flow - notify cache invalidation for page refresh
          this.notifyCacheInvalidation();
        }
      } else {
        console.error('Voucher redemption failed:', result);
        ErrorUtils.error('customer.account.redeem', 'redeem_error', 'Failed to redeem voucher');
      }
    } catch (error) {
      console.error('Error during voucher redemption:', error);
      ErrorUtils.error('customer.account.redeem', 'redeem_error', 'Failed to redeem voucher');
    }
  }

  isVoucherGroupingPopupOpen() {
    // Use the generic method for voucher grouping popup
    const voucherGroupingPopup = document.querySelector('[id$="voucher-grouping-popup"]');
    return this.isPopupOpen(voucherGroupingPopup);
  }

  isPopupOpen(popup) {
    // Generic method to check if any popup is open using Popup class standards
    return popup &&
      popup.getAttribute('aria-expanded') === 'true' &&
      window.globalPopup &&
      window.globalPopup.activePopups &&
      window.globalPopup.activePopups.includes(popup);
  }

  dispatchVoucherGroupingUpdate(redeemedVoucherId) {


    const event = new CustomEvent('voucher-grouping-update', {
      detail: { redeemedVoucherId }
    });

    document.dispatchEvent(event);
  }

  async processGiftAction() {
    // Get the gift popup element
    const giftPopup = document.querySelector('#account-gift-popup');
    if (!giftPopup) {
      console.error('Gift popup not found');
      return;
    }

    // Get the voucher identifier from the gift popup
    const voucherIdentifier = giftPopup.dataset.voucherIdentifier;
    if (!voucherIdentifier) {
      console.error('No voucher identifier found in gift popup data attributes');
      return;
    }

    try {
      // Get form data from the gift popup
      const formData = this.getGiftFormData(giftPopup);
      if (!formData) {
        console.error('Invalid gift form data');
        return;
      }

      // Call the gift API
      const result = await voucherUtils.giftVoucher(voucherIdentifier, formData);

      if (result && result.success) {


        // Reset the form before closing popup
        VoucherActionHandler.resetGiftPopupForm(giftPopup);

        // Close the popup
        if (window.globalPopup && typeof window.globalPopup.closePopup === 'function') {
          window.globalPopup.closePopup(giftPopup);
        }

        // Notify parent components to invalidate cache
        this.notifyCacheInvalidation('gift');

        // Show success message - use message from API response if available
        const successMessage = result.message || 'Voucher gifted successfully';
        if (toastNotifications) {
          toastNotifications.success(successMessage);
        }
      } else {
        console.error('Voucher gifting failed:', result);
        const errorMessage = result.message || 'Failed to gift voucher';
        if (toastNotifications) {
          toastNotifications.error(errorMessage);
        }
      }
    } catch (error) {
      console.error('Error during voucher gifting:', error);
      if (toastNotifications) {
        toastNotifications.error('Failed to gift voucher');
      }
    }
  }

  async processExtendAction() {


    // Get the extend popup element
    const extendPopup = document.querySelector('#extend-voucher-popup');
    if (!extendPopup) {
      console.error('Extend voucher popup not found');
      ErrorUtils.error('customer.account.extend', 'extend_error', 'Extend popup not found. Please try again.');
      return;
    }



    // Get the voucher data from the extend popup
    const voucherIdentifier = extendPopup.dataset.voucherIdentifier;
    const voucherGid = extendPopup.dataset.voucherIdentifier;



    if (!voucherIdentifier || !voucherGid) {
      console.error('Missing voucher data in extend popup');
      ErrorUtils.error('customer.account.extend', 'extend_error', 'Missing voucher data. Please try again.');
      return;
    }

    // Get extension product data from window.account
    const extensionProduct = window.account?.extensionProduct;
    if (!extensionProduct) {
      console.error('Extension product not configured in theme settings');
      ErrorUtils.error('customer.account.extend', 'extend_config_error', 'Extension functionality is not available. Please contact support.');
      return;
    }

    const extensionVariantId = extensionProduct.variantId;

    if (!extensionVariantId) {
      console.error('Extension variant ID not found:', {
        variantId: extensionVariantId,
        extensionProduct: extensionProduct
      });
      ErrorUtils.error('customer.account.extend', 'extend_config_error', 'Extension product configuration is invalid. Please contact support.');
      return;
    }

    try {


      // Mark extend as initiated now that user is proceeding to checkout
      try {
        const extendData = localStorage.getItem('extendGiftCard');
        if (extendData) {
          const data = JSON.parse(extendData);
          data.extendInitiated = true;
          localStorage.setItem('extendGiftCard', JSON.stringify(data));

        }
      } catch (error) {
        console.warn('Failed to update extend initiation status:', error);
      }

      // Check if cart was prepared in background and verify it's still valid
      const cartPrepared = extendPopup.dataset.cartPrepared === 'true';
      const qliroReady = extendPopup.dataset.qliroReady === 'true';


      if (cartPrepared && qliroReady) {

        await this.openQliroCheckoutForExtend(extendPopup);
      } else if (cartPrepared && !qliroReady) {

        await this.openQliroCheckoutForExtend(extendPopup);
      } else {


        // Fallback: use consolidated cart preparation method to ensure consistency
        await PopupUtils.prepareVoucherCart('extension', { voucherIdentifier: voucherGid });

        // Mark as prepared for consistency
        extendPopup.dataset.cartPrepared = 'true';

        // Use fallback Qliro initialization
        await this.openQliroCheckoutForExtend(extendPopup);
      }



    } catch (error) {
      console.error('Error during extend voucher checkout:', error);
      ErrorUtils.error('customer.account.extend', 'extend_error', 'Failed to process extend voucher checkout: ' + error.message);
    }
  }

  replacePopupContentWithQliro(extendPopup) {


    // Save the original popup state before modifying
    VoucherActionHandler.savePopupState(extendPopup);

    // Update popup title
    const popupTitle = extendPopup.querySelector('.extend-voucher-popup__title');
    if (popupTitle) {
      popupTitle.textContent = window.cartStrings?.checkout || 'Checkout';
    }

    // Find the popup content container
    const popupBody = extendPopup.querySelector('.extend-voucher-popup__body');
    if (!popupBody) {
      console.error('Popup body not found');
      return;
    }

    // Clean up any existing Qliro managers or observers before clearing
    if (extendPopup.extendQliroManager) {
      try {
        // Try to clean up existing Qliro manager if it exists
        extendPopup.extendQliroManager = null;
      } catch (error) {
        console.warn('Error cleaning up existing Qliro manager:', error);
      }
    }

    // Properly clear all existing content by removing child elements
    // This ensures any "Fetching Qliro details..." messages are completely removed
    while (popupBody.firstChild) {
      popupBody.removeChild(popupBody.firstChild);
    }

    // Add new Qliro checkout container with fresh content
    const qliroCheckoutDiv = document.createElement('div');
    qliroCheckoutDiv.id = 'qliro-checkout-container';
    qliroCheckoutDiv.className = 'cart-qliro';
    qliroCheckoutDiv.innerHTML = '<p class="cart-qliro__loading">Loading Qliro Checkout...</p>';
    popupBody.appendChild(qliroCheckoutDiv);

    // Hide the popup footer with buttons
    const popupFooter = extendPopup.querySelector('.popup-footer');
    if (popupFooter) {
      popupFooter.style.display = 'none';
    }

    // Initialize Qliro checkout in the new container
    this.initializeQliroCheckout(qliroCheckoutDiv);
  }

  async initializeQliroCheckout(container) {
    try {
      // Use the existing CartQliroManager from cart.js
      if (typeof CartQliroManager === 'undefined') {
        console.error('CartQliroManager not available - cart.js may not be loaded yet');

        // Clear loading message on error
        const existingLoadingMessages = container.querySelectorAll('.cart-qliro__loading');
        existingLoadingMessages.forEach(message => message.remove());

        // Use toast notification for CartQliroManager unavailable
        if (toastNotifications) {
          toastNotifications.error('Checkout not available. Please refresh the page.');
        }
        return;
      }



      // Set the container ID that CartQliroManager expects
      container.id = 'qliro-checkout-container';

      // Create CartQliroManager instance with the container's parent (popup body)
      const popupBody = container.parentElement;
      const qliroManager = new CartQliroManager(popupBody);

      await qliroManager.initialize();

      // Monitor for Qliro widget completion and remove loading messages
      const checkQliroLoaded = () => {
        const qliroWidget = container.querySelector('#qliro-root');
        const loadingMessages = container.querySelectorAll('.cart-qliro__loading');

        if (qliroWidget && loadingMessages.length > 0) {
          // Qliro widget is loaded, remove all loading messages
          loadingMessages.forEach(message => {

            message.remove();
          });
        } else if (loadingMessages.length > 0) {
          // Check for stale "Fetching Qliro details" messages and remove them after timeout
          loadingMessages.forEach(message => {
            if (message.textContent.includes('Fetching Qliro details')) {
              setTimeout(() => {
                if (message.parentNode && message.textContent.includes('Fetching Qliro details')) {

                  message.style.display = 'none';
                }
              }, 10000);
            }
          });
        }
      };

      // Check immediately and set up observer for changes
      checkQliroLoaded();

      // Use MutationObserver to watch for Qliro widget insertion
      const observer = new MutationObserver(checkQliroLoaded);
      observer.observe(container, { childList: true, subtree: true });

      // Clean up observer after 30 seconds
      setTimeout(() => observer.disconnect(), 30000);

    } catch (error) {
      console.error('Error initializing Qliro checkout:', error);

      // Clear loading message on error
      const existingLoadingMessages = container.querySelectorAll('.cart-qliro__loading');
      existingLoadingMessages.forEach(message => message.remove());

      // Use toast notification for Qliro initialization errors
      if (toastNotifications) {
        toastNotifications.error(`Error: ${error.message}`);
      }
    }
  }

  // Consolidated static popup state management - save original popup state before modification
  static savePopupState(popup) {
    const popupId = popup.id || 'unknown-popup';


    // Don't save if already saved
    if (VoucherActionHandler.originalPopupStates.has(popupId)) {

      return;
    }

    const originalState = {
      title: null,
      bodyContent: null,
      footerDisplay: null,
      datasetAttributes: {}
    };

    // Save title state (works for both extend and exchange popups)
    const titleElement = popup.querySelector('.extend-voucher-popup__title, .exchange-voucher-popup__title');
    if (titleElement) {
      originalState.title = {
        element: titleElement,
        content: titleElement.innerHTML,
        textContent: titleElement.textContent
      };
    }

    // Save body state (works for both popup types)
    const bodyElement = popup.querySelector('.extend-voucher-popup__body, .exchange-voucher-popup__body');
    if (bodyElement) {
      originalState.bodyContent = {
        element: bodyElement,
        content: bodyElement.innerHTML
      };
    }

    // Save footer state
    const footerElement = popup.querySelector('.popup-footer');
    if (footerElement) {
      originalState.footerDisplay = {
        element: footerElement,
        display: footerElement.style.display || getComputedStyle(footerElement).display,
        content: footerElement.innerHTML
      };
    }

    // Save dataset attributes
    Object.keys(popup.dataset).forEach(key => {
      originalState.datasetAttributes[key] = popup.dataset[key];
    });

    VoucherActionHandler.originalPopupStates.set(popupId, originalState);

  }

  // Restore popup to its original state
  static restorePopupState(popup) {
    const popupId = popup.id || 'unknown-popup';
    const originalState = VoucherActionHandler.originalPopupStates.get(popupId);

    if (!originalState) {
      console.warn('No original state found for popup:', popupId);
      return false;
    }



    try {
      // Clean up managers before restoring
      if (popup.exchangeCartDiscountManager) {
        popup.exchangeCartDiscountManager.clearDiscountForm();
        popup.exchangeCartDiscountManager = null;
      }
      if (popup.exchangeQliroManager) {
        popup.exchangeQliroManager = null;
      }

      // Mark popup as inactive
      popup.dataset.popupActive = 'false';

      // Restore title
      if (originalState.title && originalState.title.element) {
        originalState.title.element.innerHTML = originalState.title.content;
      }

      // Restore body content
      if (originalState.bodyContent && originalState.bodyContent.element) {
        originalState.bodyContent.element.innerHTML = originalState.bodyContent.content;
      }

      // Restore footer
      if (originalState.footerDisplay && originalState.footerDisplay.element) {
        originalState.footerDisplay.element.style.display = originalState.footerDisplay.display;
        originalState.footerDisplay.element.innerHTML = originalState.footerDisplay.content;
      }

      // Clear current dataset attributes and restore original ones
      Object.keys(popup.dataset).forEach(key => {
        delete popup.dataset[key];
      });
      Object.keys(originalState.datasetAttributes).forEach(key => {
        popup.dataset[key] = originalState.datasetAttributes[key];
      });



      // Clean up the stored state
      VoucherActionHandler.originalPopupStates.delete(popupId);

      return true;
    } catch (error) {
      console.error('Error restoring popup state:', error);
      return false;
    }
  }

  getGiftFormData(popup) {
    try {
      const form = popup.querySelector('form');
      if (!form) {
        console.error('No form found in gift popup');
        return null;
      }

      const formData = new FormData(form);
      const rawData = {};

      // Extract form data
      for (const [key, value] of formData.entries()) {
        if (value && value.trim()) {
          rawData[key] = value.trim();
        }
      }



      // Validate required fields
      const requiredFields = ['recipient_email', 'recipient_first_name', 'recipient_last_name'];
      for (const field of requiredFields) {
        if (!rawData[field]) {
          console.error(`Missing required field: ${field}`);
          return null;
        }
      }

      // Map form fields to API expected format and combine names
      const recipientData = {
        email: rawData.recipient_email,
        firstName: rawData.recipient_first_name,
        lastName: rawData.recipient_last_name
      };

      // Add optional fields if they exist in the form
      if (rawData.greeting_message) {
        recipientData.message = rawData.greeting_message;

      } else {

      }

      if (rawData.delivery_datetime) {
        recipientData.deliveryDateTime = rawData.delivery_datetime;

      } else {

      }



      return recipientData;
    } catch (error) {
      console.error('Error getting gift form data:', error);
      return null;
    }
  }

  static resetGiftPopupForm(popup) {
    try {
      const form = popup.querySelector('form');
      if (!form) {
        console.error('No form found in gift popup for reset');
        return;
      }

      // Reset all form inputs
      form.reset();

      // Clear specific input values (in case reset() doesn't work for all field types)
      const inputs = form.querySelectorAll('input, textarea, select');
      inputs.forEach(input => {
        if (input.type === 'checkbox' || input.type === 'radio') {
          input.checked = false;
        } else {
          input.value = '';
        }
      });

      // Clear any error states or validation messages
      const validationElements = form.querySelectorAll('.input-validation');
      validationElements.forEach(element => {
        element.textContent = '';
        element.style.display = 'none';
      });

      // Remove error classes from fields
      const fields = form.querySelectorAll('.field');
      fields.forEach(field => {
        field.classList.remove('field--error');
      });

      // Reset character counter for greeting message if present
      const charCountElement = form.querySelector('.delivery__char-count');
      if (charCountElement) {
        const maxChars = charCountElement.dataset.limit || '65';
        charCountElement.textContent = `(${maxChars} chars left)`;
      }


    } catch (error) {
      console.error('Error resetting gift form:', error);
    }
  }

  notifyCacheInvalidation(action = 'redeem') {
    // Dispatch custom event to notify other components
    const event = new CustomEvent('voucher-action-completed', {
      detail: { action: action }
    });

    // Dispatch only on document level - single, reliable approach
    document.dispatchEvent(event);
  }


  async processExchangeAction() {


    // Get the exchange popup element
    const exchangePopup = document.querySelector('#exchange-voucher-popup');
    if (!exchangePopup) {
      console.error('Exchange voucher popup not found');
      ErrorUtils.error('customer.account.exchange', 'exchange_error', 'Exchange popup not found. Please try again.');
      return;
    }



    // Find the exchange button
    const exchangeButton = exchangePopup.querySelector('[data-action="voucher-action"][data-method="exchange"]');

    // Prevent multiple clicks - check if already processing
    if (exchangeButton && exchangeButton.disabled) {

      return;
    }

    // Show loading overlay
    const loadingOverlay = exchangePopup.querySelector('#exchange-loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.classList.add('is-visible');

    }

    // Get the exchange data from the popup and localStorage
    const productId = exchangePopup.dataset.productId;
    const productTitle = exchangePopup.dataset.productTitle;
    const productPrice = exchangePopup.dataset.productPrice;
    const voucherValue = parseFloat(exchangePopup.dataset.voucherValue);
    const priceDifference = parseFloat(exchangePopup.dataset.priceDifference);



    if (!productId || !productPrice) {
      console.error('Missing exchange data in popup');
      ErrorUtils.error('customer.account.exchange', 'exchange_error', 'Missing product data for exchange. Please try again.');
      return;
    }

    // Get exchange data from localStorage
    const exchangeData = JSON.parse(localStorage.getItem('exchangeGiftCard') || '{}');

    if (!exchangeData.voucherId) {
      console.error('No exchange voucher data found in localStorage');
      ErrorUtils.error('customer.account.exchange', 'exchange_error', 'Exchange session expired. Please start the exchange process again.');
      return;
    }

    // Disable button and add loading state
    let originalButtonText = '';
    if (exchangeButton) {
      originalButtonText = exchangeButton.textContent;
      exchangeButton.disabled = true;
      exchangeButton.classList.add('button--loading');
      exchangeButton.setAttribute('aria-busy', 'true');

    }

    try {


      // Check if cart was prepared in background and verify it's still valid
      const cartPrepared = exchangePopup.dataset.cartPrepared === 'true';
      const qliroReady = exchangePopup.dataset.qliroReady === 'true';


      if (priceDifference > 0) {
        // Product costs more than voucher - proceed with checkout
        if (cartPrepared && qliroReady) {

          await this.openQliroCheckoutForExchange(exchangePopup);
        } else {

          // Wait a bit for background preparation to complete, or prepare if needed
          const productVariantId = exchangePopup.dataset.productVariantId;
          const productVariantGid = productVariantId ? `gid://shopify/ProductVariant/${productVariantId}` : null;
          await this.waitForCartOrPrepare(exchangePopup, exchangeData, productId, productTitle, productPrice, voucherValue, priceDifference, productVariantGid);

          // After preparation, check if Qliro was pre-initialized, otherwise use fallback
          const finalQliroReady = exchangePopup.dataset.qliroReady === 'true';
          if (finalQliroReady) {

            await this.openQliroCheckoutForExchange(exchangePopup);
          } else {

            await this.openQliroCheckoutForExchange(exchangePopup);
          }
        }
      } else {
        // Product costs same or less - direct exchange via API (no cart operations needed)
        const productVariantId = exchangePopup.dataset.productVariantId;
        await this.handleDirectExchange(exchangePopup, exchangeData, productVariantId);
      }

    } catch (error) {
      console.error('Error during exchange process:', error);
      ErrorUtils.error('customer.account.exchange', 'exchange_error', 'Failed to process exchange: ' + error.message);

      // Hide loading overlay on error
      const loadingOverlay = exchangePopup.querySelector('#exchange-loading-overlay');
      if (loadingOverlay) {
        loadingOverlay.classList.remove('is-visible');

      }

      // Re-enable button on error
      if (exchangeButton) {
        exchangeButton.disabled = false;
        exchangeButton.classList.remove('button--loading');
        exchangeButton.removeAttribute('aria-busy');
        if (originalButtonText) {
          exchangeButton.textContent = originalButtonText;
        }

      }
    }
  }


  async handleDirectExchange(exchangePopup, exchangeData, productVariantId) {


    // Use the actual product variant GID from the exchange popup
    const productVariantGid = productVariantId ? `gid://shopify/ProductVariant/${productVariantId}` : null;

    // Validate that we have a product variant GID for the exchange
    if (!productVariantGid) {
      throw new Error('Product variant GID is required for direct exchange');
    }

    const payload = {
      giftCardGid: exchangeData.voucherId,
      newProductId: productVariantGid,
      customerEmail: window.account?.customerEmail,
    };




    try {
      const result = await window.apiService.post(`${window.proxyPath}/exchange`, payload);

      if (result && result.success) {


        // Close the popup
        if (window.globalPopup && typeof window.globalPopup.closePopup === 'function') {
          window.globalPopup.closePopup(exchangePopup);
        }

        // Clear exchange session data
        localStorage.removeItem('exchangeGiftCard');

        // Notify cache invalidation for gift cards
        this.notifyCacheInvalidation('exchange');

        // Set flag to show success message after redirect
        try {
          sessionStorage.setItem('exchange-success', 'true');
        } catch (e) {
          console.warn('Failed to set exchange success flag:', e);
        }

        // Redirect to account page with redeemed vouchers tab
        window.location.href = '/account?vouchers=redeemed';

      } else {
        console.error('Direct exchange failed:', result);
        const errorMessage = result?.message || 'Exchange failed. Please try again.';
        ErrorUtils.error('customer.account.exchange', 'exchange_error', errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error during direct exchange API call:', error);

      // Only show toast if we haven't already shown one in the else block above
      if (!error.message || !error.message.includes('Exchange failed')) {
        const errorMessage = error.message || 'Failed to process exchange. Please try again.';
        ErrorUtils.error('customer.account.exchange', 'exchange_error', errorMessage);
      }

      throw error;
    }
  }

  replaceExchangePopupContentWithQliro(exchangePopup) {

    console.warn('Using fallback Qliro initialization - pre-prepared Qliro was not available');

    // Save the original popup state before modifying
    VoucherActionHandler.savePopupState(exchangePopup);

    // Update popup title
    const popupTitle = exchangePopup.querySelector('.exchange-voucher-popup__title');
    if (popupTitle) {
      popupTitle.textContent = window.cartStrings?.checkout || 'Complete Exchange';
    }

    // Find the popup content container
    const popupBody = exchangePopup.querySelector('.exchange-voucher-popup__body');
    if (!popupBody) {
      console.error('Exchange popup body not found');
      return;
    }

    // Clean up any existing Qliro managers or observers before clearing
    if (exchangePopup.exchangeQliroManager) {
      try {
        // Try to clean up existing Qliro manager if it exists
        exchangePopup.exchangeQliroManager = null;
      } catch (error) {
        console.warn('Error cleaning up existing Qliro manager:', error);
      }
    }

    // Properly clear all existing content by removing child elements
    // This ensures any "Fetching Qliro details..." messages are completely removed
    while (popupBody.firstChild) {
      popupBody.removeChild(popupBody.firstChild);
    }

    // Add new Qliro checkout container with fresh content (fallback initialization)
    const qliroCheckoutDiv = document.createElement('div');
    qliroCheckoutDiv.id = 'qliro-checkout-container';
    qliroCheckoutDiv.className = 'cart-qliro';
    qliroCheckoutDiv.innerHTML = '<p class="cart-qliro__loading">Loading Checkout...</p>';
    popupBody.appendChild(qliroCheckoutDiv);

    // Hide the popup footer with buttons
    const popupFooter = exchangePopup.querySelector('.popup-footer');
    if (popupFooter) {
      popupFooter.style.display = 'none';
    }

    // Initialize Qliro checkout in the new container (fallback method)
    this.initializeQliroCheckout(qliroCheckoutDiv);
  }

  async waitForCartOrPrepare(exchangePopup, exchangeData, productId, productTitle, productPrice, voucherValue, priceDifference, productVariantGid = null) {


    // First, wait a bit for background preparation to complete
    const maxWaitTime = 3000; // 3 seconds max wait
    const checkInterval = 100; // Check every 100ms
    let waitTime = 0;

    while (waitTime < maxWaitTime) {
      const cartPrepared = exchangePopup.dataset.cartPrepared === 'true';
      const qliroReady = exchangePopup.dataset.qliroReady === 'true';

      if (cartPrepared && qliroReady) {

        return;
      }

      await new Promise(resolve => setTimeout(resolve, checkInterval));
      waitTime += checkInterval;
    }

    // If still not ready after waiting, check if we need to prepare the cart


    try {
      // Get current cart state
      let cartData = await window.apiService.get('/cart.js');
      if (typeof cartData === 'string') {
        cartData = JSON.parse(cartData);
      }

      // Get exchange product data
      const exchangeProduct = window.account?.exchangeProduct;
      if (!exchangeProduct) {
        throw new Error('Exchange product not configured in theme settings');
      }

      const exchangeVariantId = exchangeProduct.variantId;
      const exchangeProductPriceValue = exchangeProduct.price / 100;
      const quantityNeeded = Math.ceil(priceDifference / exchangeProductPriceValue);

      // Check if exchange product is already in cart with correct quantity
      const existingItem = cartData.items?.find(item =>
        item.variant_id.toString() === exchangeVariantId.toString()
      );

      if (existingItem && existingItem.quantity >= quantityNeeded) {

        exchangePopup.dataset.cartPrepared = 'true';
        return;
      }

      // If not in cart or wrong quantity, add/update without clearing existing cart


      if (existingItem) {
        // Update quantity if needed
        const additionalQuantity = quantityNeeded - existingItem.quantity;
        if (additionalQuantity > 0) {
          await window.apiService.post('/cart/add.js', {
            id: exchangeVariantId,
            quantity: additionalQuantity,
            properties: {
              '_extend_type': 'voucher_exchange',
              '_gift_card_gid': exchangeData.voucherId,
              '_product_variant_gid': productVariantGid
            }
          });

          // Fetch the full cart data after adding
          const cartResponse = await window.apiService.get('/cart.js');
          window.cart = typeof cartResponse === 'string' ? JSON.parse(cartResponse) : cartResponse;
        }
      } else {
        // Add new item with exchange properties
        await window.apiService.post('/cart/add.js', {
          id: exchangeVariantId,
          quantity: quantityNeeded,
          properties: {
            '_extend_type': 'voucher_exchange',
            '_gift_card_gid': exchangeData.voucherId,
            '_product_variant_gid': productVariantGid
          }
        });

        // Fetch the full cart data after adding
        const cartResponse = await window.apiService.get('/cart.js');
        window.cart = typeof cartResponse === 'string' ? JSON.parse(cartResponse) : cartResponse;
      }

      // Update header cart icon with the new cart data
      SharedCartUtils.updateHeaderCartIcon(window.cart.item_count || 0);

      exchangePopup.dataset.cartPrepared = 'true';


      // Try to initialize Qliro if it wasn't already prepared
      if (exchangePopup.dataset.qliroReady !== 'true') {

        try {
          await PopupUtils.initializeExchangeQliroCheckout(exchangePopup, window.cart);

        } catch (qliroError) {
          console.warn('Failed to initialize Qliro after cart preparation:', qliroError);
          // Continue without Qliro - will fall back to manual initialization
        }
      }

    } catch (error) {
      console.error('Error preparing cart:', error);
      throw error;
    }
  }

  // Check if pre-prepared Qliro is available and valid
  isPrePreparedQliroAvailable(exchangePopup) {
    const qliroReady = exchangePopup.dataset.qliroReady === 'true';
    const cartPrepared = exchangePopup.dataset.cartPrepared === 'true';
    const popupActive = exchangePopup.dataset.popupActive === 'true';
    const existingQliroContainer = exchangePopup.querySelector('.exchange-qliro-container');



    return qliroReady && cartPrepared && popupActive && existingQliroContainer;
  }

  // Check if pre-prepared Qliro is available and valid for extend popup
  isPrePreparedExtendQliroAvailable(extendPopup) {
    const qliroReady = extendPopup.dataset.qliroReady === 'true';
    const cartPrepared = extendPopup.dataset.cartPrepared === 'true';
    const popupActive = extendPopup.dataset.popupActive === 'true';
    const existingQliroContainer = extendPopup.querySelector('.extend-qliro-container');



    return qliroReady && cartPrepared && popupActive && existingQliroContainer;
  }

  // Generic method to open Qliro checkout for voucher operations (extend/exchange)
  async openQliroCheckoutForVoucher(popup, voucherType) {


    try {
      const isExtend = voucherType === 'extend';
      const containerClass = isExtend ? '.extend-qliro-container' : '.exchange-qliro-container';
      const popupBodyClass = isExtend ? '.extend-voucher-popup__body' : '.exchange-voucher-popup__body';
      const popupTitleClass = isExtend ? '.extend-voucher-popup__title' : '.exchange-voucher-popup__title';
      const checkoutTitle = isExtend ? (window.cartStrings?.checkout || 'Checkout') : (window.cartStrings?.checkout || 'Complete Exchange');
      const checkAvailabilityMethod = isExtend ? 'isPrePreparedExtendQliroAvailable' : 'isPrePreparedQliroAvailable';
      const fallbackMethod = isExtend ? 'replacePopupContentWithQliro' : 'replaceExchangePopupContentWithQliro';

      // Check if pre-prepared Qliro is available and valid
      if (this[checkAvailabilityMethod](popup)) {


        const existingQliroContainer = popup.querySelector(containerClass);
        const popupBody = popup.querySelector(popupBodyClass);

        if (!popupBody) {
          console.error(`${voucherType} popup body not found`);
          return false;
        }

        // Hide all existing content except the Qliro container
        Array.from(popupBody.children).forEach(child => {
          if (!child.classList.contains(containerClass.substring(1))) {
            child.style.display = 'none';
          }
        });

        // Show the pre-initialized Qliro container
        existingQliroContainer.style.display = 'block';

        // Clear any stale loading messages in the Qliro container
        const staleLoadingMessages = existingQliroContainer.querySelectorAll('.cart-qliro__loading');
        staleLoadingMessages.forEach(message => {
          if (message.textContent.includes('Fetching Qliro details') ||
            message.textContent.includes('Preparing checkout')) {

            message.remove();
          }
        });

        // Update popup title
        const popupTitle = popup.querySelector(popupTitleClass);
        if (popupTitle) {
          popupTitle.textContent = checkoutTitle;
        }

        // Hide the popup footer with buttons
        const popupFooter = popup.querySelector('.popup-footer');
        if (popupFooter) {
          popupFooter.style.display = 'none';
        }

        // Hide loading overlay if present (for exchange flow)
        const loadingOverlay = popup.querySelector('#exchange-loading-overlay');
        if (loadingOverlay) {
          loadingOverlay.classList.remove('is-visible');

        }


        return true;

      } else {


        // Get the updated cart data with items
        let cartData = await window.apiService.get('/cart.js');
        if (typeof cartData === 'string') {
          cartData = JSON.parse(cartData);
        }


        // Replace popup content with Qliro checkout (fallback)
        this[fallbackMethod](popup);

        // Hide loading overlay if present (for exchange flow)
        const loadingOverlay = popup.querySelector('#exchange-loading-overlay');
        if (loadingOverlay) {
          loadingOverlay.classList.remove('is-visible');

        }


        return true;
      }

    } catch (error) {
      console.error(`Error opening ${voucherType} Qliro checkout:`, error);

      // Hide loading overlay if present (for exchange flow)
      const loadingOverlay = popup.querySelector('#exchange-loading-overlay');
      if (loadingOverlay) {
        loadingOverlay.classList.remove('is-visible');

      }

      // Use centralized error handling
      ErrorUtils.error(`customer.account.${voucherType}`, `${voucherType}_checkout_error`, 'Unable to proceed with checkout. Please try again.');

      return false;
    }
  }

  async openQliroCheckoutForExtend(extendPopup) {
    return this.openQliroCheckoutForVoucher(extendPopup, 'extend');
  }

  async openQliroCheckoutForExchange(exchangePopup) {
    return this.openQliroCheckoutForVoucher(exchangePopup, 'exchange');
  }

  /**
   * Handle cancel booking action for unified popup (supports both single and group bookings)
   * Updated to use new API format from cancel_implementation_fe.md
   */
  async handleCancelAllBookings(event) {

    event.preventDefault();

    const popup = document.querySelector('#cancel-booking-popup-unified');
    if (!popup) {
      console.error('Unified cancel booking popup not found');
      ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', 'Cancel booking popup not found. Please try again.');
      return;
    }

    // Get selected booking IDs from popup dataset
    const selectedIds = JSON.parse(popup.dataset.selectedBookingIds || '[]');
    if (selectedIds.length === 0) {
      console.error('No vouchers selected for cancellation');
      ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', 'No vouchers selected. Please try again.');
      return;
    }



    // Show processing state
    const cancelButton = event.target.closest('button');
    if (cancelButton) {
      const originalText = cancelButton.textContent;
      cancelButton.textContent = TranslationUtils.get('customer.account.vouchers.cancel_booking.processing', 'Cancelling bookings...');
      cancelButton.disabled = true;

      try {
        // Check if this is a vendor/supplier
        const isVendor = window.account?.isVendor || false;

        // Extract booking codes if available
        let bookingCodes = null;
        const bookingTokensJson = popup.dataset.bookingTokens;
        if (bookingTokensJson) {
          try {
            const bookingTokens = JSON.parse(bookingTokensJson);
            if (bookingTokens && bookingTokens.length > 0) {
              bookingCodes = bookingTokens;
            }
          } catch (e) {
            console.warn('Failed to parse bookingTokens:', e);
          }
        }

        let result;

        if (isVendor) {
          // VENDOR FLOW - Use supplier-action endpoint


          // Determine action type based on current tab/filter
          const accountVouchersElement = document.querySelector('account-vouchers');
          const currentFilter = accountVouchersElement?.state?.currentFilter || '';

          // cancel_request for booking-requests tab, pure_cancel for upcoming-bookings tab
          const action = currentFilter === 'booking-requests' ? 'cancel_request' : 'pure_cancel';


          // Get cancel reason from popup textarea (note: textarea name is "cancel_reason_message")
          let cancelReason = null;
          const textarea = popup.querySelector('textarea[name="cancel_reason_message"]');
          if (textarea && textarea.value.trim()) {
            cancelReason = textarea.value.trim();
          }

          // Validate cancel reason for pure_cancel action (required)
          if (action === 'pure_cancel' && !cancelReason) {
            console.error('Cancel reason is required for pure_cancel action');
            ErrorUtils.error('customer.account.cancel_booking', 'cancel_reason_required',
              TranslationUtils.get('vendor.bookings.cancel_popup_unified.reason_required',
                'Please provide a reason for cancellation.'));
            cancelButton.disabled = false;
            cancelButton.textContent = originalText;
            return;
          }

          // Build payload for supplier-action endpoint
          const payload = {
            voucherIds: selectedIds,
            action: action
          };

          // Add cancel reason if provided (required for pure_cancel, optional for cancel_request)
          if (cancelReason) {
            payload.cancelReason = cancelReason;
          }

          // Add bookingCodes for conversation closure (recommended for cancel_request)
          if (bookingCodes && bookingCodes.length > 0) {
            payload.bookingCodes = bookingCodes;

          }


          result = await window.apiService.post(`${window.proxyPath}/supplier-action`, payload);

        } else {
          // CUSTOMER FLOW - Use cancel-booking endpoint


          const payload = {
            voucherIds: selectedIds
          };

          // Add bookingCodes for customers too
          if (bookingCodes && bookingCodes.length > 0) {
            payload.bookingCodes = bookingCodes;
          }


          result = await window.apiService.post(`${window.proxyPath}/cancel-booking`, payload);
        }

        if (result && result.success) {


          // Close the popup
          if (window.globalPopup && typeof window.globalPopup.closePopup === 'function') {
            window.globalPopup.closePopup(popup);
          }

          // Show success message
          const successMessage = result.message || TranslationUtils.get(
            'customer.account.vouchers.cancel_booking.bulk_success',
            `${selectedIds.length} booking(s) cancelled successfully`
          );
          ErrorUtils.success('customer.account.cancel_booking', 'cancel_booking_success', successMessage);

          // Refresh the voucher list to show updated voucher status
          setTimeout(() => {
            VoucherGroupingManager.refreshVoucherList();
          }, 1500);

        } else {
          console.error('Booking cancellation failed:', result);

          // Handle error response with better messaging
          let errorMessage = result?.error || result?.message || TranslationUtils.get(
            'customer.account.vouchers.cancel_booking.bulk_error',
            'Failed to cancel bookings. Please try again.'
          );

          // Add details if available (e.g., eligibility errors)
          if (result?.details) {
            if (result.details.reason) {
              errorMessage = result.details.reason;
            }
            if (result.details.daysUntilBooking !== undefined) {
              errorMessage += `\n\nYour booking is in ${result.details.daysUntilBooking} days, but cancellation requires at least ${result.details.requiredDays} days notice.`;
            }
          }

          ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', errorMessage);

          // Restore button state
          cancelButton.textContent = originalText;
          cancelButton.disabled = false;
        }

      } catch (error) {
        console.error('Error during cancel booking API call:', error);

        // Handle different error types based on HTTP status
        let errorMessage;
        if (error.status === 400) {
          errorMessage = error.error || error.message || TranslationUtils.get('customer.account.vouchers.cancel_booking.invalid_request', 'Invalid request. Please check your booking details.');

          if (error.details) {
            if (error.details.reason) {
              errorMessage = error.details.reason;
            }
            if (error.details.daysUntilBooking !== undefined) {
              errorMessage += `\n\nYour booking is in ${error.details.daysUntilBooking} days, but cancellation requires at least ${error.details.requiredDays} days notice.`;
            }
          }
        } else if (error.status === 404) {
          errorMessage = TranslationUtils.get('customer.account.vouchers.cancel_booking.not_found', 'Booking not found. It may have already been canceled.');
        } else if (error.status === 401 || error.status === 403) {
          errorMessage = TranslationUtils.get('customer.account.vouchers.cancel_booking.unauthorized', 'You are not authorized to cancel this booking.');
        } else if (error.status === 500) {
          errorMessage = TranslationUtils.get('customer.account.vouchers.cancel_booking.server_error', 'Server error. Please try again later or contact support.');
        } else if (!error.status) {
          errorMessage = TranslationUtils.get('customer.account.vouchers.cancel_booking.network_error', 'Network error. Please check your connection and try again.');
        } else {
          errorMessage = error.message || TranslationUtils.get(
            'customer.account.vouchers.cancel_booking.bulk_error',
            'Failed to cancel bookings. Please try again.'
          );
        }

        ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', errorMessage);

        // Restore button state
        cancelButton.textContent = originalText;
        cancelButton.disabled = false;
      }
    }
  }

  async handleCompleteAllBookings(event) {


    // Find the AccountVouchers instance and delegate to vendor-actions handler
    const accountVouchersElement = document.querySelector('account-vouchers');
    if (accountVouchersElement && accountVouchersElement.vendorActions) {
      // Delegate to the unified vendor actions handler
      await accountVouchersElement.vendorActions.handleCompleteAllBookings(event);
    } else {
      console.error('AccountVouchers or VendorActionsHelper not found');
      ErrorUtils.error('customer.account.complete_booking', 'complete_booking_error', 'Unable to process booking completion. Please refresh the page and try again.');
    }
  }

  async handleUpdateBookingDateTime(event) {
    // Find the AccountVouchers instance and delegate to vendor-actions handler
    const accountVouchersElement = document.querySelector('account-vouchers');
    if (accountVouchersElement && accountVouchersElement.vendorActions) {
      // Delegate to the unified vendor actions handler
      await accountVouchersElement.vendorActions.handleUpdateBookingDateTime(event);
    } else {
      console.error('AccountVouchers or VendorActionsHelper not found');
      const msg = TranslationUtils.get(
        'vendor.bookings.edit_datetime_popup.unable_to_update',
        'Unable to update booking. Please refresh the page and try again.'
      );
      if (typeof toastNotifications !== 'undefined' && toastNotifications) {
        toastNotifications.error(msg);
      }
    }
  }

  /**
   * Processes the cancel booking action when user confirms in popup
   * Updated to use new API format from cancel_implementation_fe.md
   */
  async processCancelBookingAction() {


    try {
      const cancelPopup = document.querySelector('#cancel-booking-popup');
      if (!cancelPopup) {
        console.error('Cancel booking popup not found');
        ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', 'Cancel booking popup not found. Please try again.');
        return;
      }

      // Check if this is a group cancellation (multiple vouchers) or single voucher
      const isGroupCancellation = cancelPopup.dataset.isGroupCancellation === 'true';
      let selectedIds;

      if (isGroupCancellation) {
        // Group cancellation: get array of voucher IDs
        const voucherIdsStr = cancelPopup.dataset.voucherIds;
        if (!voucherIdsStr) {
          console.error('Missing voucher IDs for group cancellation');
          ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', 'Missing voucher information. Please try again.');
          return;
        }
        selectedIds = JSON.parse(voucherIdsStr);

      } else {
        // Single voucher cancellation
        const voucherId = cancelPopup.dataset.voucherId;
        const voucherDataStr = cancelPopup.dataset.voucherData;

        if (!voucherId || !voucherDataStr) {
          console.error('Missing voucher information in popup dataset');
          ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', 'Missing voucher information. Please try again.');
          return;
        }
        selectedIds = [voucherId];

      }

      // Use the same flow as handleCancelAllBookings


      // Show processing state
      const cancelButton = cancelPopup.querySelector('[data-method="cancel-booking"]') || cancelPopup.querySelector('button[data-action*="cancel"]');
      let originalText = '';
      if (cancelButton) {
        originalText = cancelButton.textContent;
        cancelButton.textContent = TranslationUtils.get('customer.account.vouchers.cancel_booking.processing', 'Cancelling bookings...');
        cancelButton.disabled = true;
      }

      try {
        // Check if this is a vendor/supplier
        const isVendor = window.account?.isVendor || false;

        // Extract booking codes if available
        let bookingCodes = null;
        const bookingTokensJson = cancelPopup.dataset.bookingTokens;
        if (bookingTokensJson) {
          try {
            const bookingTokens = JSON.parse(bookingTokensJson);
            if (bookingTokens && bookingTokens.length > 0) {
              bookingCodes = bookingTokens;
            }
          } catch (e) {
            console.warn('Failed to parse bookingTokens:', e);
          }
        }

        let result;

        if (isVendor) {
          // VENDOR FLOW - Use supplier-action endpoint


          // Determine action type based on current tab/filter
          const accountVouchersElement = document.querySelector('account-vouchers');
          const currentFilter = accountVouchersElement?.state?.currentFilter || '';

          // cancel_request for booking-requests tab, pure_cancel for upcoming-bookings tab
          const action = currentFilter === 'booking-requests' ? 'cancel_request' : 'pure_cancel';


          // Get cancel reason from popup textarea
          let cancelReason = null;
          const textarea = cancelPopup.querySelector('textarea[name="cancel_reason_message"]');
          if (textarea && textarea.value.trim()) {
            cancelReason = textarea.value.trim();
          }

          // Validate cancel reason for pure_cancel action (required)
          if (action === 'pure_cancel' && !cancelReason) {
            console.error('Cancel reason is required for pure_cancel action');
            ErrorUtils.error('customer.account.cancel_booking', 'cancel_reason_required',
              TranslationUtils.get('vendor.bookings.cancel_popup_unified.reason_required',
                'Please provide a reason for cancellation.'));
            if (cancelButton) {
              cancelButton.disabled = false;
              cancelButton.textContent = originalText;
            }
            return;
          }

          // Build payload for supplier-action endpoint
          const payload = {
            voucherIds: selectedIds,
            action: action
          };

          // Add cancel reason if provided
          if (cancelReason) {
            payload.cancelReason = cancelReason;
          }

          // Add bookingCodes for conversation closure
          if (bookingCodes && bookingCodes.length > 0) {
            payload.bookingCodes = bookingCodes;
          }


          result = await window.apiService.post(`${window.proxyPath}/supplier-action`, payload);

        } else {
          // CUSTOMER FLOW - Use cancel-booking endpoint


          const payload = {
            voucherIds: selectedIds
          };

          // Add bookingCodes for customers too
          if (bookingCodes && bookingCodes.length > 0) {
            payload.bookingCodes = bookingCodes;
          }


          result = await window.apiService.post(`${window.proxyPath}/cancel-booking`, payload);
        }

        if (result && result.success) {


          // Close the popup
          if (window.globalPopup && typeof window.globalPopup.closePopup === 'function') {
            window.globalPopup.closePopup(cancelPopup);
          }

          // Show success message
          const successMessage = result.message || TranslationUtils.get(
            'customer.account.vouchers.cancel_booking.bulk_success',
            `${selectedIds.length} booking(s) cancelled successfully`
          );
          ErrorUtils.success('customer.account.cancel_booking', 'cancel_booking_success', successMessage);

          // Refresh the voucher list to show updated voucher status
          setTimeout(() => {
            VoucherGroupingManager.refreshVoucherList();
          }, 1500);

        } else {
          console.error('Booking cancellation failed:', result);
          // Handle error (same as handleCancelAllBookings)
          let errorMessage = result?.error || result?.message || TranslationUtils.get(
            'customer.account.vouchers.cancel_booking.bulk_error',
            'Failed to cancel bookings. Please try again.'
          );
          ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', errorMessage);
          if (cancelButton) {
            cancelButton.textContent = originalText;
            cancelButton.disabled = false;
          }
        }

      } catch (error) {
        console.error('Error during cancel booking API call:', error);
        // Handle error (same as handleCancelAllBookings)
        let errorMessage = error.message || TranslationUtils.get(
          'customer.account.vouchers.cancel_booking.bulk_error',
          'Failed to cancel bookings. Please try again.'
        );
        ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', errorMessage);
        if (cancelButton) {
          cancelButton.textContent = originalText;
          cancelButton.disabled = false;
        }
      }

    } catch (error) {
      console.error('Error in processCancelBookingAction:', error);
      ErrorUtils.error('customer.account.cancel_booking', 'cancel_booking_error', 'An unexpected error occurred. Please try again.');
    }
  }

  // Handle voucher grouping actions
  handleCombineVouchers(event) {


    // Use the VoucherGroupingManager instead of delegating to AccountVouchers
    VoucherGroupingManager.handleCombineVouchers(event);
  }

  handleContinueWithoutGrouping(event) {


    // Use the VoucherGroupingManager instead of delegating to AccountVouchers
    VoucherGroupingManager.handleContinueWithoutGrouping(event);
  }

  // Handle create purchase invoice action - generates supplier invoice
  clearPurchaseInvoicePopupError(popup = document.querySelector('#purchase-order-popup')) {
    const errorElement = popup?.querySelector('[data-invoice-popup-error]');
    if (!errorElement) {
      return;
    }
    errorElement.textContent = '';
    errorElement.setAttribute('hidden', '');
  }

  showPurchaseInvoicePopupError(message, popup = document.querySelector('#purchase-order-popup')) {
    const errorElement = popup?.querySelector('[data-invoice-popup-error]');
    if (!errorElement) {
      if (typeof toastNotifications !== 'undefined') {
        toastNotifications.error(message);
      }
      return;
    }

    errorElement.textContent = message;
    errorElement.removeAttribute('hidden');
    errorElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  formatPurchaseOrderMessage(path, fallback = '', vars = {}) {
    let text = TranslationUtils.get(path, fallback);

    Object.entries(vars).forEach(([key, value]) => {
      const replacement = value === undefined || value === null ? '' : String(value);
      text = text
        .replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), replacement)
        .replace(new RegExp(`\\{${key}\\}`, 'g'), replacement);
    });

    return text;
  }

  formatPurchaseInvoiceErrorMessage(error) {
    const rawMessage = error?.message || '';
    const waitMatch = rawMessage.match(/Must wait (\d+) more days/i);

    if (waitMatch) {
      return this.formatPurchaseOrderMessage(
        'vendor.purchase_orders_page.info_popup.invoice_threshold_wait',
        'Du måste vänta {days} dagar till innan du kan skapa ett nytt fakturaunderlag.',
        { days: waitMatch[1] }
      );
    }

    if (rawMessage) {
      return rawMessage.replace(/^Purchase invoice creation not allowed:\s*/i, '');
    }

    return this.formatPurchaseOrderMessage(
      'vendor.purchase_orders_page.invoice_create_failed',
      'Det gick inte att skapa fakturaunderlag. Försök igen.'
    );
  }

  getPopupStoredPurchaseOrderIds(popup) {
    const raw = popup?.dataset?.selectedPurchaseOrderIds;
    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return [];
      }
      return typeof SimplePdfManager !== 'undefined'
        ? SimplePdfManager.normalizePurchaseOrderIds(parsed)
        : parsed;
    } catch (error) {
      console.warn('Failed to parse popup purchase order IDs', error);
      return [];
    }
  }

  async processCreatePurchaseInvoiceAction(event) {


    const button = event.target.closest('button');
    if (!button) return;

    const originalButtonHtml = button.innerHTML;
    const popup = document.querySelector('#purchase-order-popup');

    try {
      this.clearPurchaseInvoicePopupError(popup);

      const creatingLabel = this.formatPurchaseOrderMessage(
        'vendor.purchase_orders_page.info_popup.creating',
        'Skapar fakturaunderlag...'
      );

      // Disable button and show loading state
      button.disabled = true;
      button.textContent = creatingLabel;

      // Get supplier email from account data
      const supplierEmail = window.account?.supplierSettings?.supplier_email ||
        window.account?.customerEmail ||
        window.customerEmail;

      if (!supplierEmail) {
        throw new Error('Supplier email not found');
      }

      // Generate supplier invoice using SimplePdfManager
      if (typeof SimplePdfManager === 'undefined') {
        throw new Error('SimplePdfManager not available');
      }

      const accountPurchaseOrders = document.querySelector('account-purchase-orders');
      const invoiceMode = popup?.dataset.invoiceMode || accountPurchaseOrders?.getInvoiceCreationMode?.() || 'all';
      let purchaseOrderIds = [];

      if (invoiceMode === 'selected') {
        purchaseOrderIds = this.getPopupStoredPurchaseOrderIds(popup);
        if (!purchaseOrderIds.length) {
          purchaseOrderIds = typeof SimplePdfManager !== 'undefined'
            ? SimplePdfManager.normalizePurchaseOrderIds(accountPurchaseOrders?.getSelectedPurchaseOrderIds?.() || [])
            : (accountPurchaseOrders?.getSelectedPurchaseOrderIds?.() || []);
        }
      }

      const invoiceOptions = {};
      if (window.account?.isVendorAdmin && popup) {
        const targetInput = popup.querySelector('[data-target-supplier-email]');
        const giftCardInput = popup.querySelector('[data-gift-card-ids]');
        const targetSupplierEmail = (targetInput?.value || '').trim();
        const giftCardRaw = (giftCardInput?.value || '').trim();

        if (targetSupplierEmail) {
          invoiceOptions.targetSupplierEmail = targetSupplierEmail;
        }

        if (giftCardRaw) {
          invoiceOptions.giftCardIds = giftCardRaw
            .split(/[\s,;]+/)
            .map((id) => id.trim())
            .filter(Boolean);
          purchaseOrderIds = [];
        }

        if (
          invoiceOptions.targetSupplierEmail &&
          invoiceOptions.targetSupplierEmail.toLowerCase() !== String(supplierEmail || '').toLowerCase()
        ) {
          purchaseOrderIds = [];
        }
      }

      if (!invoiceOptions.giftCardIds?.length && invoiceMode === 'selected' && !purchaseOrderIds.length) {
        throw new Error(this.formatPurchaseOrderMessage(
          'vendor.purchase_orders_page.info_popup.no_selection',
          'Välj minst en bokning att inkludera i fakturaunderlaget.'
        ));
      }

      button.textContent = creatingLabel;
      const invoicePayloadOptions = { ...invoiceOptions };
      if (purchaseOrderIds.length > 0) {
        invoicePayloadOptions.purchaseOrderIds = purchaseOrderIds;
      }

      const result = await SimplePdfManager.generateSupplierInvoicePdf(
        supplierEmail,
        Object.keys(invoicePayloadOptions).length > 0 ? invoicePayloadOptions : undefined
      );



      // Get invoice details
      const shopifyInvoice = result.shopifyInvoice || {};
      const aggregatedOrders = result.aggregatedOrders || {};
      const invoiceSummary = result.invoiceSummary || {};

      // Build success message
      let message = '';
      if (shopifyInvoice.name && aggregatedOrders.count) {
        message = `Invoice ${shopifyInvoice.name} created: ${aggregatedOrders.count} orders, ${shopifyInvoice.totalAmount} ${shopifyInvoice.currency}`;
      } else if (invoiceSummary.message) {
        message = invoiceSummary.message;
      } else if (result.message) {
        message = result.message;
      } else {
        message = 'Purchase invoice created successfully';
      }

      // Show success message
      if (typeof toastNotifications !== 'undefined') {
        toastNotifications.success(message);
      } else if (typeof ErrorUtils !== 'undefined' && ErrorUtils.success) {
        ErrorUtils.success('vendor.purchase_orders', 'invoice_created', message);
      }

      // Close the popup
      if (popup && window.globalPopup && typeof window.globalPopup.closePopup === 'function') {
        window.globalPopup.closePopup(popup);
      }

      const accountPurchaseOrdersElement = document.querySelector('account-purchase-orders');
      if (accountPurchaseOrdersElement) {
        if (typeof accountPurchaseOrdersElement.clearInvoiceSelection === 'function') {
          accountPurchaseOrdersElement.clearInvoiceSelection();
        }
        if (accountPurchaseOrdersElement.cache && typeof accountPurchaseOrdersElement.cache.clear === 'function') {
          accountPurchaseOrdersElement.cache.clear();
        }

        if (typeof accountPurchaseOrdersElement.activateSubtab === 'function') {
          accountPurchaseOrdersElement.activateSubtab('purchase-invoices', 1);
        } else if (typeof accountPurchaseOrdersElement.loadOrders === 'function') {
          accountPurchaseOrdersElement.loadOrders(1);
        }
      }

      button.disabled = false;
      button.innerHTML = originalButtonHtml;

    } catch (error) {
      console.error('Error creating purchase invoice:', error);

      // Restore button state on error
      button.disabled = false;
      button.innerHTML = originalButtonHtml || 'Got it';

      const message = this.formatPurchaseInvoiceErrorMessage(error);
      this.showPurchaseInvoicePopupError(message, popup);

      if (typeof toastNotifications !== 'undefined') {
        toastNotifications.error(message);
      }
    }
  }

}

// VoucherGroupingManager - Manages voucher grouping operations
class VoucherGroupingManager {

  static handleCombineVouchers(event) {
    event.preventDefault();


    // Get the selected vouchers from the UI
    const popup = document.querySelector('[id$="voucher-grouping-popup"]');


    if (!popup) {
      console.error('❌ Voucher grouping popup not found');
      // Let's also try alternative selectors
      const alternativePopup = document.querySelector('#voucher-grouping-popup');

      const allPopups = document.querySelectorAll('[id*="voucher-grouping"]');

      return;
    }

    const selectedCards = popup.querySelectorAll('.voucher-grouping-card--selected');


    const selectedVoucherIds = Array.from(selectedCards).map(card => {
      const voucherId = card.dataset.voucherId;

      return voucherId;
    }).filter(id => id && id.trim() !== ''); // Filter out empty/undefined IDs



    // Validate that we have valid voucher IDs
    if (selectedVoucherIds.length === 0) {
      console.error('❌ No valid voucher IDs found in selection');
      if (toastNotifications) {
        const errorMessage = TranslationUtils.get('grouping.no_vouchers_error', 'No vouchers selected. Please try again.');
        toastNotifications.error(errorMessage);
      }
      return;
    }

    // Since the primary voucher is always selected, we need at least 2 total (1 primary + 1 additional)
    if (selectedVoucherIds.length < 2) {
      console.error('❌ At least 1 additional voucher must be selected for combining. Current selection:', selectedVoucherIds.length);
      if (toastNotifications) {
        const errorMessage = TranslationUtils.get('grouping.min_selection_error', 'Please select at least 1 additional voucher to combine');
        toastNotifications.error(errorMessage);
      }
      return;
    }

    // Check if any voucher in redeemableVouchers has a grouped booking
    // If so, delete redeemableVouchers completely
    try {
      const redeemableVouchers = localStorage.getItem('redeemable_vouchers');
      if (redeemableVouchers) {
        const vouchers = JSON.parse(redeemableVouchers);
        const hasGroupedBooking = Object.values(vouchers).some(voucher =>
          voucher && (voucher.group_booking || voucher.groupBooking)
        );

        if (hasGroupedBooking) {
          localStorage.removeItem('redeemable_vouchers');
          console.log('Deleted redeemableVouchers due to grouped booking detection');
        }
      }
    } catch (error) {
      console.error('Error checking redeemableVouchers for grouped booking:', error);
    }

    // Get the stored voucher data from localStorage
    try {
      const groupingData = localStorage.getItem('voucherGroupingData');
      if (groupingData) {
        const parsedData = JSON.parse(groupingData);


        // Close the popup first
        if (window.globalPopup && typeof window.globalPopup.closePopup === 'function') {
          const popup = document.querySelector('[id$="voucher-grouping-popup"]');
          if (popup) {
            window.globalPopup.closePopup(popup);
          }
        }

        // Update the grouping data with selected vouchers
        parsedData.selectedVoucherIds = selectedVoucherIds;


        // Proceed with combined voucher booking
        VoucherGroupingManager.proceedWithCombinedVoucherBooking(parsedData);
      } else {
        console.error('No voucher grouping data found in localStorage');
        if (toastNotifications) {
          const errorMessage = TranslationUtils.get('error_message', 'Failed to process voucher action');
          toastNotifications.error(errorMessage);
        }
      }
    } catch (error) {
      console.error('Error handling combine vouchers:', error);
      if (toastNotifications) {
        const errorMessage = TranslationUtils.get('error_message', 'Failed to process voucher action');
        toastNotifications.error(errorMessage);
      }
    }
  }

  static handleContinueWithoutGrouping(event) {
    event.preventDefault();


    // Try to get the booking URL from the button href first
    const button = event.target.closest('.voucher-grouping-popup__continue-btn');
    let bookingUrl = button?.href;

    // If href is not set or is just the current page, build it from localStorage data
    if (!bookingUrl || bookingUrl === '#' || bookingUrl === window.location.href || bookingUrl.endsWith('#')) {

      try {
        const groupingData = localStorage.getItem('voucherGroupingData');
        if (groupingData) {
          const parsedData = JSON.parse(groupingData);
          const primaryVoucherId = parsedData.primaryVoucherId;

          if (primaryVoucherId && window.accountVouchers) {
            const voucherData = window.accountVouchers.findVoucherById(primaryVoucherId);
            if (voucherData) {
              bookingUrl = window.accountVouchers.buildBookingUrl(voucherData);

            }
          }
        }
      } catch (error) {
        console.error('Error building booking URL from localStorage:', error);
      }
    }

    if (bookingUrl && bookingUrl !== '#' && bookingUrl !== window.location.href) {
      // Clean up localStorage
      localStorage.removeItem('voucherGroupingData');

      // Navigate to booking page (popup will close automatically on page navigation)

      window.location.href = bookingUrl;
    } else {
      console.error('No valid booking URL found');
    }
  }

  static async proceedWithCombinedVoucherBooking(groupingData) {


    try {
      const selectedVoucherIds = groupingData.selectedVoucherIds || [];

      if (selectedVoucherIds.length < 2) {
        console.error('❌ At least 1 additional voucher must be selected for combining');
        if (toastNotifications) {
          const errorMessage = TranslationUtils.get('grouping.min_selection_error', 'Please select at least 1 additional voucher to combine');
          toastNotifications.error(errorMessage);
        }
        return;
      }

      if (selectedVoucherIds.length > 50) {
        console.error('❌ Cannot group more than 50 vouchers at once');
        if (toastNotifications) {
          const errorMessage = TranslationUtils.get('grouping.too_many_vouchers', 'Cannot group more than 50 vouchers at once');
          toastNotifications.error(errorMessage);
        }
        return;
      }

      // Show loading state
      if (toastNotifications) {
        const loadingMessage = TranslationUtils.get('grouping.combining_vouchers', 'Combining vouchers...');
        toastNotifications.info(loadingMessage);
      }

      // Use voucher IDs as they come from the data attributes (should be GIDs from backend)
      const voucherGids = selectedVoucherIds.filter(id => id && id.trim() !== '');



      // Validate that we have voucher IDs
      if (voucherGids.length === 0) {
        console.error('❌ No valid voucher GIDs found');
        if (toastNotifications) {
          const errorMessage = TranslationUtils.get('grouping.invalid_vouchers_error', 'Invalid voucher selection. Please try again.');
          toastNotifications.error(errorMessage);
        }
        return;
      }

      const requestData = {
        voucher_gids: voucherGids  // API expects voucher_gids, not vouchers, and no group_id
      };



      // Call the backend API endpoint
      const response = await VoucherGroupingManager.callGroupVouchersAPI(requestData);

      if (response.success) {
        // Clean up localStorage
        localStorage.removeItem('voucherGroupingData');

        // Show success message
        if (toastNotifications) {
          const successMessage = TranslationUtils.get('grouping.success', 'Vouchers combined successfully!');
          toastNotifications.success(successMessage);
        }

        // Close the popup instead of redirecting
        if (window.globalPopup && typeof window.globalPopup.closePopup === 'function') {
          const popup = document.querySelector('[id$="voucher-grouping-popup"]');
          if (popup) {
            window.globalPopup.closePopup(popup);
          }
        }

        // Refresh the voucher list if we're NOT coming from Book flow
        VoucherGroupingManager.refreshVoucherList();
      } else {
        console.error('❌ Group vouchers API failed:', response);
        const errorMessage = VoucherGroupingManager.handleGroupVouchersError(response.error || response);

        if (toastNotifications) {
          toastNotifications.error(errorMessage);
        }
      }

    } catch (error) {
      console.error('❌ Error in combined voucher booking:', error);

      // Handle specific API errors
      const errorMessage = VoucherGroupingManager.handleGroupVouchersError(error);

      if (toastNotifications) {
        toastNotifications.error(errorMessage);
      }
    }
  }

  /**
   * Call the group vouchers API endpoint using the API service
   */
  static async callGroupVouchersAPI(requestData) {
    try {
      // Ensure API service is available
      if (!window.apiService) {
        const errorMessage = 'API service not available';
        if (toastNotifications) {
          toastNotifications.error('System error. Please refresh the page and try again.');
        }
        throw new Error(errorMessage);
      }



      // Use the API service for consistent error handling and request formatting
      const result = await window.apiService.post(`${window.proxyPath}/group-vouchers`, requestData);


      return result;

    } catch (error) {
      console.error('❌ Group vouchers API call failed:', error);
      throw error;
    }
  }

  /**
   * Handle API errors and return user-friendly messages for ToastNotifications
   */
  static handleGroupVouchersError(error) {
    const errorMessage = error.message || error || 'Unknown error';

    // Map specific errors to user-friendly messages for ToastNotifications
    if (errorMessage.includes('400') || errorMessage.includes('validation')) {
      return TranslationUtils.get('grouping.validation_error', 'Please check your selection and try again');
    } else if (errorMessage.includes('401') || errorMessage.includes('unauthorized')) {
      return TranslationUtils.get('grouping.auth_error', 'Authentication expired. Please refresh the page and log in again.');
    } else if (errorMessage.includes('404')) {
      return TranslationUtils.get('grouping.not_found_error', 'Vouchers not found. Please refresh the page and try again.');
    } else if (errorMessage.includes('500')) {
      return TranslationUtils.get('grouping.server_error', 'Server error. Please try again in a few moments.');
    } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
      return TranslationUtils.get('grouping.network_error', 'Network error. Please check your connection and try again.');
    } else {
      return TranslationUtils.get('grouping.general_error', 'Failed to combine vouchers. Please try again.');
    }
  }

  /**
   * Refresh the voucher list after successful combination
   */
  static refreshVoucherList() {
    // Try to refresh via AccountVouchers if available
    const accountVouchers = document.querySelector('account-vouchers');
    if (accountVouchers && typeof accountVouchers.refreshVoucherList === 'function') {

      accountVouchers.refreshVoucherList.call(accountVouchers);
    } else if (window.accountVouchers && typeof window.accountVouchers.refreshVoucherList === 'function') {

      window.accountVouchers.refreshVoucherList.call(window.accountVouchers);
    } else {

      window.location.reload();
    }
  }
}

// ExchangeCollectionGrid - Custom element for exchange collection page initialization and cart loading
class ExchangeCollectionGrid extends HTMLElement {
  constructor() {
    super();

    this.element = this;
    this.cartState = null;
    this.cartLoaded = false;

    this.detailedPopup = new DetailedProductPopup();
    // Initialize exchange page with cart loading
    this.initializeExchangePage();


  }



  // Define which elements to exclude when filtering popup content
  // Note: .pdp__variants--grid is NOT excluded to allow variant selection in exchange popup
  getExcludeSelectors() {
    const excludeSelectors = [
      '.pm__buttons-pay-quantity',
      '.pm__info-note-wrapper',
      '.pc__labels-holder',
      '.pm__price-container',
      'notify-me-popup',
      '.favourites-input',
      '.favourites-label',
      // The campaign never applies to an exchange: its tag, countdown box and voucher-limit box
      '.pm__banner-label--campaign:not(.pm__banner-label--seasonal)',
      '.product-discount__banner--campaign',
      'campaign-allocation'
    ];
    return excludeSelectors;
  }

  contentToAdd() {


    // Get the template and clone its content
    const template = document.getElementById('detailed_view_popup_actions');


    if (!template) {
      console.warn('detailed_view_popup_actions template not found');

      return null;
    }

    // Clone the template content
    const templateContent = template.content.cloneNode(true);



    return templateContent;
  }

  // Populate the exchange price data in the template
  populateExchangePriceData(popup) {
    try {


      // Get exchange data from localStorage
      const exchangeData = JSON.parse(localStorage.getItem('exchangeGiftCard') || '{}');
      const voucherValue = parseFloat(exchangeData.originalValue) || 0;



      // Get product price from the stored original product data
      let productPrice = 0;


      if (popup.dataset.originalProductPrice) {
        productPrice = parseInt(popup.dataset.originalProductPrice) / 100; // Convert from cents to currency

      } else {
        console.warn('No originalProductPrice found in popup dataset');
      }

      // Calculate the difference (new price - current voucher value)
      const priceDifference = productPrice - voucherValue;



      // Find the template elements and populate them
      const templateActions = popup.querySelector('.detailed-view__actions');


      if (templateActions) {
        // Current experience price (voucher value) - data-price-old
        const currentPriceElement = templateActions.querySelector('[data-price-old]');

        if (currentPriceElement) {
          const formattedVoucherValue = Shopify.formatMoney(voucherValue * 100, window.theme.moneyFormat);
          currentPriceElement.textContent = formattedVoucherValue;

        }

        // New experience price (product price) - data-price-new
        const newPriceElement = templateActions.querySelector('[data-price-new]');

        if (newPriceElement) {
          const formattedProductPrice = Shopify.formatMoney(productPrice * 100, window.theme.moneyFormat);
          newPriceElement.textContent = formattedProductPrice;

        }

        // Get elements for gift card and subtotal rows
        const subtotalElement = templateActions.querySelector('[data-subtotal]');
        const subtotalRow = templateActions.querySelector('[data-subtotal-row]');
        const giftcardElement = templateActions.querySelector('[data-giftcard-amount]');
        const giftcardRow = templateActions.querySelector('[data-giftcard-row]');

        // Handle downgrade scenario (difference < 0)
        if (priceDifference < 0) {
          const downgradeAmount = Math.abs(priceDifference);

          // If downgrade is more than 50 SEK (5000 cents), show gift card
          if (downgradeAmount > 50) {
            // Hide subtotal row
            if (subtotalRow) {
              subtotalRow.style.display = 'none';
            }

            // Show gift card row
            if (giftcardElement && giftcardRow) {
              const formattedGiftcard = Shopify.formatMoney(downgradeAmount * 100, window.theme.moneyFormat);
              // Just show the amount with + sign (label is in liquid template now)
              const displayText = `+${formattedGiftcard}`;

              giftcardElement.textContent = displayText;
              giftcardRow.style.display = 'flex';

            }
          } else {
            // Downgrade is 50 SEK or less, show as 0 subtotal
            if (subtotalElement && subtotalRow) {
              const formattedDifference = Shopify.formatMoney(0, window.theme.moneyFormat);
              subtotalElement.textContent = formattedDifference;
              subtotalRow.style.display = 'flex';

            }

            // Hide gift card row
            if (giftcardRow) {
              giftcardRow.style.display = 'none';
            }
          }
        } else {
          // Handle upgrade scenario (difference >= 0)
          if (subtotalElement && subtotalRow) {
            const formattedDifference = Shopify.formatMoney(priceDifference * 100, window.theme.moneyFormat);
            subtotalElement.textContent = formattedDifference;
            subtotalRow.style.display = 'flex';

          }

          // Hide gift card row
          if (giftcardRow) {
            giftcardRow.style.display = 'none';
          }
        }


      } else {
        console.error('Template actions (.detailed-view__actions) not found in popup');
      }
    } catch (error) {
      console.error('Error populating exchange price data:', error);
    }
  }

  // Store the original product card data when popup opens
  storeOriginalProductCard(popup, originalElement) {
    try {


      // The originalElement should be the button that was clicked
      let learnMoreButton = originalElement;

      // If originalElement is not the learn more button directly, try to find it
      if (!learnMoreButton || !learnMoreButton.dataset || !learnMoreButton.dataset.action) {
        learnMoreButton = originalElement?.closest('[data-action="detailed-view"]') ||
          originalElement?.querySelector('[data-action="detailed-view"]');
      }

      // Find the product card that contains the "Learn more" button that was clicked
      const productCard = originalElement?.closest('product-card');



      if (productCard && learnMoreButton) {
        // Store the product card reference on the popup for later use
        popup._originalProductCard = productCard;

        // Extract and store product data from the learn more button
        popup.dataset.originalProductId = learnMoreButton.dataset.productId;
        popup.dataset.originalProductTitle = learnMoreButton.dataset.productTitle;
        popup.dataset.originalProductPrice = learnMoreButton.dataset.productPrice;

        // Initialize variant info
        const productComponent = popup.querySelector('product-component');
        if (!productComponent) return;
        
        const currentVariant = productComponent.currentVariant || window.selectedVariant;
        if (!currentVariant) return;
        
        const variantId = currentVariant.id.toString();
        popup.dataset.originalVariantId = variantId;

        const baseProductTitle = popup.dataset.originalProductTitle;
        const variantOptions = currentVariant.options || [];
        
        const productOptions = productComponent.querySelectorAll('.VariantSelect, .pm__booking-step');
        const variantOptionObjects = [];
        const variantOptionLabels = [];
        
        productOptions.forEach((optionGroup, index) => {
          const fieldLabel = optionGroup.querySelector('.field-label');
          if (!fieldLabel || !variantOptions[index]) return;
          
          const optionLabel = fieldLabel.textContent.trim();
          const optionValue = variantOptions[index];
          variantOptionLabels.push(`${optionLabel}: ${optionValue}`);
          variantOptionObjects.push({
            name: optionLabel,
            value: optionValue
          });
        });
        
        const fullTitle = variantOptionLabels.length > 0 
          ? `${baseProductTitle} - ${variantOptionLabels.join(', ')}`
          : baseProductTitle;
        
        const variantImageSrc = currentVariant.featured_media?.preview_image?.src || '';
        
        popup.dataset.currentVariantInfo = JSON.stringify({
          id: variantId,
          title: fullTitle,
          image: variantImageSrc,
          options: variantOptionObjects
        });
      }
    } catch (error) {
      console.error('Error storing original product card:', error);
    }
  }

  connectedCallback() {




    this.detailedPopup.attachToElement(this.element, this.getExcludeSelectors());

    // Store bound method for proper cleanup
    this.boundHandleDetailedViewContentLoaded = this.handleDetailedViewContentLoaded.bind(this);
    this.boundHandleClickExchange = this.handleClickExchange.bind(this);



    // Listen for detailed view content loaded event

    document.addEventListener('detailed-view-content-loaded', this.boundHandleDetailedViewContentLoaded);

    // Add a test listener to see if the event is being dispatched at all
    document.addEventListener('detailed-view-content-loaded', (event) => {

    });

    // Listen for click-exchange action from template buttons
    document.addEventListener('click', this.boundHandleClickExchange);
  }

  // Handle click-exchange action from template button
  handleClickExchange(event) {
    // Check if this is a click-exchange action
    if (!event.target.matches('[data-action="click-exchange"]')) {
      return;
    }

    // Only handle if we're in an exchange collection context
    if (!this.element || !this.element.matches('exchange-collection-grid')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    // Find the detailed view popup
    const popup = event.target.closest('#detailed_view_popup');
    if (!popup) {
      console.error('Could not find detailed view popup');
      return;
    }

    // Get the stored product data from the popup dataset
    const productId = popup.dataset.originalProductId;
    const variantId = popup.dataset.originalVariantId || popup.dataset.originalProductId; // Use variant ID if available, fallback to product ID
    const productTitle = popup.dataset.originalProductTitle;
    const productPrice = popup.dataset.originalProductPrice;

    if (!productId || !productPrice) {
      console.error('Missing product data for exchange from popup dataset');
      return;
    }

    // Log which ID is being used for exchange
    if (popup.dataset.originalVariantId) {

    } else {
      console.warn('⚠️ No variant ID found, using product ID as fallback:', productId);
    }

    // Check if exchange product is properly configured
    const exchangeProduct = window.account?.exchangeProduct;
    if (!exchangeProduct || !exchangeProduct.exists || !exchangeProduct.variantId || !exchangeProduct.price) {
      console.error('Exchange product not properly configured:', exchangeProduct);
      ErrorUtils.error('customer.account.exchange', 'exchange_config_error', 'Exchange functionality is not available. Please contact support.');
      return;
    }

    // Get exchange data from localStorage
    const exchangeData = JSON.parse(localStorage.getItem('exchangeGiftCard') || '{}');
    if (!exchangeData.voucherId) {
      console.error('No exchange voucher data found');
      ErrorUtils.error('customer.account.exchange', 'exchange_session_expired', 'Exchange session expired. Please start the exchange process again.');
      return;
    }

    // Construct variant info object - use the actual variant ID
    const variantInfo = {
      id: variantId, // Use variant ID (not product ID)
      title: productTitle
    };

    // Try to get the correct variant from our cached popup variant JSON first.
    // This is the most reliable source because resolveSelectedVariantInPopup
    // already matched and stored the correct variant ID in popup.dataset.
    const cachedVariant = this._popupVariantData
      ? this._popupVariantData.find(v => String(v.id) === String(variantId))
      : null;

    if (cachedVariant) {
      // Build options from the cached variant JSON + DOM labels
      if (cachedVariant.featured_image) {
        variantInfo.image = cachedVariant.featured_image?.src || null;
      }
      // If variant has no specific image, get the product image from the
      // original product card or from the popup's first slide image.
      if (!variantInfo.image) {
        const originalProductCard = popup._originalProductCard;
        if (originalProductCard && originalProductCard.dataset.variantInfo) {
          try {
            variantInfo.image = JSON.parse(originalProductCard.dataset.variantInfo).image || null;
          } catch (e) { /* ignore */ }
        }
        if (!variantInfo.image) {
          const slideImg = popup.querySelector('.ProductSlider--Image');
          if (slideImg) {
            variantInfo.image = slideImg.dataset.originalSrc || slideImg.src || null;
          }
        }
      }
      // Get option names from .pdp__variants--grid labels inside the popup
      // (the popup has no <variant-selects>, so we read from product-component)
      const productComponent = popup.querySelector('product-component');
      const optionLabels = productComponent
        ? productComponent.querySelectorAll('.pdp__variants--grid .field-label')
        : [];
      // Normalize: variant_selector.liquid strips dots from option values
      const normalizeOption = (val) => {
        const str = String(val).trim();
        return str.includes('.') ? str.split('.')[0] : str;
      };
      variantInfo.options = cachedVariant.options.map((value, index) => {
        const label = optionLabels[index];
        return {
          name: label?.textContent?.trim() || `Option ${index + 1}`,
          value: normalizeOption(value)
        };
      }).filter(opt => opt.value !== 'Default Title');
    } else {
      // Fallback 1: Try window.selectedVariant (works on standard PDP, usually null in popup)
      const currentVariant = window.selectedVariant;
      if (currentVariant && String(currentVariant.id) === String(variantId)) {
        if (currentVariant.featured_image) {
          variantInfo.image = currentVariant.featured_image.src;
        }
        if (currentVariant.options) {
          const variantSelects = popup.querySelector('variant-selects');
          if (variantSelects) {
            const optionLabels = variantSelects.querySelectorAll('.field-label, .variant-label');
            variantInfo.options = currentVariant.options.map((value, index) => {
              const label = optionLabels[index];
              return {
                name: label?.textContent?.trim() || `Option ${index + 1}`,
                value: value
              };
            }).filter(opt => opt.value !== 'Default Title');
          }
        }
      } else {
        // Fallback 2: Try the original product card data
        const originalProductCard = popup._originalProductCard;
        if (originalProductCard && originalProductCard.dataset.variantInfo) {
          try {
            const additionalVariantInfo = JSON.parse(originalProductCard.dataset.variantInfo);
            variantInfo.image = additionalVariantInfo.image;
            variantInfo.options = additionalVariantInfo.options;
          } catch (error) {
            console.warn('Failed to parse additional variant info:', error);
          }
        }
      }
    }



    // Close the detailed view popup first
    if (window.globalPopup && typeof window.globalPopup.closePopup === 'function') {
      window.globalPopup.closePopup(popup);
    }

    // Wait a brief moment for popup close animation, then open exchange popup
    setTimeout(() => {

      PopupUtils.openExchangePopup(productId, productTitle, productPrice, variantInfo);
    }, 100);
  }

  // Handle when detailed view content is loaded
  handleDetailedViewContentLoaded(event) {

    const { popup, originalElement } = event.detail;






    // Only proceed if this is an exchange collection context
    if (!this.element || !this.element.matches('exchange-collection-grid')) {

      return;
    }

    // Remove paused connected products from the location picker in exchange context.
    // Why: liquid keeps paused items on the regular PDP so normal browsing is unaffected;
    // we strip them here so users can't switch into a paused product through the city dropdown.
    this.removePausedConnectedProducts(popup);

    // Store the original product card element for later use
    this.storeOriginalProductCard(popup, originalElement);

    // Get the template content
    const templateContent = this.contentToAdd();
    if (!templateContent) {
      console.error('Failed to get exchange actions template content');
      return;
    }

    // Find the popup footer and append the template content
    const popupContent = popup.querySelector('.pm__information');


    if (popupContent) {
      // Check if exchange actions are already added to avoid duplicates
      const existingActions = popupContent.querySelector('.detailed-view__actions');
      if (!existingActions) {



        // Insert the template content before the existing footer content
        popupContent.appendChild(templateContent);


        // Use requestAnimationFrame to ensure DOM is updated before populating data
        requestAnimationFrame(() => {
          // Populate the price data after insertion and DOM update
          this.populateExchangePriceData(popup);
        });
      }
      
      // Initialize selectboxes in the popup (they require explicit instantiation)
      this.initPopupSelectboxes(popup);
      
      // Set up variant change listener for the popup
      this.setupVariantChangeListener(popup);
    }
  }

  removePausedConnectedProducts(popup) {
    const picker = popup.querySelector('connected-products-picker');
    if (!picker) return;

    const pausedItems = picker.querySelectorAll('[data-paused="true"]');
    if (!pausedItems.length) return;

    const pausedIds = new Set();
    pausedItems.forEach((el) => {
      const id = el.dataset.productId;
      if (id) pausedIds.add(String(id));
      el.remove();
    });

    // Keep data-products JSON in sync so prefetch/reload logic won't resurrect paused ids
    try {
      const ids = JSON.parse(picker.dataset.products || '[]');
      if (Array.isArray(ids)) {
        picker.dataset.products = JSON.stringify(
          ids.filter((id) => !pausedIds.has(String(id)))
        );
      }
    } catch (error) {
      console.warn('[Exchange Popup] Failed to sync connected-products data after paused removal:', error);
    }

    // If nothing is left in the dropdown, hide the picker entirely
    if (!picker.querySelector('.pm__connected-product__selectbox-item')) {
      picker.style.display = 'none';
    }
  }

  // Initialize RevolutionSelectbox instances inside the popup.
  // On the PDP, ProductElement.initSelectboxes() handles this,
  // but dynamically loaded popup content needs manual initialization.
  initPopupSelectboxes(popup) {
    const selectBoxes = popup.querySelectorAll('.pdp__variants--grid .RevolutionSelectbox');
    console.log('[Exchange Popup] initPopupSelectboxes: found', selectBoxes.length, 'selectboxes');
    selectBoxes.forEach((item, i) => {
      // Only init if not already an instance
      const alreadyInitialized = RevolutionSelectbox.instances && 
        Array.from(RevolutionSelectbox.instances).some(inst => inst.wrapper === item);
      if (!alreadyInitialized) {
        console.log('[Exchange Popup] Initializing selectbox', i);
        new RevolutionSelectbox(item);
      } else {
        console.log('[Exchange Popup] Selectbox', i, 'already initialized');
      }
    });
  }
  
  // Set up listener for variant changes in the detailed view popup
  setupVariantChangeListener(popup) {
    // Clean up any existing listeners
    this.cleanupVariantChangeListeners();
    
    // The popup does NOT have a <variant-selects> custom element.
    // Variant blocks are bare .pdp__variants--grid divs inside product-component.
    // Variant JSON is a bare <script type="application/json"> inside product-component.
    const productComponent = popup.querySelector('product-component');
    if (!productComponent) {
      console.log('[Exchange Popup] setupVariantChangeListener: no product-component found');
      return;
    }
    
    // Find variant JSON - bare script tag inside product-component (no id)
    const variantJsonScript = productComponent.querySelector('script[type="application/json"]');
    if (variantJsonScript) {
      try {
        this._popupVariantData = JSON.parse(variantJsonScript.textContent);
        console.log('[Exchange Popup] Parsed variant data:', this._popupVariantData.length, 'variants');
      } catch (e) {
        console.warn('[Exchange Popup] Failed to parse variants JSON:', e);
        this._popupVariantData = null;
      }
    } else {
      console.warn('[Exchange Popup] No variant JSON script found in product-component');
    }
    
    // Find all selects and inputs inside variant selector blocks
    const variantBlocks = productComponent.querySelectorAll('.pdp__variants--grid');
    const selects = productComponent.querySelectorAll('.pdp__variants--grid select');
    const inputs = productComponent.querySelectorAll('.pdp__variants--grid input');
    console.log('[Exchange Popup] Found', variantBlocks.length, 'variant blocks,', selects.length, 'selects,', inputs.length, 'inputs');
    
    // Listen on product-component for change/input events from variant selectors
    this._variantSelectsElement = productComponent;
    this.boundHandleVariantChange = (e) => {
      // Only react to changes from within variant blocks
      if (!e.target.closest('.pdp__variants--grid')) return;
      console.log('[Exchange Popup] Change/input event fired:', e.type, 'target:', e.target.tagName, e.target.className, 'value:', e.target.value);
      // Pass the changed element so we can prioritize its value
      const changedEl = e.target;
      setTimeout(() => this.handleVariantChangeInPopup(popup, changedEl), 0);
    };
    
    productComponent.addEventListener('change', this.boundHandleVariantChange);
    productComponent.addEventListener('input', this.boundHandleVariantChange);
  }
  
  // Clean up variant change listeners
  cleanupVariantChangeListeners() {
    if (this.boundHandleVariantChange && this._variantSelectsElement) {
      this._variantSelectsElement.removeEventListener('change', this.boundHandleVariantChange);
      this._variantSelectsElement.removeEventListener('input', this.boundHandleVariantChange);
      this._variantSelectsElement = null;
      this.boundHandleVariantChange = null;
    }
  }
  
  // Keep duplicate variant blocks in sync.
  // A product may render the same option as both a participants number-input
  // and a RevolutionSelectbox dropdown.  When one changes we must update the
  // other so the UI stays consistent and the value-collection logic works.
  syncDuplicateVariantBlocks(popup, changedEl) {
    if (!changedEl) return;
    const productComponent = popup.querySelector('product-component');
    if (!productComponent) return;
    
    const newValue = String(changedEl.value).trim();
    const isParticipantsInput = changedEl.matches('.pm__participants-input') ||
      changedEl.closest('quantity-input-participants');
    const isSelectInput = changedEl.matches('select.selectboxInputs') ||
      changedEl.closest('.RevolutionSelectbox');
    
    if (isParticipantsInput) {
      // Participants input changed → sync all hidden selects in other variant blocks
      const selects = productComponent.querySelectorAll('.pdp__variants--grid select.selectboxInputs');
      selects.forEach(select => {
        if (select.closest('.pdp__variants--grid') === changedEl.closest('.pdp__variants--grid')) return;
        // Check if this select has the new value as an option
        const matchingOption = Array.from(select.options).find(o => o.value === newValue);
        if (matchingOption) {
          select.value = newValue;
          // Also update the RevolutionSelectbox UI (selected button)
          const wrapper = select.closest('.RevolutionSelectbox');
          if (wrapper) {
            const prevBtn = wrapper.querySelector('.RevolutionSelectbox--Item[selected]');
            if (prevBtn) { prevBtn.removeAttribute('selected'); prevBtn.removeAttribute('disabled'); }
            const newBtn = wrapper.querySelector(`.RevolutionSelectbox--Item[data-value="${CSS.escape(newValue)}"]`);
            if (newBtn) {
              newBtn.setAttribute('selected', 'selected');
              newBtn.setAttribute('disabled', 'true');
              const titleEl = wrapper.querySelector('.RevolutionSelectbox--Title');
              if (titleEl) titleEl.innerHTML = newBtn.innerHTML;
            }
          }
        }
      });
    } else if (isSelectInput) {
      // Select/dropdown changed → sync participants inputs in other variant blocks
      const inputs = productComponent.querySelectorAll('.pdp__variants--grid .pm__participants-input');
      inputs.forEach(input => {
        if (input.closest('.pdp__variants--grid') === changedEl.closest('.pdp__variants--grid')) return;
        const min = parseInt(input.min) || 0;
        const max = parseInt(input.max) || Infinity;
        const numVal = parseInt(newValue);
        if (!isNaN(numVal) && numVal >= min && numVal <= max) {
          input.value = newValue;
        }
      });
    }
  }
  
  // Resolve the currently selected variant from the popup's variant selects
  resolveSelectedVariantInPopup(popup, changedEl = null) {
    if (!this._popupVariantData) {
      console.log('[Exchange Popup] resolveVariant: no variant data cached');
      return null;
    }
    
    const productComponent = popup.querySelector('product-component');
    if (!productComponent) {
      console.log('[Exchange Popup] resolveVariant: no product-component');
      return null;
    }
    
    // How many options the product actually has (from the variant JSON).
    // Multiple DOM blocks can represent the same option (e.g. a participants
    // number-input AND a dropdown select both for "Select number of people"),
    // so we must collect at most this many values.
    const expectedOptionCount = this._popupVariantData[0]?.options?.length || 0;
    
    // Collect one value per variant block (.pdp__variants--grid).
    const variantBlocks = productComponent.querySelectorAll('.pdp__variants--grid');
    const changedBlock = changedEl ? changedEl.closest('.pdp__variants--grid') : null;
    const allBlockValues = []; // { value, isChanged }
    
    variantBlocks.forEach((block, i) => {
      let value = null;
      let isChanged = false;
      
      // If this block contains the element that just changed, use that value
      if (changedBlock === block && changedEl) {
        value = changedEl.value;
        isChanged = true;
        console.log('[Exchange Popup] resolveVariant: block', i, 'using CHANGED element value:', value);
      } else {
        // Read from hidden select (dropdown) 
        const select = block.querySelector('select.selectboxInputs');
        if (select) {
          value = select.value;
          console.log('[Exchange Popup] resolveVariant: block', i, 'select value:', value);
        } else {
          // Try number input (participants)
          const numberInput = block.querySelector('input[type="number"]');
          if (numberInput) {
            value = numberInput.value;
            console.log('[Exchange Popup] resolveVariant: block', i, 'number value:', value);
          } else {
            // Try checked radio/checkbox
            const checked = block.querySelector('input:not(.form-checkbox__input):checked');
            if (checked) {
              value = checked.value;
              console.log('[Exchange Popup] resolveVariant: block', i, 'checked value:', value);
            }
          }
        }
      }
      
      if (value !== null) {
        allBlockValues.push({ value: String(value).trim(), isChanged });
      }
    });
    
    // Build selectedOptions: prioritise the changed element's value, then fill
    // from the remaining blocks up to expectedOptionCount.  This correctly
    // handles duplicate blocks for the same option (e.g. participants input +
    // dropdown both representing one option) — even when the blocks are out of
    // sync after a user interaction.
    const selectedOptions = [];
    const seen = new Set();
    
    // 1. Changed element value first (most reliable)
    for (const entry of allBlockValues) {
      if (entry.isChanged && !seen.has(entry.value)) {
        seen.add(entry.value);
        selectedOptions.push(entry.value);
      }
    }
    // 2. Fill with remaining unique values
    for (const entry of allBlockValues) {
      if (selectedOptions.length >= expectedOptionCount && expectedOptionCount > 0) break;
      if (!seen.has(entry.value)) {
        seen.add(entry.value);
        selectedOptions.push(entry.value);
      }
    }
    
    console.log('[Exchange Popup] resolveVariant: selectedOptions:', JSON.stringify(selectedOptions),
      '(from', allBlockValues.length, 'blocks, expected', expectedOptionCount, 'options)');
    console.log('[Exchange Popup] resolveVariant: variant JSON options:', this._popupVariantData.map(v => ({ id: v.id, options: v.options })));
    
    if (selectedOptions.length === 0) return null;
    
    // Normalize option values: variant_selector.liquid strips everything after the
    // first dot from option values (e.g. "1.timme" → "1"), but the variant JSON
    // ({{ product.variants | json }}) keeps the originals.  We must apply the same
    // cleaning before comparing so DOM values match the JSON data.
    const normalizeOption = (val) => {
      const str = String(val).trim();
      return str.includes('.') ? str.split('.')[0] : str;
    };
    
    // Match against variant data using positional comparison with normalisation.
    const matchedVariant = this._popupVariantData.find(variant => {
      if (variant.options.length !== selectedOptions.length) return false;
      return selectedOptions.every((opt, index) =>
        normalizeOption(variant.options[index]) === normalizeOption(opt)
      );
    });
    
    // Fallback: set-based matching with normalisation
    const matchedVariantFallback = !matchedVariant ? this._popupVariantData.find(variant => {
      if (variant.options.length !== selectedOptions.length) return false;
      const normVariantOpts = variant.options.map(normalizeOption);
      const normSelectedOpts = selectedOptions.map(normalizeOption);
      return normSelectedOpts.every(opt => normVariantOpts.includes(opt)) &&
             normVariantOpts.every(opt => normSelectedOpts.includes(opt));
    }) : null;
    
    const finalMatch = matchedVariant || matchedVariantFallback;
    
    console.log('[Exchange Popup] resolveVariant: matched variant:', finalMatch ? { id: finalMatch.id, price: finalMatch.price, options: finalMatch.options } : 'NONE');
    return finalMatch || null;
  }
  
  // Handle variant change in the detailed view popup
  handleVariantChangeInPopup(popup, changedEl = null) {
    try {
      console.log('[Exchange Popup] handleVariantChangeInPopup called');
      console.log('[Exchange Popup] window.selectedVariant:', window.selectedVariant ? { id: window.selectedVariant.id, price: window.selectedVariant.price } : 'null');
      
      // Sync duplicate blocks (e.g. participants input ↔ dropdown) before resolving
      this.syncDuplicateVariantBlocks(popup, changedEl);
      
      // Resolve variant: try our own resolution first (most reliable in popup context),
      // then fall back to window.selectedVariant
      const currentVariant = this.resolveSelectedVariantInPopup(popup, changedEl) || window.selectedVariant;
      if (!currentVariant) {
        console.warn('[Exchange Popup] No selected variant found in popup');
        return;
      }
      
      console.log('[Exchange Popup] Using variant:', { id: currentVariant.id, price: currentVariant.price, title: currentVariant.title });
      console.log('[Exchange Popup] Previous popup price:', popup.dataset.originalProductPrice);
      
      // Update popup dataset with new variant information
      popup.dataset.originalVariantId = currentVariant.id;
      popup.dataset.originalProductPrice = currentVariant.price;
      
      console.log('[Exchange Popup] Updated popup price to:', popup.dataset.originalProductPrice);
      
      // Recalculate and update prices based on the new variant
      this.populateExchangePriceData(popup);
      
      console.log('[Exchange Popup] Price update complete');
    } catch (error) {
      console.error('[Exchange Popup] Error handling variant change:', error);
    }
  }

  // Initialize exchange page with cart loading as first step
  async initializeExchangePage() {
    try {


      // Step 1: Load cart state first (critical for performance)
      await this.loadAndCacheCartState();

      // Step 2: Proceed with exchange logic setup
      this.proceedWithExchangeLogic();


    } catch (error) {
      console.error('ExchangeCollectionGrid: Failed to initialize exchange page:', error);
      this.handleInitializationError(error);
    }
  }

  // Load cart state and cache it for exchange operations
  async loadAndCacheCartState() {


    try {
      // Use existing ExchangeCartManager for DRY compliance
      const cartData = await ExchangeCartManager.initializeCartState();

      // Save cart state locally for immediate access
      this.saveCartState(cartData);


    } catch (error) {
      console.error('ExchangeCollectionGrid: Error loading cart state:', error);
      throw error;
    }
  }

  // Save cart state locally (wrapper around ExchangeCartManager for consistency)
  saveCartState(cartData) {
    this.cartState = cartData;
    this.cartLoaded = true;

    // Mark element as cart-ready for CSS/other components
    this.dataset.cartLoaded = 'true';
    this.dataset.cartItemCount = cartData?.item_count || '0';


  }

  // Proceed with exchange logic after cart is loaded
  proceedWithExchangeLogic() {
    // Only proceed once cart is loaded
    if (!this.cartLoaded) {
      throw new Error('ExchangeCollectionGrid: Cart not loaded - cannot proceed with exchange logic');
    }



    // Set up event delegation for exchange actions
    this.setupExchangeEventDelegation();

    // Initialize ExchangeCardPrice for price difference calculations
    this.initializeExchangeCardPrice();

    // Mark as ready for exchange operations
    this.dataset.exchangeReady = 'true';


  }

  // Set up event delegation for exchange-specific actions
  // Set up event delegation for exchange-specific actions
  setupExchangeEventDelegation() {


    // Use Delegate class for event handling
    this.delegateElement = new Delegate(this.element);

    // Handle exchange actions directly
    this.delegateElement.on('click', '[data-action="voucher-action"][data-method="init-exchange"]', this.handleInitExchangeAction.bind(this));


  }

  // Handle exchange initialization (moved from VoucherActionHandler for better separation)
  handleInitExchangeAction(event) {
    event.preventDefault();


    // Check if exchange product is properly configured first
    const exchangeProduct = window.account?.exchangeProduct;
    if (!exchangeProduct || !exchangeProduct.exists || !exchangeProduct.variantId || !exchangeProduct.price) {
      console.error('Exchange product not properly configured:', exchangeProduct);
      ErrorUtils.error('customer.account.exchange', 'exchange_config_error', 'Exchange functionality is not available. Please contact support.');
      return;
    }

    const productId = event.target.dataset.productId; // This is actually the variant ID
    const productTitle = event.target.dataset.productTitle;
    const productPrice = event.target.dataset.productPrice;

    if (!productId || !productPrice) {
      console.error('Missing product data for exchange');
      return;
    }

    // Construct variant info object with the variant ID from data-product-id
    // The data-product-id actually contains the variant ID, not the product ID
    const variantInfo = {
      id: productId, // This is the variant ID from the button
      title: productTitle,
      // We can still try to get additional variant info from data-variant-info if available
    };

    // Try to get additional variant info from the product card's data-variant-info attribute
    const productCard = event.target.closest('product-card');
    if (productCard && productCard.dataset.variantInfo) {
      try {
        const additionalVariantInfo = JSON.parse(productCard.dataset.variantInfo);
        // Merge additional info but keep the variant ID from the button
        variantInfo.image = additionalVariantInfo.image;
        variantInfo.options = additionalVariantInfo.options;

      } catch (error) {
        console.warn('Failed to parse additional variant info:', error);
      }
    }

    // Get exchange data from localStorage
    const exchangeData = JSON.parse(localStorage.getItem('exchangeGiftCard') || '{}');

    if (!exchangeData.voucherId) {
      console.error('No exchange voucher data found');
      ErrorUtils.error('customer.account.exchange', 'exchange_session_expired', 'Exchange session expired. Please start the exchange process again.');
      return;
    }



    // Open the exchange confirmation popup with variant info (cart state already cached)
    PopupUtils.openExchangePopup(productId, productTitle, productPrice, variantInfo);
  }

  // Initialize ExchangeCardPrice for price difference calculations
  initializeExchangeCardPrice() {


    // Check if ExchangeCardPrice class is available
    if (typeof ExchangeCardPrice === 'undefined') {
      console.warn('ExchangeCardPrice class not available - price differences will not be shown');
      return;
    }

    try {
      // Initialize ExchangeCardPrice with this collection element
      this.exchangeCardPrice = new ExchangeCardPrice(this.element);


    } catch (error) {
      console.error('ExchangeCollectionGrid: Failed to initialize ExchangeCardPrice:', error);
      // Don't throw - price differences are optional functionality
    }
  }

  // Handle initialization errors gracefully
  handleInitializationError(error) {
    console.error('ExchangeCollectionGrid: Initialization error:', error);

    // Mark element as failed for CSS/debugging
    this.dataset.initializationFailed = 'true';
    this.dataset.errorMessage = error.message || 'Unknown initialization error';

    // Show user-friendly error if needed
    if (toastNotifications) {
      toastNotifications.error('Exchange page failed to load. Please refresh the page.');
    }
  }

  // Cleanup when component is removed from DOM
  disconnectedCallback() {


    // Remove event listeners using the stored bound methods
    if (this.boundHandleDetailedViewContentLoaded) {
      document.removeEventListener('detailed-view-content-loaded', this.boundHandleDetailedViewContentLoaded);
      this.boundHandleDetailedViewContentLoaded = null;
    }

    if (this.boundHandleClickExchange) {
      document.removeEventListener('click', this.boundHandleClickExchange);
      this.boundHandleClickExchange = null;
    }
    
    // Clean up variant change listeners
    this.cleanupVariantChangeListeners();
    this._popupVariantData = null;

    // Destroy ExchangeCardPrice instance if it exists
    if (this.exchangeCardPrice && typeof this.exchangeCardPrice.destroy === 'function') {
      this.exchangeCardPrice.destroy();
      this.exchangeCardPrice = null;
    }

    // Clean up event delegation
    if (this.delegateElement) {
      // Delegate class doesn't have a destroy method, but we can remove reference
      this.delegateElement = null;
    }


  }
}

// Custom element registration

// Register the ExchangeCollectionGrid as a custom element
customElements.define('exchange-collection-grid', ExchangeCollectionGrid);

// Register VoucherActionHandler as a custom element
customElements.define('voucher-action-handler', VoucherActionHandler);
